import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Tabs,
  Table,
  Tag,
  Space,
  Spin,
  Alert,
  Checkbox,
  Tree,
  Progress,
  Tooltip,
  Button,
  Statistic,
  Select,
  Slider,
  Modal,
  Badge,
  Divider,
  message,
  Radio,
  Collapse,
  Empty,
  Skeleton,
  Input,
  Breadcrumb,
  List,
  Tour,
} from 'antd';
import {
  BarChartOutlined,
  FileTextOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  LineChartOutlined,
  StarOutlined,
  StarFilled,
  InfoCircleOutlined,
  UserOutlined,
  RiseOutlined,
  CopyOutlined,
  FallOutlined,
  CaretDownOutlined,
  EyeOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
// import { mockApi } from '../mockApi'; // モックAPIは削除済み
import { v2AnalysisApi, v2ArticlesApi, publicSamplesApi } from '../../../api';
import GuestBottomBar from '../../Demo/GuestBottomBar';
import favoriteKeywordsAPI from '../../../api/favoriteKeywords';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { useTutorialTour } from '../../../hooks/useTutorialTour';
import RealtimeProgressUI from './components/RealtimeProgressUI';
import AIOContentStrategy from './AIOContentStrategy';
import TopicSunburstMap from './TopicSunburstMap';
import ShareSettings from './components/ShareSettings';
import './CompetitorAnalysis.css';
import {
  getHeatmapColor,
  getTermsHeatmapColor,
  getRelatedHeatmapColor,
  getLSIHeatmapColor,
} from '../common/heatmapColors';
import TipsRotator from '../../Common/TipsRotator';
import { researchTips } from '../../Common/tipsData';
import EnhancedTour, { TourHelpButton } from '../../Common/EnhancedTour';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

/**
 * マークダウンの太字（**text**）をHTMLに変換するヘルパー関数
 * @param {string} text - 変換するテキスト
 * @returns {string} - HTMLに変換されたテキスト
 */
const formatBoldMarkdown = text => {
  if (!text || typeof text !== 'string') return '';
  // **text** を <strong>text</strong> に変換
  // XSS対策: <>をエスケープしてから太字変換
  let escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
};

// CSSアニメーションのスタイル
const fadeInAnimation = {
  animation: 'fadeIn 0.6s ease-in-out',
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
};

const pulseAnimation = {
  animation: 'pulse 1.5s infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
};

const CompetitorAnalysis = ({
  sessionData,
  updateSessionData,
  onNext,
  onPrev,
  isNewArticle = true,
  articleUuid = null,
  // 共有ビュー用のプロパティ
  analysisData = null,
  initialAnalysisState = null,
  isSharedView = false,
  enableProgressUI = true,
  showNavigation = true,
  showShareSettings = true,
  isGuest = false,
}) => {
  // マウント/アンマウントログ
  React.useEffect(() => {
    console.log('[AIO] CompetitorAnalysis mounted');
    return () => console.log('[AIO] CompetitorAnalysis unmounted');
  }, []);

  // CSS アニメーションを動的に追加
  React.useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes fadeIn {
        0% { opacity: 0; transform: translateY(10px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
      
      .fade-in { animation: fadeIn 0.6s ease-in-out; }
      .pulse { animation: pulse 1.5s infinite; }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [competitorData, setCompetitorData] = useState(
    isSharedView ? analysisData : null,
  );
  // お気に入りキーワード管理
  const [favoriteKeywords, setFavoriteKeywords] = useState([]);
  const [favoriteKeywordsLoading, setFavoriteKeywordsLoading] = useState({});
  const [selectedImportantTerms, setSelectedImportantTerms] = useState([]);
  const [headingView, setHeadingView] = useState('headings');

  // スコアポップアップ用の状態
  const [scorePopupVisible, setScorePopupVisible] = useState(false);
  const [scorePopupData, setScorePopupData] = useState(null);
  const [expandedPaa, setExpandedPaa] = useState({});

  // スコアポップアップ表示関数
  // options.source: 'site' | 'heatmap'（既定: 'heatmap'）
  const showScorePopup = (
    topic,
    siteIndex,
    score,
    record,
    options = { source: 'heatmap' },
  ) => {
    const source = options?.source || 'heatmap';
    const competitors = competitorData?.competitor_analysis?.competitors || [];
    const competitor = competitors[siteIndex];
    if (!competitor) return;

    // 見出しを階層構造で抽出（最上位レベルに応じて動的に変更）
    const headings = competitor.headings_analysis?.headings || [];
    const structuredHeadings = [];

    // 最上位レベルを判定
    const topLevel = Math.min(
      ...headings.map(h => h.level).filter(level => level >= 2 && level <= 4),
    );
    const isH3Top = topLevel === 3;

    if (isH3Top) {
      // H3が最上位の場合：H3->H4構造
      let currentH3 = null;

      headings.forEach(heading => {
        if (heading.level === 3) {
          currentH3 = {
            text: heading.text,
            level: 3,
            h4s: [],
          };
          structuredHeadings.push(currentH3);
        } else if (heading.level === 4 && currentH3) {
          currentH3.h4s.push(heading.text);
        }
      });
    } else {
      // H2が最上位の場合：H2->H3構造（従来通り）
      let currentH2 = null;

      headings.forEach(heading => {
        if (heading.level === 2) {
          currentH2 = {
            text: heading.text,
            level: 2,
            h3s: [],
          };
          structuredHeadings.push(currentH2);
        } else if (heading.level === 3 && currentH2) {
          currentH2.h3s.push(heading.text);
        }
      });
    }

    // サイト情報を抽出
    const url = competitor.url || '#';
    const siteName = url
      ? new URL(url).hostname.replace('www.', '')
      : 'サイト不明';
    const favicon = `https://www.google.com/s2/favicons?domain=${siteName}&sz=16`;

    // 文字数を計算（概算）
    const contentLength =
      competitor.content_analysis?.content_length ||
      competitor.content_analysis?.total_characters ||
      competitor.content_analysis?.content?.length ||
      0;

    setScorePopupData({
      source,
      topic,
      siteIndexOriginal: siteIndex,
      siteIndex: siteIndex >= 0 ? siteIndex + 1 : -1,
      pageTitle: competitor.title || 'タイトル不明',
      pageUrl: url,
      siteName,
      favicon,
      contentLength,
      score: score || 0,
      structuredHeadings,
    });
    setScorePopupVisible(true);
  };

  // 🎯 シンプル化された状態管理
  const [analysisState, setAnalysisState] = useState(() => {
    // 共有ビューの場合
    if (isSharedView && initialAnalysisState) {
      return initialAnalysisState;
    }

    // 通常ビュー：既に完了している分析かどうかをチェック
    const existingStatus = sessionData?.step1AnalysisResult?.status;
    const isCompleted =
      existingStatus === 'completed' ||
      existingStatus === 'completed_with_errors';

    return {
      status: isCompleted ? 'completed' : 'idle',
      progress: isCompleted ? 100 : 0,
      currentStep: '',
      analysisId: sessionData?.step1AnalysisResult?.analysis_id || null,
      data: {},
    };
  });

  // ✅ ツアー管理（リサーチ結果画面）— リサーチ完了後に手動で開始
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    startTour,
    currentStep: tourCurrentStep,
    updateCurrentStep: updateTourCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('research_result', { disabled: isGuest, autoStart: false });

  // ツアー開始タイミング管理用のref
  const tourStartedRef = useRef(false);
  const prevStatusRef = useRef(null);

  // タブ管理（ツアー用）
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [tourHeadingView, setTourHeadingView] = useState(null); // ツアー用のサブタブ制御

  // ツアー用のheadingView制御
  useEffect(() => {
    if (tourHeadingView !== null) {
      setHeadingView(tourHeadingView);
    }
  }, [tourHeadingView]);

  // リサーチ完了時にツアーを開始
  useEffect(() => {
    const currentStatus = analysisState.status;
    const prevStatus = prevStatusRef.current;

    const errorStatuses = ['error', 'failed', 'interrupted'];
    if (errorStatuses.includes(currentStatus)) {
      prevStatusRef.current = currentStatus;
      return;
    }

    const justCompleted =
      prevStatus !== 'completed' && currentStatus === 'completed';
    const initiallyCompleted =
      prevStatus === null && currentStatus === 'completed';

    if (
      (justCompleted || initiallyCompleted) &&
      !tourStartedRef.current &&
      !tourLoading
    ) {
      console.log('[Tour] Research completed, starting tour...');
      tourStartedRef.current = true;
      if (startTour) {
        setTimeout(() => startTour(), 500);
      }
    }

    prevStatusRef.current = currentStatus;
  }, [analysisState.status, tourLoading]);

  const [initializing, setInitializing] = useState(true);
  const scrollContainerRef = useRef(null);

  // スティッキー状態の検出
  const [isTabBarSticky, setIsTabBarSticky] = useState(false);
  const stickyObserverRef = useRef(null);

  // 計算された状態（derived state）
  const computedState = useMemo(
    () => ({
      isIdle: analysisState.status === 'idle',
      isRunning: analysisState.status === 'running',
      isCompleted: analysisState.status === 'completed',
      hasError: analysisState.status === 'error',
      shouldShowProgress: analysisState.status === 'running',
      shouldAcceptWebSocket: analysisState.status === 'running',
    }),
    [analysisState.status],
  );

  // 🗑️ analysisExecuted -> analysisState.status に統合済み

  // sessionDataが更新された時にanalysisIdを更新
  useEffect(() => {
    if (sessionData?.step1AnalysisResult?.analysis_id) {
      setAnalysisState(prev => ({
        ...prev,
        analysisId: sessionData.step1AnalysisResult.analysis_id,
      }));
      console.log(
        'Analysis ID updated:',
        sessionData.step1AnalysisResult.analysis_id,
      );
    }
  }, [sessionData?.step1AnalysisResult?.analysis_id]);

  // 🗑️ sessionData.competitorData処理を削除（DB直接読み込みに統一）
  const [showProgressUI, setShowProgressUI] = useState(false);
  // 🗑️ analysisProgress -> analysisState.progress に統合済み
  const [targetProgress, setTargetProgress] = useState(0);
  // 🗑️ currentStep -> analysisState.currentStep に統合済み
  const [stepDetails, setStepDetails] = useState(null);
  // 🗑️ initialDataLoaded関連削除（シンプル化されたWebSocketで不要）
  // 🗑️ analysisStatus -> analysisState.status に統合済み
  const [stepData, setStepData] = useState({});
  const [currentStepMessage, setCurrentStepMessage] = useState('');
  // 検索意図トグルの選択状態
  const [intentToggleSelection, setIntentToggleSelection] = useState({
    core_needs: new Set(),
    sub_needs: new Set(),
    pain_points: new Set(),
    motivators: new Set(),
    personas: new Set(),
  });
  // ヒートマップのホバー中の行・列
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [hoveredColIndex, setHoveredColIndex] = useState(null);
  // サイト種別の日本語マッピング
  const siteTypeMap = useMemo(
    () => ({
      owned_media: 'オウンドメディア',
      service_site: 'サービスサイト',
      portal: 'ポータル',
      ec: 'EC',
      news: 'ニュース',
      corporate_site: 'コーポレートサイト',
      government_site: '公的機関',
      sns: 'SNS',
      ugc: 'UGC',
      others: 'その他',
    }),
    [],
  );
  const colorByType = useMemo(
    () => ({
      owned_media: { bg: '#edf2ff', text: '#2f54eb' },
      service_site: { bg: '#e6fffb', text: '#13c2c2' },
      portal: { bg: '#fff7e6', text: '#ad6800' },
      ec: { bg: '#fff1f0', text: '#cf1322' },
      news: { bg: '#f9f0ff', text: '#531dab' },
      corporate_site: { bg: '#e6f4ff', text: '#0958d9' },
      government_site: { bg: '#f6ffed', text: '#389e0d' },
      sns: { bg: '#fcffe6', text: '#7cb305' },
      ugc: { bg: '#fff0f6', text: '#c41d7f' },
      others: { bg: '#f5f5f5', text: '#595959' },
    }),
    [],
  );

  // dataReadyの安全マージ: 既にtrueのフラグは絶対にfalseへ戻さない
  const mergeDataReady = (prev, next) => {
    const safe = key => (prev[key] ? true : !!next[key]);
    const mergeSummary = () => ({
      persona: prev.summary?.persona || !!next?.summary?.persona,
      user_needs: prev.summary?.user_needs || !!next?.summary?.user_needs,
      competitor_summaries:
        prev.summary?.competitor_summaries ||
        !!next?.summary?.competitor_summaries,
      site_summaries:
        prev.summary?.site_summaries || !!next?.summary?.site_summaries,
    });
    return {
      serp_data: safe('serp_data'),
      related_keywords: safe('related_keywords'),
      lsi_keywords: safe('lsi_keywords'),
      keyword_metrics: safe('keyword_metrics'),
      competitor_analysis: safe('competitor_analysis'),
      aio_analysis: safe('aio_analysis'),
      aio_content_strategy: safe('aio_content_strategy'),
      summary: mergeSummary(),
      heatmaps: safe('heatmaps'),
      topic_analysis: safe('topic_analysis'),
      aio_slots: safe('aio_slots'),
      complete: safe('complete'),
    };
  };

  // データ準備状況の管理
  const [dataReady, setDataReady] = useState({
    // Step1 簡易分析（①）- 既存記事でも完了
    serp_data: false,
    related_keywords: false, // 分析サマリー用
    lsi_keywords: false, // 分析サマリー用
    keyword_metrics: false,
    competitor_analysis: false,
    aio_analysis: false,
    aio_content_strategy: false,

    // Step2 詳細分析（②）- 既存記事でも未完了、WebSocketで段階的に更新
    summary: {
      persona: false, // 想定検索者像
      user_needs: false, // ユーザーニーズ
      competitor_summaries: false, // 上位サイト概要
      site_summaries: false, // サイト概要
    },

    // Step3 拡張分析（③）- 既存記事でも未完了
    heatmaps: false,
    topic_analysis: false,
    aio_slots: false,
    // 新規表示用
    serps_evaluation: false,
    topic_landscape: false,

    // 🔧 競合サイト分析タブ ヒートマップ専用フラグ（分析サマリー用と分離）
    heatmap_topics: false,
    heatmap_terms: false,
    heatmap_related: false,
    heatmap_lsi: false,

    complete: false,
  });

  // スムーズな進捗バーアニメーション（予測的進捗）
  useEffect(() => {
    if (targetProgress === 0) return;

    // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）

    // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）

    // 予測的な進捗アニメーション
    // 実際の処理時間分析に基づく最適化された進捗設定
    // 🔧 待機時間が長いステップの上限を引き上げ、よりスムーズな進捗表示に
    const slowProgressSteps = {
      // 前段取得系
      'dataforseo_api': { maxProgress: 12, duration: 7000 },
      'scraping': { maxProgress: 22, duration: 15000 }, // 16→22%に引き上げ、待機感を緩和
      'aio_analysis': { maxProgress: 28, duration: 10000 }, // 22→28%に引き上げ

      // 詳細分析（中央値ベースの補間）
      'raw_topic_extraction': { maxProgress: 45, duration: 40000 }, // 37→45%に引き上げ、待機時間中も進捗
      'topic_integration_mece': { maxProgress: 55, duration: 45000 }, // 50→55%に引き上げ
      'site_topic_analysis': { maxProgress: 72, duration: 45000 }, // 停滞緩和：上限72%・45sに短縮
      'term_normalization': { maxProgress: 75, duration: 25000 }, // 70→75%に引き上げ
      'keyword_lsi_analysis': { maxProgress: 81, duration: 15000 }, // 10–20s → 15s

      // 統合/派生
      'integrated_heatmap': { maxProgress: 87, duration: 12000 }, // 10–15s → 12s
      'topic_landscape_analysis': { maxProgress: 89, duration: 7000 }, // 3–10s → 7s
      'site_summary_generation': { maxProgress: 95, duration: 8000 }, // 5–10s → 8s
      'serps_evaluation_generation': { maxProgress: 97, duration: 8000 }, // 5–10s → 8s
      'aio_content_strategy_generation': { maxProgress: 98, duration: 25000 }, // 20–30s → 25s
      'search_intent_analysis': { maxProgress: 100, duration: 12000 }, // 10–15s → 12s
    };

    const config = slowProgressSteps[analysisState.currentStep];
    if (!config || targetProgress >= config.maxProgress) return;

    const startProgress = targetProgress;
    const endProgress = config.maxProgress;
    const totalIncrease = endProgress - startProgress;
    const incrementPerSecond = totalIncrease / (config.duration / 1000);

    const timer = setInterval(() => {
      // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）
      const steps = config.duration / 100; // 100msごとの更新回数
      setAnalysisState(prev => ({
        ...prev,
        progress: Math.min(
          prev.progress + totalIncrease / steps,
          config.maxProgress,
        ),
      }));
      // 🗑️ 以下のレガシーコード削除
    }, 100);

    return () => clearInterval(timer);
  }, [targetProgress, analysisState.currentStep]);

  // 完了後の進捗UI自動クローズ（既存分析・再訪時対策）
  useEffect(() => {
    // WebSocket接続中は常に表示、完了/失敗時のみ閉じる。
    if (
      analysisState.status === 'completed' ||
      analysisState.status === 'error'
    ) {
      if (showProgressUI) setShowProgressUI(false);
    }
  }, [analysisState.status, competitorData, showProgressUI]);

  // 既存の完了済み分析を開いた場合の進捗UI・状態を補正
  useEffect(() => {
    const s = sessionData?.step1AnalysisResult?.status;
    const p = sessionData?.step1AnalysisResult?.progress;
    if (s === 'completed' || s === 'completed_with_errors' || p >= 100) {
      // 🗑️ setAnalysisStatus削除('completed');
      // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）
      if (showProgressUI) setShowProgressUI(false);
    }
  }, [
    sessionData?.step1AnalysisResult?.status,
    sessionData?.step1AnalysisResult?.progress,
    showProgressUI,
  ]);
  // 🎯 シンプル化されたWebSocketハンドラー
  const simpleWebSocketOptions = useMemo(
    () => ({
      onProgress: data => {
        // minimal log: WS progress

        // 🔧 分析中状態を確実に維持
        setAnalysisState(prev => {
          const newStatus =
            data.status === 'completed' ? 'completed' : 'running';
          if (newStatus !== prev.status) {
            console.log(
              `🔧 [WebSocket] Status changed: ${prev.status} -> ${newStatus}`,
            );
          }
          return {
            ...prev,
            progress: data.progress || 0,
            currentStep: data.current_step || '',
            status: newStatus,
          };
        });

        // 🔧 初回受信時に必ずプログレスUIを可視化。0%のままでも補間を起動するため目標値を更新
        if (!showProgressUI) setShowProgressUI(true);
        setTargetProgress(data.progress || 0);

        // 🔧 ステップメッセージを更新
        if (data.current_step) {
          updateStepMessage(data.current_step, data.progress || 0);
        }

        // 🔧 プログレスバーを表示（分析開始時）
        if (data.status === 'running' && !showProgressUI) {
          setShowProgressUI(true);
        }

        // 🔧 分析完了時の処理
        if (data.status === 'completed') {
          console.log(
            '🔧 [WebSocket] Analysis completed, setting all dataReady to true',
          );

          // 完了時にすべてのdataReadyをtrueに設定
          setDataReady({
            serp_data: true,
            related_keywords: true,
            lsi_keywords: true,
            keyword_metrics: true,
            competitor_analysis: true,
            aio_analysis: true,
            aio_content_strategy: true,
            serps_evaluation: true,
            topic_landscape: true,
            summary: {
              persona: true,
              user_needs: true,
              competitor_summaries: true,
              site_summaries: true,
            },
            heatmaps: true,
            topic_analysis: true,
            aio_slots: true,
            // 🔧 ヒートマップ専用フラグ
            heatmap_topics: true,
            heatmap_terms: true,
            heatmap_related: true,
            heatmap_lsi: true,
            complete: true,
          });

          // 最終データ取得（dataReadyの上書きを防ぐため、setDataReadyを遅延実行）
          refreshDataFromDB(analysisState.analysisId).catch(err => {
            console.warn('🔧 [Completion] Final DB refresh failed:', err);
          });

          // DB反映後に再度dataReadyを確実にtrueにする
          setTimeout(() => {
            setDataReady(prev => ({
              ...prev,
              serp_data: true,
              related_keywords: true,
              lsi_keywords: true,
              keyword_metrics: true,
              competitor_analysis: true,
              aio_analysis: true,
              aio_content_strategy: true,
              serps_evaluation: true,
              topic_landscape: true,
              summary: {
                ...prev.summary,
                persona: true,
                user_needs: true,
                competitor_summaries: true,
                site_summaries: true,
              },
              heatmaps: true,
              topic_analysis: true,
              aio_slots: true,
              // 🔧 ヒートマップ専用フラグ
              heatmap_topics: true,
              heatmap_terms: true,
              heatmap_related: true,
              heatmap_lsi: true,
              complete: true,
            }));
          }, 2000);
        }

        // 🔧 Strength（competitor_scoring）を94%以上で解放（site_summary_generation段階の見栄え）
        try {
          if (
            (data.current_step === 'site_summary_generation' &&
              (data.progress || 0) >= 94) ||
            // 進捗イベントが粗い環境向けフォールバック
            (data.progress || 0) >= 94
          ) {
            setDataReady(prev => ({ ...prev, competitor_scoring: true }));
          }
        } catch (_) {
          /* noop: progress-based competitor_scoring unlock is best-effort */
        }
      },

      onDataUpdate: data => {
        // console.log('🟢 [Simple-WS-DataUpdate]', data.data_update?.type);

        // 🎯 完了データ表示時はWebSocket無視（既にデータ読み込み済み）
        if (analysisState.status === 'completed') {
          // minimal log
          return;
        }

        // 🔧 修正: 分析IDが一致する場合は常に受信（状態に関係なく）
        // WebSocketデータ受信時は分析中として扱う
        const updateType = data.data_update?.type;
        const payload = data.data_update?.data || data.data_update;

        if (updateType && payload) {
          // minimal log

          // 🔧 ステップ完了時の推定進捗（バックエンドの進捗更新が不十分な場合のフォールバック）
          const stepProgressMap = {
            'serp_extraction': 9,
            'dataforseo_api': 9,
            'related_keywords': 12,
            'lsi_keywords': 14,
            'keyword_metrics': 16,
            'scraping': 20,
            'aio_analysis': 25,
            'raw_topic_extraction': 37,
            'topic_integration_mece': 48,
            'site_topic_analysis': 55,
            'term_normalization': 63,
            'keyword_lsi_analysis': 72,
            'integrated_heatmap': 80,
            'topic_landscape_analysis': 85,
            'site_summary_generation': 90,
            'serps_evaluation_generation': 93,
            'aio_content_strategy_generation': 96,
            'search_intent_analysis': 98,
          };
          const estimatedProgress = stepProgressMap[updateType] || 0;

          // 🔧 分析状態を確実にrunningに設定（進捗も更新）
          setAnalysisState(prev => {
            // 現在の進捗より推定進捗が大きい場合のみ更新
            const newProgress = Math.max(prev.progress || 0, estimatedProgress);
            return {
              ...prev,
              status: 'running',
              progress: newProgress,
              currentStep: updateType,
              data: { ...prev.data, [updateType]: payload },
            };
          });

          // targetProgressも更新（進捗アニメーション用）
          if (estimatedProgress > 0) {
            setTargetProgress(prev => Math.max(prev, estimatedProgress));
          }

          // 🔧 [STATS-FIX] keyword_metricsの場合、competitorDataも直接更新
          if (
            updateType === 'keyword_metrics' &&
            payload.keywords_analysis?.main_keyword_metrics
          ) {
            // minimal log
            setCompetitorData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                keywords_analysis: {
                  ...prev.keywords_analysis,
                  main_keyword_metrics:
                    payload.keywords_analysis.main_keyword_metrics,
                },
              };
            });
          }

          // 🔧 UI表示フラグ更新（即座にスケルトンを解除）
          // WebSocketのupdateTypeをdataReadyキーにマッピング
          const stepToDataReadyMapping = {
            'serp_extraction': 'serp_data',
            'related_keywords': 'related_keywords', // ← 関連キーワード取得完了（分析サマリー用）
            'keyword_lsi_analysis': 'lsi_keywords', // ← LSI分析完了（分析サマリー用）
            'lsi_keywords': 'lsi_keywords', // ← LSIキーワード取得完了（分析サマリー用）
            'keyword_metrics': 'keyword_metrics',
            'keywords_analysis': 'keyword_metrics',
            'competitor_analysis': 'competitor_analysis',
            'scraping': 'competitor_analysis',
            'aio_analysis': 'aio_analysis',
            'topic_analysis': 'topic_analysis',
            'summary': 'summary',
            'aio_content_strategy': 'aio_content_strategy',
            'complete': 'complete',
            'serps_evaluation': 'serps_evaluation',
            'topic_landscape': 'topic_landscape',
            // 🔧 競合サイト分析タブ ヒートマップ専用フラグ（分析サマリー用と分離）
            'site_topic_analysis': 'heatmap_topics', // ← トピックヒートマップ完了
            'term_normalization': 'heatmap_terms', // ← 重要語句ヒートマップ完了
            'integrated_heatmap': 'heatmap_related', // ← 関連キーワードヒートマップ完了
            // LSIヒートマップは統合ヒートマップと同タイミングで設定
            // Strength/SEO難易度はサイト概要生成完了まで待つ
            'site_summary_generation': 'competitor_scoring',
          };

          // 特殊ハンドリング: 準備開始のイベントはスケルトン維持
          if (
            updateType === 'serps_evaluation' ||
            updateType === 'topic_landscape'
          ) {
            // {'response': '```json {...} ```'} 形式にも対応してsummaryを検出
            let summary = payload?.summary;
            if (
              !summary &&
              payload &&
              typeof payload === 'object' &&
              typeof payload.response === 'string'
            ) {
              try {
                let txt = payload.response.trim();
                if (txt.startsWith('```json')) txt = txt.slice(7);
                if (txt.endsWith('```')) txt = txt.slice(0, -3);
                const inner = JSON.parse(txt.trim());
                summary = inner?.summary;
              } catch (e) {
                // noop
              }
            }
            const ready =
              !!summary ||
              (updateType === 'serps_evaluation' &&
                (payload?.has_evaluation === true ||
                  !!payload?.evaluation_summary)) ||
              (updateType === 'topic_landscape' &&
                payload?.has_landscape === true);
            if (updateType === 'serps_evaluation') {
              setDataReady(prev => ({ ...prev, serps_evaluation: ready }));
            } else {
              setDataReady(prev => ({ ...prev, topic_landscape: ready }));
            }
          } else {
            const dataReadyKey =
              stepToDataReadyMapping[updateType] || updateType;
            // ヒートマップ系はDBデータ取得後に更新（スケルトン→データなし切替のタイミング最適化）
            const heatmapKeys = [
              'topics',
              'terms',
              'related_keywords',
              'lsi_keywords',
            ];
            if (!heatmapKeys.includes(dataReadyKey)) {
              // heatmaps系の"まとめフラグ"ではなく、個別に解除
              if (dataReadyKey === 'heatmaps') {
                setDataReady(prev => ({ ...prev, heatmaps: true }));
              } else {
                setDataReady(prev => ({ ...prev, [dataReadyKey]: true }));
              }
            }
          }

          // 🔧 プログレスUIを表示（データ受信時）
          if (!showProgressUI) {
            setShowProgressUI(true);
          }

          // 🔧 特定のデータタイプのみDB再取得（keyword_metricsは直接更新済みなので除外）
          const needsDbRefresh = !['keyword_metrics'].includes(updateType);

          if (needsDbRefresh) {
            const wsAnalysisId = data.analysis_id || analysisState.analysisId;
            if (wsAnalysisId) {
              // DBにデータが保存されるまで少し待つ
              setTimeout(() => {
                refreshDataFromDB(wsAnalysisId)
                  .then(() => {
                    // DBデータ取得後にヒートマップ系dataReadyを更新
                    // Reactのstate更新がバッチ処理されるため、少し遅延を追加
                    const dataReadyKey =
                      stepToDataReadyMapping[updateType] || updateType;
                    // 🔧 ヒートマップ専用フラグを使用
                    const heatmapKeys = [
                      'heatmap_topics',
                      'heatmap_terms',
                      'heatmap_related',
                      'heatmap_lsi',
                    ];
                    if (heatmapKeys.includes(dataReadyKey)) {
                      setTimeout(() => {
                        // integrated_heatmapの場合、related と lsi 両方を設定
                        if (updateType === 'integrated_heatmap') {
                          setDataReady(prev => ({
                            ...prev,
                            heatmap_related: true,
                            heatmap_lsi: true,
                          }));
                        } else {
                          setDataReady(prev => ({
                            ...prev,
                            [dataReadyKey]: true,
                          }));
                        }
                      }, 300);
                    }
                  })
                  .catch(err => {
                    console.warn('🔧 [WS-Trigger] DB refresh failed:', err);
                    // エラー時もdataReadyを更新（スケルトンを永久に表示しないため）
                    const dataReadyKey =
                      stepToDataReadyMapping[updateType] || updateType;
                    const heatmapKeys = [
                      'heatmap_topics',
                      'heatmap_terms',
                      'heatmap_related',
                      'heatmap_lsi',
                    ];
                    if (heatmapKeys.includes(dataReadyKey)) {
                      setTimeout(() => {
                        if (updateType === 'integrated_heatmap') {
                          setDataReady(prev => ({
                            ...prev,
                            heatmap_related: true,
                            heatmap_lsi: true,
                          }));
                        } else {
                          setDataReady(prev => ({
                            ...prev,
                            [dataReadyKey]: true,
                          }));
                        }
                      }, 300);
                    }
                  });
                // 一部セクションは更新到着で即時解除
                const map = {
                  serps_evaluation: 'serps_evaluation',
                  topic_landscape: 'topic_landscape',
                  aio_content_strategy: 'aio_content_strategy',
                };
                const key = map[updateType];
                if (key) {
                  setDataReady(prev => ({ ...prev, [key]: true }));
                }

                // 🔧 keywords_analysisに含まれる内訳で個別即時解除
                // 注意: related_keywords, lsi_keywordsはheatmapKeysに含まれるため、
                // ここでは設定しない（DBデータ取得後の処理で設定される）
                if (updateType === 'keywords_analysis') {
                  // keyword_metricsのみ即時解除
                  setDataReady(prev => ({
                    ...prev,
                    keyword_metrics: true,
                  }));
                }
              }, 800); // 0.8秒遅延
            }
          }
        }
      },

      onConnect: () => {
        setShowProgressUI(true);
        // 接続直後に0%表示静止を避けるため、目標進捗を0で初期化（補間起動のトリガ）
        setTargetProgress(0);
      },

      onError: error => {
        console.error('🔴 [Simple-WS] Error:', error);
        setAnalysisState(prev => ({ ...prev, status: 'error' }));
      },

      onAnalysisCompleted: data => {
        console.log(
          '🔧 [Simple-WS] Analysis already completed notification:',
          data,
        );
        // 完了済み分析の場合は状態を適切に設定
        setAnalysisState(prev => ({
          ...prev,
          status: 'completed',
          progress: 100,
        }));
        // 🔧 WebSocket接続は shouldUseWebSocket の変更により自動的に切断される
        console.log(
          '🔧 [Simple-WS] WebSocket will be disconnected by shouldUseWebSocket change',
        );
      },
    }),
    [analysisState.status, analysisState.analysisId], // 🔧 依存関係をステータスとIDのみに限定してレンダリングループを防止
  );
  // WebSocket接続（シンプル版）- 適切なステータス判定で無効化
  const shouldUseWebSocket = useMemo(() => {
    // 共有ビューの場合は常に無効
    if (isSharedView) {
      return false;
    }

    // 分析IDが存在しない場合は無効
    if (!analysisState.analysisId) {
      return false;
    }

    // 終了状態（完了/エラー/中断）の場合は無効
    const terminalStatuses = [
      'completed',
      'completed_with_errors',
      'error',
      'failed',
      'interrupted',
    ];
    if (terminalStatuses.includes(analysisState.status)) {
      console.log(
        `🔧 [WebSocket] Disabled for terminal status: ${analysisState.status}, analysisId: ${analysisState.analysisId?.substring(0, 20)}...`,
      );
      return false;
    }

    // 🔧 既存の分析データがある場合はWebSocket接続しない（再訪問時のループ防止）
    // sessionData.step1AnalysisResult が存在し、status が終了状態の場合
    const existingResult = sessionData?.step1AnalysisResult;
    if (existingResult && terminalStatuses.includes(existingResult.status)) {
      console.log(
        `🔧 [WebSocket] Disabled for existing terminal analysis: ${analysisState.analysisId?.substring(0, 20)}...`,
      );
      return false;
    }

    // 🔧 runningまたはqueuedの場合のみWebSocket接続を有効化
    // idleで分析IDがある場合は、既存の完了した分析を閲覧している可能性が高い
    if (analysisState.status === 'idle' && analysisState.analysisId) {
      console.log(
        `🔧 [WebSocket] Disabled for idle state with existing analysisId (likely viewing completed analysis)`,
      );
      return false;
    }

    console.log(
      `🔧 [WebSocket] Enabled (status: ${analysisState.status || 'idle'}, analysisId: ${analysisState.analysisId?.substring(0, 20)}...)`,
    );
    return true;
  }, [
    isSharedView,
    analysisState.analysisId,
    analysisState.status,
    sessionData?.step1AnalysisResult,
  ]);

  const {
    isConnected,
    connectionError,
    stepData: wsStepData,
    realtimeUpdates,
    isOnline,
  } = useWebSocket(
    shouldUseWebSocket ? analysisState.analysisId : null,
    simpleWebSocketOptions,
  );
  // 初回レンダリング時の処理
  useEffect(() => {
    if (!sessionData.keyword) {
      setInitializing(false);
      return;
    }

    // 🗑️ sessionData.step1AnalysisResult処理を削除（DB直接読み込みに統一）

    // 🗑️ 不要なログ削除

    // 完了済み分析の場合：analysis_idベースでDBから読み込み
    const hasAnalysisId = !!analysisState.analysisId;
    console.log('🔍 [useEffect] DB loading conditions:', {
      isNewArticle,
      status: analysisState.status,
      analysisId: analysisState.analysisId,
      hasAnalysisId,
      shouldLoadFromDB:
        !isNewArticle && analysisState.status === 'idle' && hasAnalysisId,
    });

    if (!isNewArticle && analysisState.status === 'idle' && hasAnalysisId) {
      console.log(
        '🔴 [useEffect] Loading from DB using analysis_id:',
        analysisState.analysisId,
      );
      loadExistingAnalysis();
      return;
    }

    // 既存記事で分析結果がない場合（articleUuidもない）：分析は実行しない
    if (
      !isNewArticle &&
      !sessionData.step1AnalysisResult &&
      analysisState.status === 'idle' // 🗑️ analysisExecuted削除
    ) {
      setInitializing(false);
      return;
    }

    // 新規記事の場合のみ分析実行（キーワードがあり、分析未実行で、既存データがない場合のみ）
    // 🎯 シンプル化された分析開始条件
    const shouldStartAnalysis =
      isNewArticle &&
      analysisState.status === 'idle' &&
      sessionData.keyword &&
      !sessionData.hasExistingAnalysis;

    console.log('🔴 [useEffect] Checking startAnalysis conditions:', {
      isNewArticle,
      analysisStatus: analysisState.status,
      hasKeyword: !!sessionData.keyword,
      hasExistingAnalysis: !!sessionData.hasExistingAnalysis,
      shouldStartAnalysis,
      articleUuid, // 🔍 articleUuidの値を確認
    });

    if (shouldStartAnalysis) {
      console.log('🔴 [useEffect] Starting analysis');
      analyzeCompetitors();
    }

    // 🔧 queuedステータス時にプログレスバーを表示
    if (analysisState.status === 'queued' && !showProgressUI) {
      console.log(
        '🔧 [DEBUG-PROGRESS] Setting showProgressUI to true (queued status)',
      );
      setShowProgressUI(true);
    }

    // 初期化完了
    setInitializing(false);
  }, [
    sessionData.keyword, // 新規記事の分析開始に必要
    isNewArticle,
    analysisState.status, // 🔧 追加：status変更時にuseEffectを再実行
    analysisState.analysisId, // 🔧 追加：analysis_id変更時にuseEffectを再実行
    showProgressUI, // 🔧 追加：showProgressUI状態も監視
  ]);

  // 🔍 dataReady状態変化をモニタリング
  useEffect(() => {
    console.log('🔄 [DEBUG-DATAREADY] dataReady state changed:', dataReady);
  }, [dataReady]);

  // competitorDataの状態変化をモニタリング（制限）
  const [lastCompetitorDataLog, setLastCompetitorDataLog] = useState(null);
  useEffect(() => {
    const currentHash = competitorData
      ? JSON.stringify(competitorData).length
      : 0;
    if (lastCompetitorDataLog !== currentHash) {
      // 🔍 aio_content_strategy専用デバッグ
      console.log(
        '📊 [CompetitorData] State changed - aio_content_strategy:',
        competitorData?.aio_content_strategy,
      );
      console.log(
        '📊 [CompetitorData] State changed - keys:',
        competitorData ? Object.keys(competitorData) : 'null',
      );
      setLastCompetitorDataLog(currentHash);
    }
  }, [competitorData, lastCompetitorDataLog]);
  // 共有ビューの場合、データが既に存在するのでdataReadyを適切に設定
  useEffect(() => {
    if (isSharedView && competitorData) {
      console.log('🔄 [SharedView] Setting dataReady flags for shared view');
      // 共有ビューでは、データの有無に関わらずdataReadyをtrueに設定
      // これにより、データがない場合は「データがありません」表示になる（スケルトンではなく）
      const newDataReady = {
        serp_data: true, // 共有ビューでは常にtrue（データ有無は内部で判定）
        related_keywords: true, // 共有ビューでは常にtrue
        lsi_keywords: true, // 共有ビューでは常にtrue
        keyword_metrics: true, // 共有ビューでは常にtrue
        competitor_analysis: true, // 共有ビューでは常にtrue
        aio_analysis: true, // 共有ビューでは常にtrue
        aio_content_strategy: true, // 共有ビューでは常にtrue
        topic_analysis: true, // 共有ビューでは常にtrue
        topics: true, // 共有ビューでは常にtrue
        competitor_scoring: true, // 共有ビューでは常にtrue
        comprehensive_report: true, // 共有ビューでは常にtrue
        summary: {
          persona: true, // 共有ビューでは常にtrue
          user_needs: true, // 共有ビューでは常にtrue
          competitor_summaries: true, // 共有ビューでは常にtrue
          site_summaries: true, // 共有ビューでは常にtrue
        },
        heatmaps: true, // 共有ビューでは常にtrue
        serps_evaluation: true, // 共有ビューでは常にtrue
        // 🔧 ヒートマップ専用フラグ
        heatmap_topics: true,
        heatmap_terms: true,
        heatmap_related: true,
        heatmap_lsi: true,
        complete: true, // 共有ビューでは常にtrue
      };

      console.log(
        '🔄 [SharedView] New dataReady flags (all true for shared view):',
        newDataReady,
      );
      setDataReady(prev => ({ ...prev, ...newDataReady }));
    }
  }, [isSharedView, competitorData]);

  // スティッキー状態の監視
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 監視要素が画面外に出たらスティッキー状態
        const isSticky = !entry.isIntersecting;
        console.log(
          '🔍 [Sticky] isIntersecting:',
          entry.isIntersecting,
          'isSticky:',
          isSticky,
          'boundingClientRect.top:',
          entry.boundingClientRect.top,
        );
        setIsTabBarSticky(isSticky);
      },
      { threshold: [0] },
    );

    if (stickyObserverRef.current) {
      observer.observe(stickyObserverRef.current);
    }

    return () => {
      if (stickyObserverRef.current) {
        observer.unobserve(stickyObserverRef.current);
      }
    };
  }, []);

  // currentStepMessageとdataReadyの状態変化をモニタリング
  useEffect(() => {
    console.log(
      '📢 [StepMessage] Current step message changed:',
      currentStepMessage,
    );
  }, [currentStepMessage]);

  useEffect(() => {
    // ログを一時的に無効化
    // console.log('✅ [DataReady] Data ready state changed:', { ... });
  }, [dataReady]);

  // メッセージとデータ準備状況を更新するヘルパー関数
  const updateStepMessage = (step, progress) => {
    console.log('💬 [UpdateStepMessage] Called with:', { step, progress });

    const messages = {
      // キュー状態
      'queued': '分析を待機しています...',

      // 統一された英数字キーと日本語メッセージ
      'dataforseo_api': 'AIエージェントが検索結果データを取得しています...',
      'scraping': '上位サイトのコンテンツを収集・解析しています...',
      'raw_topic_extraction':
        'トピックを分析・抽出しています（処理に時間がかかる場合があります）...',
      'topic_integration_mece':
        '抽出したトピックをMECEで整理・統合しています...',
      'site_topic_analysis': '各サイトのトピック含有率を計算しています...',
      'term_normalization': '重要語句の出現パターンを正規化しています...',
      'keyword_lsi_analysis':
        '関連キーワードとLSIキーワードを分析しています...',
      'integrated_heatmap': 'ヒートマップデータを統合しています...',
      'site_summary_generation': 'サイト概要と検索意図を分析しています...',
      'search_intent_analysis': '分析結果を最終処理しています...',
      'finalization': '間もなく分析が完了します...',

      // 従来の互換性維持用（徐々に廃止予定）
      'serp_extraction': 'DataForSEO APIでSERP結果を取得しています...',
      'related_keywords': '関連キーワードを取得しています...',
      'lsi_keywords': 'LSIキーワードを取得しています...',
      'keyword_metrics': 'キーワードメトリクスを取得しています...',
      'competitor_analysis': '競合サイトの見出し構造を分析しています...',
      'aio_analysis': 'AI Overviewを分析しています...',
      'data_integration': '各分析結果を統合しています...',
    };

    // ステータスベースのメッセージ処理
    let message;
    if (sessionData?.step1AnalysisResult?.status === 'queued') {
      message = '分析を待機しています...';
    } else {
      message = messages[step] || '分析を実行しています...';
    }

    console.log('💬 [UpdateStepMessage] Message mapping result:', {
      step,
      status: sessionData?.step1AnalysisResult?.status,
      found_message: messages[step],
      final_message: message,
      is_fallback:
        !messages[step] &&
        sessionData?.step1AnalysisResult?.status !== 'queued',
      available_keys: Object.keys(messages),
    });

    setCurrentStepMessage(prev => (prev === message ? `${message} ` : message));

    console.log('💬 [UpdateStepMessage] Message updated to:', message);
  };

  // 初期状態でのメッセージ更新
  useEffect(() => {
    if (sessionData?.step1AnalysisResult?.status === 'queued') {
      console.log(
        '🔄 [Initial] Setting queued message for analysis:',
        sessionData.step1AnalysisResult.analysis_id,
      );
      updateStepMessage('queued', 0);
      // 🔧 分析開始時にプログレスバーを表示
      if (!showProgressUI) {
        console.log(
          '🔧 [DEBUG-PROGRESS] Setting showProgressUI to true (queued)',
        );
        setShowProgressUI(true);
        console.log('🔧 [Progress] Showing progress UI for queued analysis');
      }
    }

    // 完了済み分析の場合は初期進捗メッセージを設定しない（レガシー進捗を防ぐ）
    const isCompleted =
      sessionData?.step1AnalysisResult?.status === 'completed' ||
      sessionData?.step1AnalysisResult?.status === 'completed_with_errors';

    if (sessionData?.step1AnalysisResult?.current_step && !isCompleted) {
      console.log(
        '🔄 [Initial] Setting message for step:',
        sessionData.step1AnalysisResult.current_step,
      );
      updateStepMessage(
        sessionData.step1AnalysisResult.current_step,
        0, // 初期化時は常に0から開始
      );
    }
  }, [
    sessionData?.step1AnalysisResult?.status,
    sessionData?.step1AnalysisResult?.current_step,
  ]);
  const updateDataReady = (step, ready) => {
    console.log('🔴 [updateDataReady] Called with:', { step, ready });
    // 完了済み分析では特定のステップをスキップ（根本的制限）
    const skipStepsForCompleted = [
      'comprehensive_report',
      'summary.persona',
      'summary.user_needs',
      'summary.competitor_summaries',
      'summary.site_summaries',
      'topic_analysis', // 無限ループ防止
      'heatmaps', // 無限ループ防止
    ];

    if (skipStepsForCompleted.includes(step)) {
      return; // ログも出力しない
    }

    // 分析完了状態で初期化中の場合は、不要なupdateDataReadyをスキップ
    // progress: 100 または current_step: analysis_complete の場合も完了として扱う
    const isAnalysisCompleted =
      sessionData?.step1AnalysisResult?.status === 'completed' ||
      sessionData?.step1AnalysisResult?.status === 'completed_with_errors' ||
      (sessionData?.step1AnalysisResult?.progress || 0) >= 100 ||
      sessionData?.step1AnalysisResult?.current_step === 'analysis_complete';

    if (isAnalysisCompleted && initializing) {
      console.log(
        '🎯 [UpdateDataReady] Skipping during initialization of completed analysis',
      );
      return;
    }

    // 分析完了状態では、summary.*とcomprehensive_reportのupdateDataReadyを制限
    // ログを一時的に無効化
    // console.log('🔍 [DEBUG] isAnalysisCompleted check:', { ... });

    if (
      isAnalysisCompleted &&
      (step.startsWith('summary.') || step === 'comprehensive_report')
    ) {
      console.log(
        '🎯 [UpdateDataReady] Skipping summary/comprehensive_report updates for completed analysis',
        { step, isAnalysisCompleted },
      );
      return;
    }

    // バックエンドのステップ名をフロントエンドのデータキーにマッピング
    const stepMapping = {
      // 統一された英数字キーからフロントエンドのデータキーにマッピング
      'dataforseo_api': 'serp_data',
      'scraping': 'competitor_analysis',
      'raw_topic_extraction': 'topic_analysis',
      'topic_integration_mece': 'topic_analysis',
      'site_topic_analysis': 'topic_analysis',
      // 'term_normalization': 'related_keywords', // 不要な早期解除を防止
      'keyword_lsi_analysis': 'lsi_keywords',
      'site_summary_generation': 'summary.site_summaries',
      'search_intent_analysis': 'summary.user_needs',
      'finalization': 'complete',

      // 従来の互換性維持用（徐々に廃止予定）
      'serp_extraction': 'serp_data',
      'related_keywords': 'related_keywords',
      'lsi_keywords': 'lsi_keywords',
      'keyword_metrics': 'keyword_metrics',
      'keywords_analysis': 'keyword_metrics',
      'competitor_analysis': 'competitor_analysis',
      'aio_analysis': 'aio_analysis',
      'aio_content_strategy_generation': 'aio_content_strategy',
      'data_integration': 'complete',

      // Step2 詳細分析
      'persona_analysis': 'summary.persona',
      'user_needs_analysis': 'summary.user_needs',
      'competitor_summaries': 'summary.competitor_summaries',
      'site_summaries': 'comprehensive_report.site_summaries',

      // Step3 拡張分析
      'heatmap_analysis': 'heatmaps',
      'topic_analysis': 'topic_analysis',
      'integrated_heatmap': 'heatmaps',
      'aio_slots_analysis': 'aio_slots',
    };

    const dataKey = stepMapping[step] || step;

    // ログを一時的に無効化
    // console.log('🎯 [UpdateDataReady] Step mapping result:', { ... });

    setDataReady(prev => {
      // ログを一時的に無効化
      // console.log('🎯 [UpdateDataReady] Current dataReady state before update:', prev);

      let newState = { ...prev };

      // ネストされたオブジェクトの更新を処理
      if (dataKey.includes('.')) {
        const [parentKey, childKey] = dataKey.split('.');
        // ログを一時的に無効化
        // console.log('🎯 [UpdateDataReady] Processing nested key:', { parentKey, childKey, ready });

        newState = {
          ...newState,
          [parentKey]: {
            ...newState[parentKey],
            [childKey]: ready,
          },
        };
      } else {
        // ログを一時的に無効化
        // console.log('🎯 [UpdateDataReady] Processing simple key:', { dataKey, ready });
        newState = { ...newState, [dataKey]: ready };
      }

      // 最終ステップの場合、全て完了にする
      if (step === 'finalization' || step === 'data_integration') {
        console.log(
          '🎯 [UpdateDataReady] Final step detected, setting all to complete',
        );
        const finalState = {
          serp_data: true,
          related_keywords: true,
          lsi_keywords: true,
          keyword_metrics: true,
          competitor_analysis: true,
          aio_analysis: true,

          summary: {
            persona: true,
            user_needs: true,
            competitor_summaries: true,
            site_summaries: true,
          },

          heatmaps: true,
          topic_analysis: true,
          aio_slots: true,

          // 🔧 ヒートマップ専用フラグ
          heatmap_topics: true,
          heatmap_terms: true,
          heatmap_related: true,
          heatmap_lsi: true,

          complete: true,
        };
        return finalState;
      }

      // ログを一時的に無効化
      // console.log('🎯 [UpdateDataReady] New dataReady state after update:', newState);
      // console.log('🎯 [UpdateDataReady] Changes applied:', { key: dataKey, value: ready, is_nested: dataKey.includes('.') });

      return newState;
    });
  };
  const analyzeCompetitors = async () => {
    // 重複実行防止
    if (analysisState.status !== 'idle') {
      console.log(
        '🔴 [analyzeCompetitors] Analysis already running/completed, skipping. Status:',
        analysisState.status,
      );
      return;
    }

    // 既存の分析IDをチェック（ThemeSetupから遷移してきた場合）
    const existingAnalysisId = sessionData?.step1AnalysisResult?.analysis_id;
    if (existingAnalysisId) {
      console.log(
        '✅ [analyzeCompetitors] Using existing analysis_id from ThemeSetup:',
        existingAnalysisId,
      );
      // 既存の分析IDを使用し、WebSocketで進捗を監視するのみ
      setAnalysisState(prev => ({
        ...prev,
        status: 'running',
        analysisId: existingAnalysisId,
      }));
      setLoading(true);
      setShowProgressUI(true);
      setCurrentStepMessage('分析を開始しています...');
      setInitializing(false);
      return; // 新規分析は開始しない
    }

    // 分析開始状態に設定
    setAnalysisState(prev => ({ ...prev, status: 'running' }));
    setLoading(true);
    setShowProgressUI(true);
    // 🗑️ レガシー状態設定削除（analysisStateで統合済み）
    setCurrentStepMessage('分析を開始しています...');
    setInitializing(false); // 分析開始時は初期化完了
    // 🗑️ initialDataLoaded設定削除（シンプル化されたWebSocketで不要）

    // データ準備状況をリセット（既にtrueのキーは維持）
    setDataReady(prev =>
      mergeDataReady(prev, {
        serp_data: false,
        related_keywords: false,
        lsi_keywords: false,
        keyword_metrics: false,
        competitor_analysis: false,
        aio_analysis: false,
        heatmaps: false,
        topic_analysis: false,
        complete: false,
      }),
    );

    try {
      // ステップ①分析を実行
      const analysisData = {
        keyword: sessionData.keyword,
        location: 'Japan',
        language: 'ja',
        target_audience: sessionData.targetAudience || '',
        content_length: sessionData.contentLength || '3000-5000文字',
      };

      // 実際のAPI分析を実行（DataForSEO APIを使用）
      let analysisResult;

      // 分析開始前にanalysisIdを生成してWebSocket接続を確立
      const currentAnalysisId = `quick_step1_${sessionData.uuid || articleUuid || 'unknown'}_${Date.now()}`;
      // 🗑️ setAnalysisId削除(currentAnalysisId);

      try {
        console.log(
          '🚀 [analyzeCompetitors] Starting NEW analysis (no existing analysis_id)',
        );
        const startResult = await v2AnalysisApi.startAnalysis({
          keyword: analysisData.keyword,
          location: analysisData.location || 'Japan',
          language: analysisData.language || 'ja',
          target_audience: analysisData.target_audience || '',
          content_length: analysisData.content_length || '3000-5000文字',
          skip_llm: analysisData.skip_llm,
          article_uuid: articleUuid,
        });

        // 分析IDを更新
        const newAnalysisId = startResult.analysis_id;
        // 🗑️ setAnalysisId削除(newAnalysisId);

        // プログレスUIを表示
        // レガシーの早期進捗を抑制し、開始メッセージを優先
        setShowProgressUI(true);
        // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）
        // setCurrentStep('AIエージェントが分析を開始しました...'); // 削除済み

        // WebSocketで進捗を監視（ポーリングは不要）
        console.log(
          'Analysis started, WebSocket will handle progress updates:',
          newAnalysisId,
        );
      } catch (error) {
        console.error('API Failed:', error.message);
        // APIが利用できない場合はエラーを表示
        message.error('分析の実行に失敗しました。');
        // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）
        // setCurrentStep('分析完了（サンプルデータ）'); // 削除済み
        // 🗑️ setAnalysisStatus削除('completed');
      }

      // 分析結果を変換（新規分析時はanalysisResultがundefined）
      const transformedData = analysisResult
        ? analysisResult // 🗑️ transformAnalysisResults削除
        : null;

      // 変換前後のデータを詳しくログ出力
      console.log('🔍 [analyzeCompetitors] 変換前のanalysisResult:', {
        'serp_data存在': !!analysisResult?.serp_data,
        'serp_data.organic_results数':
          analysisResult?.serp_data?.organic_results?.length,
        'keywords_analysis存在': !!analysisResult?.keywords_analysis,
        'keywords_analysis.related_keywords数':
          analysisResult?.keywords_analysis?.related_keywords?.length,
      });

      console.log('🔍 [analyzeCompetitors] 変換後のanalysisResult:', {
        'serp_data存在': !!analysisResult?.serp_data,
        'serp_data.organic_results数':
          analysisResult?.serp_data?.organic_results?.length,
        'keywords_analysis存在': !!analysisResult?.keywords_analysis,
        'keywords_analysis.related_keywords数':
          analysisResult?.keywords_analysis?.related_keywords?.length,
      });

      setCompetitorData(analysisResult);

      // 重要語句の初期選択（頻度上位のものを自動選択）
      const importantTermsData = analysisResult?.important_terms_analysis;
      let initialSelectedTerms = [];
      let initialTermStrengths = {};

      if (
        importantTermsData?.extracted_terms &&
        Array.isArray(importantTermsData.extracted_terms) &&
        importantTermsData.extracted_terms.length > 0
      ) {
        // 頻度上位5個を自動選択
        const topTerms = importantTermsData.extracted_terms
          .filter(
            term => term && term.term && typeof term.frequency === 'number',
          )
          .sort((a, b) => (b.frequency || 0) - (a.frequency || 0))
          .slice(0, 5)
          .map(term => term.term);

        initialSelectedTerms = topTerms;

        // 初期強度設定（頻度に応じて）
        topTerms.forEach(term => {
          const termData = importantTermsData.extracted_terms.find(
            t => t.term === term,
          );
          const frequency = termData?.frequency || 1;
          // 頻度に応じて強度を設定（1-5の範囲）
          const strength = Math.min(
            Math.max(Math.floor(frequency / 2) + 2, 1),
            5,
          );
          initialTermStrengths[term] = strength;
        });

        setSelectedImportantTerms(initialSelectedTerms);
        // 選択強度の概念は廃止
      }

      // セッションデータにも保存
      updateSessionData({
        selectedImportantTerms: initialSelectedTerms,
        step1AnalysisResult: analysisResult,
        analysisId: analysisState.analysisId,
        hasExistingAnalysis: true,
        isNewArticleCreated: false,
      });

      // 記事更新はWebSocket通知で行われるため、ここでは削除

      // 分析完了フラグを設定
      // 🗑️ setAnalysisExecuted削除(true);

      // URLパラメータ?new=trueを削除（分析完了後）
      if (window.location.search.includes('new=true')) {
        const url = new URL(window.location);
        url.searchParams.delete('new');
        window.history.replaceState({}, '', url.toString());
      }
      // データ準備状況を完了に設定
      setDataReady({
        serp_data: true,
        related_keywords: true,
        lsi_keywords: true,
        keyword_metrics: true,
        competitor_analysis: true,
        aio_analysis: true,
        heatmaps: true,
        topic_analysis: true,
        aio_content_strategy: true,
        summary: {
          persona: true,
          user_needs: true,
          competitor_summaries: true,
          site_summaries: true,
        },
        aio_slots: true,
        // 🔧 ヒートマップ専用フラグ
        heatmap_topics: true,
        heatmap_terms: true,
        heatmap_related: true,
        heatmap_lsi: true,
        complete: true,
      });

      // 3秒後にプログレスUIを非表示にする
      setTimeout(() => {
        setShowProgressUI(false);
      }, 3000);
    } catch (error) {
      message.error('競合分析に失敗しました。しばらく後に再試行してください。');
      // 🗑️ setAnalysisExecuted削除(false); // エラー時はフラグをリセット

      // エラー時の処理
      // 🗑️ setAnalysisProgress削除（analysisState.progressで管理）
      // setCurrentStep('分析でエラーが発生しました'); // 削除済み
      // setAnalysisStatus('failed'); // 削除済み
      setCurrentStepMessage('分析でエラーが発生しました');

      // データ準備状況をリセット
      setDataReady({
        serp_data: false,
        related_keywords: false,
        lsi_keywords: false,
        keyword_metrics: false,
        competitor_analysis: false,
        aio_analysis: false,
        complete: false,
      });

      // 5秒後にプログレスUIを非表示にする
      setTimeout(() => {
        setShowProgressUI(false);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };
  // WebSocket受信時にDBから最新データを取得
  const refreshDataFromDB = async overrideAnalysisId => {
    console.log('🔄 [RefreshDataFromDB] Starting data refresh from DB...');

    // 分離アーキテクチャ対応：articleUuidが無い場合は分析APIから取得
    if (!articleUuid) {
      const effectiveAnalysisId =
        overrideAnalysisId || analysisState.analysisId;
      if (!effectiveAnalysisId) {
        console.warn('⚠️ [RefreshDataFromDB] No articleUuid and no analysisId');
        return;
      }

      console.log(
        '🔄 [RefreshDataFromDB] Fetching analysis result for ID:',
        effectiveAnalysisId,
      );

      try {
        console.log(
          '🔄 [DEBUG-REFRESH] About to call v2AnalysisApi.getAnalysisResult...',
        );
        const analysisResult =
          await v2AnalysisApi.getAnalysisResult(effectiveAnalysisId);

        console.log('🔄 [DEBUG-REFRESH] API call completed, result:', {
          hasResult: !!analysisResult,
          status: analysisResult?.status,
          keys: analysisResult ? Object.keys(analysisResult) : null,
        });

        if (analysisResult) {
          console.log('🔄 [DEBUG-REFRESH] Processing analysisResult...');
          // 🔧 analysisResult変数の重複宣言エラーを修正

          // ログを一時的に無効化
          // console.log('🔍 [DEBUG] analysisResult structure:', { ... });

          // WebSocketで受信済みのデータを保護
          setCompetitorData(prev => {
            if (!prev) return analysisResult;

            // 保護するデータ（WebSocketで直接受信したもの）
            const protectedData = {};

            // related_keywords, lsi_keywords, keyword_metricsを保護
            if (prev.keywords_analysis) {
              if (
                prev.keywords_analysis.related_keywords?.related_keywords
                  ?.length > 0
              ) {
                if (!analysisResult.keywords_analysis)
                  analysisResult.keywords_analysis = {};
                analysisResult.keywords_analysis.related_keywords =
                  prev.keywords_analysis.related_keywords;
              }
              if (
                prev.keywords_analysis.lsi_keywords?.lsi_candidates?.length > 0
              ) {
                if (!analysisResult.keywords_analysis)
                  analysisResult.keywords_analysis = {};
                analysisResult.keywords_analysis.lsi_keywords =
                  prev.keywords_analysis.lsi_keywords;
              }
              if (prev.keywords_analysis.main_keyword_metrics) {
                if (!analysisResult.keywords_analysis)
                  analysisResult.keywords_analysis = {};
                analysisResult.keywords_analysis.main_keyword_metrics =
                  prev.keywords_analysis.main_keyword_metrics;
              }
            }

            // integrated_heatmapを保護
            if (prev.topic_analysis?.integrated_heatmap?.formatted_heatmaps) {
              if (!analysisResult.topic_analysis)
                analysisResult.topic_analysis = {};
              analysisResult.topic_analysis.integrated_heatmap =
                prev.topic_analysis.integrated_heatmap;
            }

            return analysisResult;
          });

          // 受信データに基づいて dataReady を自動補正（WSイベントが来ない環境対策）
          try {
            const hasSerp = !!analysisResult?.serp_data;
            const hasRelated = !!(
              analysisResult?.keywords_analysis?.related_keywords ||
              analysisResult?.topic_analysis?.integrated_heatmap
                ?.formatted_heatmaps?.related_keywords_heatmap ||
              analysisResult?.keywords_analysis?.heatmap_data
                ?.related_keywords ||
              analysisResult?.keywords_analysis?.heatmap_data?.rows?.some(
                r => r?.keyword_type === 'related',
              )
            );
            const hasLsi = !!(
              analysisResult?.keywords_analysis?.lsi_keywords ||
              analysisResult?.topic_analysis?.integrated_heatmap
                ?.formatted_heatmaps?.lsi_keywords_heatmap ||
              analysisResult?.keywords_analysis?.lsi_heatmap_data ||
              analysisResult?.keywords_analysis?.heatmap_data?.lsi_keywords ||
              analysisResult?.keywords_analysis?.heatmap_data?.rows?.some(
                r => r?.keyword_type === 'lsi',
              )
            );
            const mk = analysisResult?.keywords_analysis?.main_keyword_metrics;
            const hasMetrics =
              !!mk &&
              (typeof mk.search_volume === 'number' ||
                typeof mk.keyword_difficulty === 'number' ||
                typeof mk.cpc === 'number');

            const aio = analysisResult?.aio_analysis;
            const hasAio =
              !!aio &&
              ((aio.ai_overview &&
                (aio.ai_overview.exists ||
                  (aio.ai_overview.text || '').length > 0)) ||
                (Array.isArray(aio.sources) && aio.sources.length > 0));

            // 🔧 完了済み分析では、aio_content_strategyがnullでもtrueとする
            const hasAioContentStrategy =
              !!analysisResult?.aio_content_strategy ||
              (analysisResult?.status === 'completed' &&
                analysisResult?.analysis_id &&
                Object.keys(analysisResult || {}).includes(
                  'aio_content_strategy',
                ));

            console.log('🔍 [DEBUG-AIO] hasAioContentStrategy calculation:', {
              hasStrategy: !!analysisResult?.aio_content_strategy,
              isCompleted: analysisResult?.status === 'completed',
              hasAnalysisId: !!analysisResult?.analysis_id,
              hasKey: Object.keys(analysisResult || {}).includes(
                'aio_content_strategy',
              ),
              finalResult: hasAioContentStrategy,
            });

            const topic = analysisResult?.topic_analysis || {};
            // 新規: SERPs評価/トピックランドスケープの存在確認（summary抽出を含む）
            const serpsEval =
              analysisResult?.serps_evaluation ||
              analysisResult?.serp_data?.serps_evaluation;
            let hasSerpsEvaluation = false;
            if (serpsEval) {
              let s = serpsEval.evaluation_summary || serpsEval.summary;
              if (!s && typeof serpsEval.response === 'string') {
                try {
                  let txt = serpsEval.response.trim();
                  if (txt.startsWith('```json')) txt = txt.slice(7);
                  if (txt.endsWith('```')) txt = txt.slice(0, -3);
                  const inner = JSON.parse(txt.trim());
                  s = inner?.summary;
                } catch (e) {
                  // noop
                }
              }
              // 要約が無くてもオブジェクトが存在すれば解除（完了画面ではスケルトンにしない）
              hasSerpsEvaluation = !!(s || serpsEval);
            }

            const topicLandscape =
              analysisResult?.topic_landscape ||
              analysisResult?.topic_analysis?.landscape;
            let hasTopicLandscape = false;
            if (topicLandscape) {
              let s = topicLandscape.summary;
              if (!s && typeof topicLandscape.response === 'string') {
                try {
                  let txt = topicLandscape.response.trim();
                  if (txt.startsWith('```json')) txt = txt.slice(7);
                  if (txt.endsWith('```')) txt = txt.slice(0, -3);
                  const inner = JSON.parse(txt.trim());
                  s = inner?.summary;
                } catch (e) {
                  // noop
                }
              }
              // 要約が無くてもオブジェクトが存在すれば解除（完了画面ではスケルトンにしない）
              hasTopicLandscape = !!(s || topicLandscape);
            }
            const ih = topic?.integrated_heatmap || {};
            const fm = ih?.formatted_heatmaps || {};
            const hm = ih?.heatmap_data;
            const te = topic?.terms_evaluation?.heatmap_data;
            const hasRows = obj =>
              Array.isArray(obj?.rows) && obj.rows.length > 0;
            const hasFormattedRows =
              fm && typeof fm === 'object'
                ? Object.values(fm).some(
                    h => Array.isArray(h?.rows) && h.rows.length > 0,
                  )
                : false;
            const hasHeatmaps =
              hasRows(hm) ||
              hasRows(te) ||
              hasFormattedRows ||
              hasRelated ||
              hasLsi;

            const cr = analysisResult?.comprehensive_report || {};
            const hasPersona = !!(
              cr?.persona ||
              cr?.summary_data?.persona ||
              analysisResult?.search_intent_analysis?.persona
            );
            const hasNeeds = !!(
              cr?.user_needs ||
              cr?.summary_data?.user_needs ||
              analysisResult?.search_intent_analysis?.user_needs
            );
            const hasCompSummaries = !!(
              cr?.competitor_summaries && cr.competitor_summaries.length > 0
            );
            const hasSiteSummaries = !!(
              cr?.site_summaries && cr.site_summaries.length > 0
            );

            // 競合分析データの存在確認（実際に表示に使用されるデータで判定）
            const hasCompetitorAnalysis = !!(
              analysisResult?.serp_data?.organic_results ||
              analysisResult?.analysis_summary ||
              (analysisResult?.comprehensive_report &&
                Object.keys(analysisResult.comprehensive_report).length > 0)
            );

            // 分析完了状態でのsetDataReady実行を制限（無限ループ防止）
            // progress: 100 または current_step: analysis_complete の場合も完了として扱う
            const isCompleted =
              analysisResult.status === 'completed' ||
              analysisResult.status === 'completed_with_errors' ||
              (analysisResult.progress || 0) >= 100 ||
              analysisResult.current_step === 'analysis_complete';

            console.log('🔄 [DEBUG-REFRESH] Data flags:', {
              hasSerp,
              hasRelated,
              hasLsi,
              hasMetrics,
              hasCompetitorAnalysis,
              hasAio,
              hasAioContentStrategy,
              hasHeatmaps,
              hasPersona,
              hasNeeds,
              isCompleted,
            });

            // 🔍 詳細データパス確認
            console.log('🔍 [DEBUG-KEYWORDS] Keywords analysis structure:', {
              'keywords_analysis exists': !!analysisResult?.keywords_analysis,
              'keywords_analysis type':
                typeof analysisResult?.keywords_analysis,
              'keywords_analysis keys': analysisResult?.keywords_analysis
                ? Object.keys(analysisResult.keywords_analysis)
                : null,
            });

            console.log('🔍 [DEBUG-RELATED] Related keywords path:', {
              'related_keywords exists':
                !!analysisResult?.keywords_analysis?.related_keywords,
              'related_keywords type':
                typeof analysisResult?.keywords_analysis?.related_keywords,
              'related_keywords keys': analysisResult?.keywords_analysis
                ?.related_keywords
                ? Object.keys(analysisResult.keywords_analysis.related_keywords)
                : null,
              'related_keywords.related_keywords exists':
                !!analysisResult?.keywords_analysis?.related_keywords
                  ?.related_keywords,
              'related_keywords.related_keywords length':
                analysisResult?.keywords_analysis?.related_keywords
                  ?.related_keywords?.length,
              'hasRelated calculation': hasRelated,
            });

            console.log('🔍 [DEBUG-LSI] LSI keywords path:', {
              'lsi_keywords exists':
                !!analysisResult?.keywords_analysis?.lsi_keywords,
              'lsi_keywords type':
                typeof analysisResult?.keywords_analysis?.lsi_keywords,
              'lsi_keywords keys': analysisResult?.keywords_analysis
                ?.lsi_keywords
                ? Object.keys(analysisResult.keywords_analysis.lsi_keywords)
                : null,
              'lsi_candidates exists':
                !!analysisResult?.keywords_analysis?.lsi_keywords
                  ?.lsi_candidates,
              'lsi_candidates length':
                analysisResult?.keywords_analysis?.lsi_keywords?.lsi_candidates
                  ?.length,
              'hasLsi calculation': hasLsi,
            });

            console.log('🔍 [DEBUG-METRICS] Keyword metrics path:', {
              'main_keyword_metrics exists':
                !!analysisResult?.keywords_analysis?.main_keyword_metrics,
              'main_keyword_metrics type':
                typeof analysisResult?.keywords_analysis?.main_keyword_metrics,
              'main_keyword_metrics keys': analysisResult?.keywords_analysis
                ?.main_keyword_metrics
                ? Object.keys(
                    analysisResult.keywords_analysis.main_keyword_metrics,
                  )
                : null,
              'search_volume':
                analysisResult?.keywords_analysis?.main_keyword_metrics
                  ?.search_volume,
              'keyword_difficulty':
                analysisResult?.keywords_analysis?.main_keyword_metrics
                  ?.keyword_difficulty,
              'cpc':
                analysisResult?.keywords_analysis?.main_keyword_metrics?.cpc,
              'hasMetrics calculation': hasMetrics,
            });

            // 常にdataReadyを更新（リアルタイム描画のため）
            console.log(
              `🔄 [DEBUG-REFRESH] Updating dataReady (completed: ${isCompleted})...`,
            );
            setDataReady(prev => {
              const inputFlags = {
                serp_data: hasSerp,
                related_keywords: hasRelated,
                lsi_keywords: hasLsi,
                keyword_metrics: hasMetrics,
                competitor_analysis: hasCompetitorAnalysis,
                aio_analysis: hasAio,
                aio_content_strategy: hasAioContentStrategy,
                heatmaps: hasHeatmaps,
                topic_analysis: !!topic,
                serps_evaluation: hasSerpsEvaluation,
                topic_landscape: hasTopicLandscape,
                summary: {
                  persona: hasPersona,
                  user_needs: hasNeeds,
                  competitor_summaries: hasCompSummaries,
                  site_summaries: hasSiteSummaries,
                },
                complete: isCompleted,
              };

              console.log('🔍 [MERGE-DEBUG] mergeDataReady inputs:', {
                'prev': prev,
                'inputFlags': inputFlags,
                'focus_flags': {
                  'prev.related_keywords': prev.related_keywords,
                  'prev.lsi_keywords': prev.lsi_keywords,
                  'prev.keyword_metrics': prev.keyword_metrics,
                  'input.related_keywords': inputFlags.related_keywords,
                  'input.lsi_keywords': inputFlags.lsi_keywords,
                  'input.keyword_metrics': inputFlags.keyword_metrics,
                },
              });

              const newDataReady = mergeDataReady(prev, inputFlags);

              console.log('🔍 [MERGE-DEBUG] mergeDataReady output:', {
                'newDataReady': newDataReady,
                'focus_output': {
                  'output.related_keywords': newDataReady.related_keywords,
                  'output.lsi_keywords': newDataReady.lsi_keywords,
                  'output.keyword_metrics': newDataReady.keyword_metrics,
                },
              });

              console.log('🔄 [DEBUG-REFRESH] New dataReady:', newDataReady);
              console.log('🔄 [DEBUG-REFRESH] Previous dataReady:', prev);
              return newDataReady;
            });
          } catch (e) {
            console.warn('[RefreshDataFromDB] readiness auto-fix failed:', e);
          }

          // sessionDataも更新しておく（再描画・後続処理の安定化）
          updateSessionData({
            step1AnalysisResult: analysisResult,
            analysisId: analysisResult.analysis_id || effectiveAnalysisId,
            hasExistingAnalysis: true,
          });

          // 完了状態ならUIを完了へ
          if (
            (analysisResult.status === 'completed' ||
              analysisResult.status === 'completed_with_errors') &&
            showProgressUI
          ) {
            setShowProgressUI(false);
          }

          // 🔧 refreshDataFromDBはdataReady更新のみに専念（loadExistingAnalysisは呼び出さない）
          console.log(
            '🔄 [DEBUG-REFRESH] Data refresh completed - dataReady updated',
          );
        }
      } catch (error) {
        console.error(
          '🔴 [DEBUG-REFRESH] Failed to load analysis result:',
          error,
        );
        console.error(
          '🔴 [DEBUG-REFRESH] Error details:',
          error.message,
          error.stack,
        );
      }
      return;
    }

    console.log(
      '🔄 [RefreshDataFromDB] Fetching article details for UUID:',
      articleUuid,
    );
    try {
      const articleDetails = await v2ArticlesApi.getArticle(articleUuid);

      console.log('🔄 [RefreshDataFromDB] Article details received:', {
        has_article_details: !!articleDetails,
        has_step1_result: !!articleDetails?.step1_analysis_result,
        article_details_keys: articleDetails
          ? Object.keys(articleDetails)
          : null,
      });

      if (articleDetails && articleDetails.step1_analysis_result) {
        const analysisResult = articleDetails.step1_analysis_result;

        console.log('🔄 [RefreshDataFromDB] Analysis result found:', {
          analysis_result_keys: Object.keys(analysisResult),
          analysis_result_size:
            JSON.stringify(analysisResult).length + ' characters',
        });

        // 🗑️ transformAnalysisResults削除（直接使用）

        console.log('🔄 [RefreshDataFromDB] Data transformed:', {
          transformed_data_keys: Object.keys(analysisResult),
          has_competitor_analyses: !!analysisResult.competitor_analyses,
          has_analysis_summary: !!analysisResult.analysis_summary,
        });

        // フェードイン効果でデータを更新（WebSocketデータを保護）
        setCompetitorData(prev => {
          if (!prev) return analysisResult;

          // WebSocketで受信済みのデータを保護
          if (prev.keywords_analysis) {
            if (
              prev.keywords_analysis.related_keywords?.related_keywords
                ?.length > 0
            ) {
              if (!analysisResult.keywords_analysis)
                analysisResult.keywords_analysis = {};
              analysisResult.keywords_analysis.related_keywords =
                prev.keywords_analysis.related_keywords;
            }
            if (
              prev.keywords_analysis.lsi_keywords?.lsi_candidates?.length > 0
            ) {
              if (!analysisResult.keywords_analysis)
                analysisResult.keywords_analysis = {};
              analysisResult.keywords_analysis.lsi_keywords =
                prev.keywords_analysis.lsi_keywords;
            }
            if (prev.keywords_analysis.main_keyword_metrics) {
              if (!analysisResult.keywords_analysis)
                analysisResult.keywords_analysis = {};
              analysisResult.keywords_analysis.main_keyword_metrics =
                prev.keywords_analysis.main_keyword_metrics;
            }
          }

          // integrated_heatmapを保護
          if (prev.topic_analysis?.integrated_heatmap?.formatted_heatmaps) {
            if (!analysisResult.topic_analysis)
              analysisResult.topic_analysis = {};
            analysisResult.topic_analysis.integrated_heatmap =
              prev.topic_analysis.integrated_heatmap;
          }

          return analysisResult;
        });

        console.log(
          '✅ [RefreshDataFromDB] CompetitorData updated with fresh data from DB',
        );

        // 受信データに基づいて dataReady を自動補正（WSイベントが来ない環境対策）
        try {
          const hasSerp = !!analysisResult?.serp_data;
          const hasRelated =
            !!analysisResult?.keywords_analysis?.related_keywords;
          const hasLsi = !!analysisResult?.keywords_analysis?.lsi_keywords;
          const hasMetrics =
            !!analysisResult?.keywords_analysis?.main_keyword_metrics;

          const aio = analysisResult?.aio_analysis;
          const hasAio =
            !!aio &&
            ((aio.ai_overview &&
              (aio.ai_overview.exists ||
                (aio.ai_overview.text || '').length > 0)) ||
              (Array.isArray(aio.sources) && aio.sources.length > 0));

          const topic = analysisResult?.topic_analysis || {};
          const ih = topic?.integrated_heatmap || {};
          const fm = ih?.formatted_heatmaps || {};
          const hm = ih?.heatmap_data;
          const te = topic?.terms_evaluation?.heatmap_data;
          const hasRows = obj =>
            Array.isArray(obj?.rows) && obj.rows.length > 0;
          const hasFormattedRows =
            fm && typeof fm === 'object'
              ? Object.values(fm).some(
                  h => Array.isArray(h?.rows) && h.rows.length > 0,
                )
              : false;
          const hasHeatmaps = hasRows(hm) || hasRows(te) || hasFormattedRows;

          const cr = analysisResult?.comprehensive_report || {};
          const hasPersona = !!(
            cr?.persona ||
            cr?.summary_data?.persona ||
            analysisResult?.search_intent_analysis?.persona
          );
          const hasNeeds = !!(
            cr?.user_needs ||
            cr?.summary_data?.user_needs ||
            analysisResult?.search_intent_analysis?.user_needs
          );
          const hasCompSummaries = !!(
            cr?.competitor_summaries && cr.competitor_summaries.length > 0
          );
          const hasSiteSummaries = !!(
            cr?.site_summaries && cr.site_summaries.length > 0
          );

          setDataReady(prev =>
            mergeDataReady(prev, {
              serp_data: hasSerp,
              related_keywords: hasRelated,
              lsi_keywords: hasLsi,
              keyword_metrics: hasMetrics,
              aio_analysis: hasAio,
              heatmaps: hasHeatmaps,
              topic_analysis: !!topic,
              summary: {
                persona: hasPersona,
                user_needs: hasNeeds,
                competitor_summaries: hasCompSummaries,
                site_summaries: hasSiteSummaries,
              },
              complete: true,
            }),
          );
        } catch (e) {
          console.warn('[RefreshDataFromDB] readiness auto-fix failed:', e);
        }
      } else {
        console.warn(
          '⚠️ [RefreshDataFromDB] No step1_analysis_result found in article details',
        );
      }
    } catch (error) {
      console.error(
        '🔴 [RefreshDataFromDB] Failed to load analysis data from DB:',
        error,
      );
    }
  };
  // 既存記事の分析データをDBから読み込む
  // 🎯 シンプル化されたデータ読み込み
  const loadExistingAnalysis = async () => {
    console.log(
      '🔴 [loadAnalysisDataFromDB] Function called with analysis_id:',
      analysisState.analysisId,
    );
    if (!analysisState.analysisId) return;

    setLoading(true);
    try {
      console.log(
        '🔍 [DEBUG] About to call v2AnalysisApi.getAnalysisResult with:',
        analysisState.analysisId,
      );

      const apiProvider = isGuest ? publicSamplesApi : v2AnalysisApi;
      const analysisResult = await apiProvider.getAnalysisResult(
        analysisState.analysisId,
      );

      console.log('🔍 [DEBUG] analysisResult received:', analysisResult);
      console.log('🔍 [DEBUG] analysisResult type:', typeof analysisResult);

      if (analysisResult) {
        // 分析結果を直接使用

        // データ構造をデバッグ

        if (analysisResult.topic_analysis) {
          const ta = analysisResult.topic_analysis;
        }

        // 🎯 完了済みデータの正確な判定：aio_content_strategyが存在する場合は完了済み
        const isActuallyCompleted =
          analysisResult.aio_content_strategy ||
          analysisResult.status === 'completed';
        const finalStatus = isActuallyCompleted
          ? 'completed'
          : analysisResult.status;

        setAnalysisState({
          status: finalStatus,
          progress: isActuallyCompleted ? 100 : analysisResult.progress || 0,
          currentStep: isActuallyCompleted
            ? '完了'
            : analysisResult.current_step || '分析中',
          analysisId: analysisResult.analysis_id,
          data: analysisResult,
        });

        // 🎯 シンプル化：transformAnalysisResultsを使わず直接設定
        setCompetitorData(analysisResult);

        // 🗑️ sessionData更新削除（analysis_idベースで独立）

        // 🗑️ // 🗑️ setAnalysisExecuted削除削除（analysisState.statusで管理）

        // 🎯 シンプル化：データ準備状況を実際のデータ存在に基づいて設定
        const hasSerp = !!analysisResult?.serp_data?.organic_results;
        const hasRelated =
          !!analysisResult?.keywords_analysis?.related_keywords;
        const hasLsi = !!analysisResult?.keywords_analysis?.lsi_keywords;
        const hasMetrics =
          !!analysisResult?.keywords_analysis?.main_keyword_metrics;
        const hasCompetitor = !!analysisResult?.competitor_analysis;
        const hasAio = !!analysisResult?.aio_analysis;
        const hasHeatmaps =
          !!analysisResult?.topic_analysis?.integrated_heatmap;
        const hasTopic = !!analysisResult?.topic_analysis;
        const hasAioContent = !!analysisResult?.aio_content_strategy;

        console.log('🔍 [loadAnalysisDataFromDB] データ存在チェック:', {
          hasSerp,
          hasRelated,
          hasLsi,
          hasMetrics,
          hasCompetitor,
          hasAio,
          hasHeatmaps,
          hasTopic,
          hasAioContent,
          analysisResultKeys: analysisResult
            ? Object.keys(analysisResult)
            : null,
          analysisResultSize: analysisResult
            ? JSON.stringify(analysisResult).length
            : 0,
        });

        // 詳細なデータ構造をログ出力
        console.log('🔍 [loadAnalysisDataFromDB] 詳細データ構造:', {
          'serp_data.organic_results':
            analysisResult?.serp_data?.organic_results,
          'keywords_analysis.related_keywords':
            analysisResult?.keywords_analysis?.related_keywords,
          'keywords_analysis.lsi_keywords':
            analysisResult?.keywords_analysis?.lsi_keywords,
          'keywords_analysis.main_keyword_metrics':
            analysisResult?.keywords_analysis?.main_keyword_metrics,
          'competitor_analysis': analysisResult?.competitor_analysis,
          'aio_analysis': analysisResult?.aio_analysis,
          'topic_analysis.integrated_heatmap':
            analysisResult?.topic_analysis?.integrated_heatmap,
          'topic_analysis': analysisResult?.topic_analysis,
          'aio_content_strategy': analysisResult?.aio_content_strategy,
        });

        // サマリー関連
        const hasPersona =
          !!analysisResult?.comprehensive_report?.search_intent_analysis
            ?.persona;
        const hasNeeds =
          !!analysisResult?.comprehensive_report?.search_intent_analysis
            ?.user_needs;
        const hasCompSummaries = !!(
          analysisResult?.comprehensive_report?.competitor_summaries?.length > 0
        );
        const hasSiteSummaries = !!(
          analysisResult?.comprehensive_report?.site_summaries?.site_summaries
            ?.length > 0
        );

        // 🎯 正しいデータパスでdataReadyを設定
        // 🔧 ヒートマップデータの存在確認
        const hasIntegratedHeatmap =
          !!analysisResult?.topic_analysis?.integrated_heatmap;
        const hasTopicHeatmap = !!(
          analysisResult?.topic_analysis?.formatted_heatmaps?.topic_heatmap
            ?.rows?.length > 0 ||
          analysisResult?.topic_analysis?.site_topic_analysis?.heatmap_data
            ?.rows?.length > 0
        );
        const hasTermHeatmap = !!(
          analysisResult?.topic_analysis?.formatted_heatmaps?.term_heatmap?.rows
            ?.length > 0
        );
        const hasRelatedHeatmap = !!(
          analysisResult?.topic_analysis?.formatted_heatmaps?.related_heatmap
            ?.rows?.length > 0 ||
          analysisResult?.topic_analysis?.integrated_heatmap?.related_keywords
            ?.rows?.length > 0
        );
        const hasLsiHeatmap = !!(
          analysisResult?.topic_analysis?.formatted_heatmaps?.lsi_heatmap?.rows
            ?.length > 0 ||
          analysisResult?.topic_analysis?.integrated_heatmap?.lsi_keywords?.rows
            ?.length > 0
        );

        const correctDataReady = {
          serp_data: !!analysisResult?.serp_data?.organic_results,
          related_keywords:
            !!analysisResult?.keywords_analysis?.related_keywords,
          lsi_keywords: !!analysisResult?.keywords_analysis?.lsi_keywords,
          keyword_metrics:
            !!analysisResult?.keywords_analysis?.main_keyword_metrics,
          competitor_analysis: !!(
            analysisResult?.competitor_analysis?.competitors?.length > 0
          ),
          aio_analysis: !!analysisResult?.aio_analysis,
          heatmaps: hasIntegratedHeatmap,
          topic_analysis: !!analysisResult?.topic_analysis,
          // 🔧 完了済み分析では、aio_content_strategyがnullでもtrueとする
          aio_content_strategy:
            !!analysisResult?.aio_content_strategy ||
            (analysisResult?.status === 'completed' &&
              Object.keys(analysisResult || {}).includes(
                'aio_content_strategy',
              )),
          summary: {
            persona:
              !!analysisResult?.search_intent_analysis?.persona ||
              !!analysisResult?.comprehensive_report?.search_intent_analysis
                ?.persona ||
              !!analysisResult?.comprehensive_report?.persona,
            user_needs:
              !!analysisResult?.search_intent_analysis?.user_needs ||
              !!analysisResult?.comprehensive_report?.search_intent_analysis
                ?.user_needs ||
              !!analysisResult?.comprehensive_report?.user_needs,
            competitor_summaries: !!(
              analysisResult?.competitor_analysis?.competitors?.length > 0
            ),
            site_summaries: !!(
              analysisResult?.comprehensive_report?.site_summaries
                ?.site_summaries?.length > 0
            ),
          },
          aio_slots: false,
          // 🔧 ヒートマップ専用フラグ（実際のヒートマップデータの存在で判定）
          heatmap_topics: hasTopicHeatmap || hasIntegratedHeatmap,
          heatmap_terms: hasTermHeatmap || hasIntegratedHeatmap,
          heatmap_related: hasRelatedHeatmap || hasIntegratedHeatmap,
          heatmap_lsi: hasLsiHeatmap || hasIntegratedHeatmap,
          complete: true,
        };

        console.log(
          '🔧 [DEBUG-AIO-DATAREADY] aio_content_strategy calculation:',
          {
            hasStrategy: !!analysisResult?.aio_content_strategy,
            isCompleted: analysisResult?.status === 'completed',
            hasKey: Object.keys(analysisResult || {}).includes(
              'aio_content_strategy',
            ),
            finalResult: correctDataReady.aio_content_strategy,
          },
        );

        console.log(
          '🎯 [loadExistingAnalysis] Setting correct dataReady:',
          correctDataReady,
        );
        setDataReady(correctDataReady);

        // 🔧 分析ステータスを適切に設定（完了済みデータの場合のみ）
        console.log('🔧 [DEBUG-STATUS] Checking status conditions:', {
          hasAnalysisResult: !!analysisResult,
          currentStatus: analysisResult?.status,
          keyCount: analysisResult ? Object.keys(analysisResult).length : 0,
          isCompleted: analysisResult?.status === 'completed',
        });

        // 🔧 完了済みデータの正確な判定：aio_content_strategyが存在する場合は完了済み
        const isLoadCompleted =
          analysisResult?.aio_content_strategy ||
          analysisResult?.status === 'completed';
        const loadFinalStatus = isLoadCompleted
          ? 'completed'
          : analysisResult?.status;

        if (analysisResult && loadFinalStatus) {
          console.log(
            `🔧 [DEBUG-STATUS] Setting status to ${loadFinalStatus} (actually completed: ${isLoadCompleted})`,
          );
          setAnalysisState(prev => ({
            ...prev,
            status: loadFinalStatus,
            progress: isLoadCompleted
              ? 100
              : analysisResult.progress || prev.progress,
          }));
          console.log(
            `🔧 [loadExistingAnalysis] Set status to ${loadFinalStatus}`,
          );
        }
      } else {
        // 分析データがない場合の処理
        setInitializing(false);

        // 空のデータでも最低限の表示を行う
        setDataReady({
          serp_data: false,
          related_keywords: false,
          lsi_keywords: false,
          keyword_metrics: false,
          competitor_analysis: false,
          aio_analysis: false,
          heatmaps: false,
          topic_analysis: false,
          aio_content_strategy: false,
          summary: {
            persona: false,
            user_needs: false,
            competitor_summaries: false,
            site_summaries: false,
          },
          aio_slots: false,
          complete: false,
        });
      }
    } catch (error) {
      // エラーハンドリングを強化
      console.error('🔴 [loadAnalysisDataFromDB] Error occurred:', error);
      console.error(
        '🔴 [loadAnalysisDataFromDB] Error message:',
        error.message,
      );
      console.error('🔴 [loadAnalysisDataFromDB] Error stack:', error.stack);
      setInitializing(false);

      // 空のデータでも最低限の表示を行う
      setDataReady({
        serp_data: false,
        related_keywords: false,
        lsi_keywords: false,
        keyword_metrics: false,
        competitor_analysis: false,
        aio_analysis: false,
        heatmaps: false,
        topic_analysis: false,
        aio_content_strategy: false,
        summary: {
          persona: false,
          user_needs: false,
          competitor_summaries: false,
          site_summaries: false,
        },
        aio_slots: false,
        complete: false,
      });
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };
  // 🗑️ transformAnalysisResults関数削除開始
  const DELETED_transformAnalysisResults = analysisResult => {
    if (!analysisResult) return null;

    // 新しい構造と古い構造の両方に対応
    const {
      serp_data,
      competitor_analysis,
      keywords_analysis,
      aio_analysis,
      topic_analysis,
      competitor_scoring,
      comprehensive_report,
      // 新しい構造のフィールド
      analysis_summary,
      keywords_data,
      competitor_analyses,
      heatmap_data,
      // 直接アクセス可能な構造
      serp_analysis,
      // AIOコンテンツ戦略
      aio_content_strategy,
    } = analysisResult;

    // serp_dataが存在する場合はそのまま使用
    const actualSerpData = serp_data || serp_analysis || {};

    // 新しい構造かどうかの判定を最適化
    // 複数の判定基準を組み合わせて、より信頼性の高い判定を実行
    const hasProperCompetitorStructure =
      competitor_analysis?.competitors &&
      Array.isArray(competitor_analysis.competitors) &&
      competitor_analysis.competitors.length > 0;

    // キーワード分析の新しい構造判定
    const hasProperKeywordStructure =
      keywords_analysis?.suggest_keywords &&
      Array.isArray(keywords_analysis.suggest_keywords) &&
      keywords_analysis.lsi_keywords?.lsi_candidates;

    // SERPデータの新しい構造判定
    const hasProperSerpStructure =
      actualSerpData?.organic_results &&
      Array.isArray(actualSerpData.organic_results) &&
      actualSerpData.organic_results.length > 0;

    // 分析サマリーの存在判定
    const hasAnalysisSummary =
      analysis_summary && Object.keys(analysis_summary).length > 0;

    // 総合的な新しい構造判定（複数の条件を組み合わせ）
    const isNewStructure =
      hasProperCompetitorStructure ||
      hasProperKeywordStructure ||
      hasProperSerpStructure ||
      hasAnalysisSummary;

    // AI Overview処理
    const aiOverview = actualSerpData?.ai_overview || null;
    let aiOverviewData = null;

    if (aiOverview && aiOverview.elements && aiOverview.elements.length > 0) {
      // 新しい構造に対応
      const allReferences = [];
      let totalTextLength = 0;

      // 全要素から参照元を収集
      aiOverview.elements.forEach(element => {
        if (element.text) {
          totalTextLength += element.text.length;
        }
        if (element.references && element.references.length > 0) {
          allReferences.push(...element.references);
        }
      });

      // 重複を除去（ドメインベース）
      const uniqueReferences = [];
      const seenDomains = new Set();

      allReferences.forEach(ref => {
        if (ref.domain && !seenDomains.has(ref.domain)) {
          seenDomains.add(ref.domain);
          uniqueReferences.push(ref);
        }
      });

      aiOverviewData = {
        elements: aiOverview.elements,
        references: uniqueReferences.slice(0, 10), // 最大10個
        timestamp:
          aiOverview.timestamp ||
          actualSerpData?.timestamp ||
          new Date().toISOString(),
        totalTextLength: totalTextLength,
        elementsCount: aiOverview.elements.length,
      };
    }

    // 既存フォーマットに変換
    return {
      // 新しい構造と古い構造の両方に対応
      ...(isNewStructure
        ? {
            // 新しい構造でのデータマッピング
            analysis_summary: analysis_summary,
            keywords_data: keywords_data,
            competitor_analyses: competitor_analyses,
            heatmap_data: heatmap_data,
            competitors:
              competitor_analysis?.competitors || competitor_analyses || [],
            important_terms_analysis: {
              terms:
                competitor_analyses?.flatMap(c => c.important_terms || []) ||
                [],
              total_terms:
                competitor_analyses?.reduce(
                  (sum, c) => sum + (c.important_terms?.length || 0),
                  0,
                ) || 0,
              unique_terms: 0,
              analysis_timestamp: null,
            },
          }
        : {
            // 古い構造でのデータマッピング
            competitors: competitor_analysis?.competitor_details || [],
            important_terms_analysis: {
              terms:
                competitor_analysis?.important_terms_analysis
                  ?.extracted_terms || [],
              total_terms:
                competitor_analysis?.important_terms_analysis
                  ?.total_terms_extracted || 0,
              unique_terms:
                competitor_analysis?.important_terms_analysis?.unique_terms ||
                0,
              analysis_timestamp:
                competitor_analysis?.important_terms_analysis
                  ?.analysis_timestamp || null,
            },
          }),

      pattern_match_analysis:
        competitor_analysis?.pattern_match_analysis || null,

      summary: {
        search_volume:
          keywords_analysis?.main_keyword_metrics?.search_volume || 0,
        difficulty:
          keywords_analysis?.main_keyword_metrics?.keyword_difficulty ?? 0,
        cpc: keywords_analysis?.main_keyword_metrics?.cpc
          ? Math.floor(keywords_analysis.main_keyword_metrics.cpc * 150)
          : 0,
        competition: keywords_analysis?.main_keyword_metrics?.competition ?? 0,
        search_intent: {
          primary:
            actualSerpData?.serp_features_analysis?.search_intent?.primary ||
            '情報収集',
          secondary:
            actualSerpData?.serp_features_analysis?.search_intent?.secondary ||
            '',
          user_needs: actualSerpData?.serp_features_analysis?.search_intent
            ?.user_needs || [
            '基本情報の理解',
            '実践的なアドバイス',
            '具体的な事例',
          ],
        },
        persona: {
          age: '20-40代',
          role: 'ビジネスパーソン',
          knowledge_level: '初級～中級',
          goals: ['基本知識の習得', '実践的な情報の収集', '効果的な手法の理解'],
        },
        related_keywords: (() => {
          try {
            const baseKeyword =
              analysis_summary?.main_keyword ||
              keywords_analysis?.main_keyword ||
              sessionData.keyword ||
              '';

            if (isNewStructure) {
              // 新しい構造での処理
              const relatedKeywords =
                keywords_analysis?.related_keywords?.related_keywords ||
                keywords_analysis?.related_keywords ||
                [];

              if (
                Array.isArray(relatedKeywords) &&
                relatedKeywords.length > 0
              ) {
                return relatedKeywords
                  .filter(kw => kw.keyword !== baseKeyword)
                  .map(kw => ({
                    keyword: kw.keyword || '',
                    full_keyword: kw.keyword || '',
                    volume: kw.search_volume || 0,
                    difficulty: kw.keyword_difficulty || 0,
                    cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0, // USD→JPY換算
                    competition_level:
                      kw.competition_level?.toLowerCase() || 'low',
                  }));
              }

              return [];
            } else {
              // 古い構造での処理（既存のコード）
              const suggestKeywords = keywords_analysis?.suggest_keywords;

              if (
                Array.isArray(suggestKeywords) &&
                suggestKeywords.length > 0
              ) {
                return suggestKeywords
                  .filter(kw => kw.keyword !== baseKeyword)
                  .map(kw => ({
                    keyword: kw.keyword || '',
                    full_keyword: kw.keyword || '',
                    volume: kw.search_volume || 0,
                    difficulty: kw.keyword_difficulty || 0,
                    cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0, // USD→JPY換算
                    competition_level:
                      kw.competition_level?.toLowerCase() || 'low',
                  }));
              }

              return [];
            }
          } catch (error) {
            return [];
          }
        })(),
        suggest_keywords: (() => {
          try {
            // まずserp_dataから直接サジェストキーワードを取得
            const serpSuggestKeywords = actualSerpData?.suggest_keywords;
            const baseKeyword =
              analysisResult?.keywords_analysis?.main_keyword ||
              sessionData.keyword ||
              '';

            if (
              Array.isArray(serpSuggestKeywords) &&
              serpSuggestKeywords.length > 0
            ) {
              return serpSuggestKeywords
                .filter(keyword => {
                  // ベースキーワードと完全一致するものを除外
                  return keyword !== baseKeyword;
                })
                .map(keyword => ({
                  keyword: keyword,
                  full_keyword: keyword,
                  volume: Math.floor(Math.random() * 2000) + 100, // 暫定値
                  difficulty: Math.floor(Math.random() * 80) + 20, // 暫定値
                  cpc: Math.floor((Math.random() * 3 + 1) * 150), // USD→JPY換算 (1-4ドル)
                  competition_level: ['low', 'medium', 'high'][
                    Math.floor(Math.random() * 3)
                  ],
                }));
            }

            // フォールバック1: keywords_analysis.suggest_keywords
            const suggestKws = keywords_analysis?.suggest_keywords;
            if (Array.isArray(suggestKws) && suggestKws.length > 0) {
              return suggestKws
                .filter(kw => {
                  const keyword = kw?.keyword || '';
                  // ベースキーワードと完全一致するものを除外
                  return keyword !== baseKeyword;
                })
                .map(kw => ({
                  keyword: kw.keyword || '',
                  full_keyword: kw.keyword || '',
                  volume: kw.search_volume || 0,
                  difficulty: kw.keyword_difficulty || 0,
                  cpc: kw.cpc || 0, // 既に円変換済み
                  competition_level: kw.competition_level || '',
                }));
            }

            // フォールバック2: heatmap_dataから関連キーワードを取得
            const heatmapData = keywords_analysis?.heatmap_data;
            if (heatmapData && heatmapData.rows) {
              const relatedKeywords = heatmapData.rows.filter(
                row => row.keyword_type === 'related',
              );

              if (relatedKeywords.length > 0) {
                return relatedKeywords.map(row => ({
                  keyword: row.keyword,
                  search_volume: row.values?.[0]?.strength || 0,
                  keyword_difficulty: Math.floor(Math.random() * 80) + 20,
                  cpc: Math.floor((Math.random() * 200 + 50) * 150), // USD→JPY換算
                  competition: row.average_relevance || 0,
                  trend: 0,
                }));
              }
            }

            return [];
          } catch (error) {
            return [];
          }
        })(),
        people_also_ask: (() => {
          try {
            const paa = actualSerpData?.people_also_ask;
            if (Array.isArray(paa) && paa.length > 0) {
              return paa
                .filter(item => {
                  // 質問と回答が両方存在するもののみを含める
                  const question =
                    item?.question ||
                    item?.title ||
                    (typeof item === 'string' ? item : '');
                  const answer = item?.answer || item?.description || '';
                  return question.trim() !== '' && answer.trim() !== '';
                })
                .map(item => ({
                  question:
                    item?.question ||
                    item?.title ||
                    (typeof item === 'string' ? item : ''),
                  answer: item?.answer || item?.description || '',
                  source_url: item?.source_url || item?.url || '',
                  source_domain: item?.source_domain || item?.domain || '',
                  source_title: item?.source_title || '',
                }));
            }
            return [];
          } catch (error) {
            console.error('Error processing people also ask:', error);
            return [];
          }
        })(),
        people_also_search: (() => {
          try {
            const pas = actualSerpData?.people_also_search;
            if (Array.isArray(pas)) {
              return pas.filter(
                item => typeof item === 'string' || item?.keyword,
              );
            }
            return [];
          } catch (error) {
            console.error('Error processing people also search:', error);
            return [];
          }
        })(),
        lsi_keywords: (() => {
          try {
            if (isNewStructure) {
              // 新しい構造での処理
              const lsiKeywords =
                keywords_analysis?.lsi_keywords?.lsi_candidates ||
                keywords_analysis?.lsi_keywords ||
                [];

              if (Array.isArray(lsiKeywords) && lsiKeywords.length > 0) {
                const processedLsiKeywords = lsiKeywords.map(kw => ({
                  keyword: kw.keyword || '',
                  search_volume: kw.search_volume || 0,
                  keyword_difficulty: kw.keyword_difficulty || 0,
                  competition: kw.competition || 0,
                  competition_level: kw.competition_level || '',
                  cpc: kw.cpc && kw.cpc > 0 ? Math.floor(kw.cpc * 150) : 0, // ドル→円変換
                  search_intent: kw.search_intent || '',
                  lsi_level: kw.lsi_level || 1,
                  parent_keyword: kw.parent_keyword || '',
                }));

                return {
                  level_breakdown: {
                    level1_count: processedLsiKeywords.length,
                    level2_count: 0,
                    total_count: processedLsiKeywords.length,
                  },
                  level1_keywords: processedLsiKeywords,
                  level2_keywords: [],
                  lsi_candidates: processedLsiKeywords,
                };
              }

              return {
                level_breakdown: {
                  level1_count: 0,
                  level2_count: 0,
                  total_count: 0,
                },
                level1_keywords: [],
                level2_keywords: [],
                lsi_candidates: [],
              };
            } else {
              // 古い構造での処理（既存のコード）
              const lsiData = keywords_analysis?.lsi_keywords;

              if (lsiData && typeof lsiData === 'object') {
                // level1_keywords と level2_keywords を取得
                const level1Keywords = lsiData.level1_keywords || [];
                const level2Keywords = lsiData.level2_keywords || [];
                const lsiCandidates = lsiData.lsi_candidates || [];

                return {
                  level_breakdown: lsiData.level_breakdown || {
                    level1_count: level1Keywords.length,
                    level2_count: level2Keywords.length,
                    total_count: level1Keywords.length + level2Keywords.length,
                  },
                  level1_keywords: level1Keywords.map(kw => ({
                    keyword: kw.keyword || '',
                    search_volume: kw.search_volume || 0,
                    keyword_difficulty: kw.keyword_difficulty || 0,
                    competition: kw.competition || 0,
                    competition_level: kw.competition_level || '',
                    cpc: kw.cpc && kw.cpc > 0 ? Math.floor(kw.cpc * 150) : 0, // ドル→円変換
                    search_intent: kw.search_intent || '',
                    lsi_level: kw.lsi_level || 1,
                    parent_keyword: kw.parent_keyword || '',
                  })),
                  level2_keywords: level2Keywords.map(kw => ({
                    keyword: kw.keyword || '',
                    search_volume: kw.search_volume || 0,
                    keyword_difficulty: kw.keyword_difficulty || 0,
                    competition: kw.competition || 0,
                    competition_level: kw.competition_level || '',
                    cpc: kw.cpc && kw.cpc > 0 ? Math.floor(kw.cpc * 150) : 0, // ドル→円変換
                    search_intent: kw.search_intent || '',
                    lsi_level: kw.lsi_level || 2,
                    parent_keyword: kw.parent_keyword || '',
                  })),
                  lsi_candidates: lsiCandidates,
                };
              }

              return {
                level_breakdown: {
                  level1_count: 0,
                  level2_count: 0,
                  total_count: 0,
                },
                level1_keywords: [],
                level2_keywords: [],
                lsi_candidates: [],
              };
            }
          } catch (error) {
            console.error('Error processing LSI keywords:', error);
            return {
              level_breakdown: {
                level1_count: 0,
                level2_count: 0,
                total_count: 0,
              },
              level1_keywords: [],
              level2_keywords: [],
              lsi_candidates: [],
            };
          }
        })(),
        // AI Overview データを summary に含める
        ai_overview: aiOverviewData,
      },
      // 各データソースも個別に含める（AIOタブ等で使用）
      serp_data: actualSerpData || {},
      aio_analysis: aio_analysis
        ? {
            ...aio_analysis,
            aio_present: !!(
              aiOverviewData &&
              aiOverviewData.elements &&
              aiOverviewData.elements.length > 0
            ),
            references_count: aiOverviewData?.references?.length || 0,
          }
        : {},
      keywords_analysis: keywords_analysis || {},
      competitor_analysis: isNewStructure
        ? competitor_analyses || []
        : competitor_analysis || {},
      topic_analysis: topic_analysis || {},
      competitor_scoring: competitor_scoring || {},
      comprehensive_report: comprehensive_report || {},
      // AIOコンテンツ戦略を追加
      aio_content_strategy: aio_content_strategy || null,
    };
  };

  // 選択の概念は廃止（関連関数を削除）
  // お気に入りキーワード初期読み込み
  useEffect(() => {
    const loadFavoriteKeywords = async () => {
      try {
        const response = await favoriteKeywordsAPI.getFavoriteKeywords({
          limit: 1000,
        });
        const keywords = response.keywords.map(kw => kw.keyword);
        setFavoriteKeywords(keywords);
      } catch (error) {
        console.warn('お気に入りキーワードの読み込みに失敗:', error);
      }
    };

    if (!isSharedView && !isGuest) {
      loadFavoriteKeywords();
    }
  }, [isSharedView, isGuest]);

  const toggleFavoriteKeyword = async keyword => {
    if (isSharedView) return; // 共有ビューでは操作不可

    const isCurrentlyFavorite = favoriteKeywords.includes(keyword);
    setFavoriteKeywordsLoading(prev => ({ ...prev, [keyword]: true }));

    try {
      if (isCurrentlyFavorite) {
        // お気に入りから削除
        await favoriteKeywordsAPI.deleteFavoriteKeyword(keyword);
        setFavoriteKeywords(prev => prev.filter(k => k !== keyword));
        message.success(`「${keyword}」をお気に入りから削除しました`);
      } else {
        // お気に入りに追加
        const keywordData = {
          keyword,
          sourceAnalysisId: analysisState.analysisId,
        };

        // 現在の分析結果からメトリクスを取得
        const relatedKeywordsObj =
          competitorData?.keywords_analysis?.related_keywords;
        const lsiKeywordsObj = competitorData?.keywords_analysis?.lsi_keywords;

        // 関連キーワードから検索
        const relatedData =
          relatedKeywordsObj?.related_keywords ||
          relatedKeywordsObj?.keywords ||
          [];
        const relatedKeyword = relatedData.find(kw => kw.keyword === keyword);

        // LSIキーワードから検索
        const lsiData =
          lsiKeywordsObj?.lsi_candidates || lsiKeywordsObj?.keywords || [];
        const lsiKeyword = lsiData.find(kw => kw.keyword === keyword);

        // メトリクスを設定
        const foundKeyword = relatedKeyword || lsiKeyword;
        if (foundKeyword) {
          keywordData.searchVolume = foundKeyword.search_volume;
          keywordData.keywordDifficulty = foundKeyword.keyword_difficulty;
          keywordData.cpc = foundKeyword.cpc;
        }

        await favoriteKeywordsAPI.addFavoriteKeyword(keywordData);
        setFavoriteKeywords(prev => [...prev, keyword]);
        message.success(`「${keyword}」をお気に入りに追加しました`);
      }
    } catch (error) {
      console.error('お気に入りキーワードの操作に失敗:', error);
      if (
        error.response?.status === 400 &&
        error.response?.data?.detail?.includes('既に')
      ) {
        message.warning(`「${keyword}」は既にお気に入りに追加されています`);
      } else {
        message.error(
          `お気に入りキーワードの操作に失敗しました: ${error.message}`,
        );
      }
    } finally {
      setFavoriteKeywordsLoading(prev => ({ ...prev, [keyword]: false }));
    }
  };

  const handleNext = () => {
    // 選択の概念を廃し、保存対象を縮小
    updateSessionData({ competitorData });
    onNext();
  };
  // 見出しの階層構造を正しく表示する関数
  const renderHeadingHierarchy = headings => {
    if (!headings || headings.length === 0) return null;

    const levelStyles = {
      2: { color: '#fa8c16', backgroundColor: '#fff7e6' },
      3: { color: '#faad14', backgroundColor: '#fffbe6' },
      4: { color: '#52c41a', backgroundColor: '#f6ffed' },
      5: { color: '#1890ff', backgroundColor: '#e6f7ff' },
      6: { color: '#722ed1', backgroundColor: '#f9f0ff' },
    };

    // 階層構造を構築
    const buildHierarchy = headings => {
      const result = [];
      const levelStack = []; // 各レベルの最後の位置を記録

      headings.forEach((heading, index) => {
        const level = heading.level || 2;
        const text = heading.text || '';
        const displayText =
          text.length > 40 ? text.substring(0, 40) + '...' : text;
        const style = levelStyles[level] || levelStyles[6];

        // 現在のレベルより深いレベルをスタックから削除
        while (
          levelStack.length > 0 &&
          levelStack[levelStack.length - 1].level >= level
        ) {
          levelStack.pop();
        }

        // 現在の深度を計算（親の数）
        const depth = levelStack.length;

        const headingElement = (
          <div
            key={index}
            style={{
              marginBottom: '6px',
              paddingLeft: `${depth * 20}px`, // 階層に応じたインデント
              position: 'relative',
            }}
          >
            {/* 階層の接続線 */}
            {depth > 0 && (
              <>
                {/* 親レベルからの垂直線 */}
                {levelStack.map((parentLevel, parentIndex) => (
                  <div
                    key={parentIndex}
                    style={{
                      position: 'absolute',
                      left: `${parentIndex * 20 + 10}px`,
                      top: '-6px',
                      bottom:
                        parentIndex === levelStack.length - 1 ? '50%' : '100%',
                      width: '1px',
                      backgroundColor: '#d9d9d9',
                    }}
                  />
                ))}
                {/* 水平線 */}
                <div
                  style={{
                    position: 'absolute',
                    left: `${(depth - 1) * 20 + 10}px`,
                    top: '50%',
                    width: '10px',
                    height: '1px',
                    backgroundColor: '#d9d9d9',
                  }}
                />
              </>
            )}

            <Tag
              style={{
                ...style,
                marginRight: '8px',
                fontWeight: 'bold',
                border: `1px solid ${style.color}`,
                fontSize: '11px',
              }}
            >
              H{level}
            </Tag>
            <span style={{ fontSize: '13px' }} title={text}>
              {displayText}
            </span>
          </div>
        );

        result.push(headingElement);

        // 現在の要素をスタックに追加
        levelStack.push({ level, index });
      });

      return result;
    };

    return buildHierarchy(headings);
  };
  // 分析サマリータブ
  const renderSummaryTab = () => {
    try {
      const { summary } = competitorData || {};

      // summaryが未定義の場合は空オブジェクトを使用
      const safeeSummary = summary || {};

      // さらに安全のために、各プロパティにデフォルト値を設定
      const safeSummaryWithDefaults = {
        persona: safeeSummary.persona || {},
        search_intent: safeeSummary.search_intent || {},
        related_keywords: safeeSummary.related_keywords || [],
        suggest_keywords: safeeSummary.suggest_keywords || [],
        lsi_keywords: safeeSummary.lsi_keywords || {},
        people_also_ask: safeeSummary.people_also_ask || [],
        people_also_search: safeeSummary.people_also_search || [],
        search_volume: safeeSummary.search_volume || 0,
        difficulty: safeeSummary.difficulty || 0,
        cpc: safeeSummary.cpc || 0,
        ...safeeSummary,
      };

      return (
        <Row gutter={[16, 16]}>
          {/* 統計情報 - ユーザー入力キーワードの実際値 */}
          <Col xs={24}>
            <Card
              className="v2-card"
              title={`統計情報（キーワード: ${sessionData?.keyword || sessionData?.keywords || 'N/A'}）`}
              hoverable={false}
            >
              {/* 先頭段：サイト種別分布（左3）+ SERPs評価（右7） */}
              <Row
                gutter={[16, 16]}
                style={{
                  marginBottom: '1.5em',
                  paddingBottom: '1.5em',
                  borderBottom: '1px solid #fafafa',
                }}
              >
                <Col xs={24} md={8}>
                  {competitorData?.competitor_analysis
                    ?.site_type_distribution ? (
                    (() => {
                      const dist =
                        competitorData.competitor_analysis
                          .site_type_distribution || {};
                      const keys = Object.keys(dist || {});
                      const total = keys.reduce(
                        (sum, k) => sum + (dist[k] || 0),
                        0,
                      );
                      if (keys.length === 0 || total === 0) {
                        return (
                          <Skeleton
                            active
                            paragraph={{ rows: 3 }}
                            title={false}
                            className="pulse"
                          />
                        );
                      }
                      // 円グラフは濃い文字色（saturated）を使用
                      const palette = keys.map(
                        k => colorByType[k]?.text || '#8c8c8c',
                      );
                      let acc = 0;
                      const segments = keys.map((k, idx) => {
                        const val = dist[k] || 0;
                        const start = (acc / total) * 100;
                        acc += val;
                        const end = (acc / total) * 100;
                        const color = palette[idx % palette.length];
                        return `${color} ${start.toFixed(2)}% ${end.toFixed(2)}%`;
                      });
                      const pieStyle = {
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        background: `conic-gradient(${segments.join(',')})`,
                        boxShadow: 'inset 0 0 0 20px #fff',
                        margin: '0 auto',
                      };
                      return (
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div style={pieStyle} aria-label="サイト種別の構成" />
                          <div style={{ flex: 1 }}>
                            {keys.map((k, idx) => {
                              const color = palette[idx % palette.length];
                              return (
                                <div
                                  key={k}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 10,
                                        height: 10,
                                        backgroundColor: color,
                                        borderRadius: 2,
                                        display: 'inline-block',
                                      }}
                                    />
                                    <Text style={{ fontSize: 12 }}>
                                      {siteTypeMap[k] || k}
                                    </Text>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <Skeleton
                      active
                      paragraph={{ rows: 3 }}
                      title={false}
                      className="pulse"
                    />
                  )}
                </Col>
                <Col xs={24} md={16}>
                  {(() => {
                    const serpsObj =
                      competitorData?.serps_evaluation ||
                      competitorData?.serp_data?.serps_evaluation;
                    console.log('[SERPS] render probe:', {
                      hasTopKey: !!competitorData?.serps_evaluation,
                      hasAltKey: !!competitorData?.serp_data?.serps_evaluation,
                      readyFlag: !!dataReady?.serps_evaluation,
                      objExists: !!serpsObj,
                    });
                    if (
                      (!dataReady.serps_evaluation && !serpsObj) ||
                      !competitorData
                    ) {
                      return (
                        <Skeleton
                          active
                          paragraph={{ rows: 4 }}
                          title={false}
                          className="pulse"
                        />
                      );
                    }
                    // 1) 既存構造
                    let summary =
                      (serpsObj && serpsObj.evaluation_summary) ||
                      competitorData?.serps_evaluation?.evaluation_summary;
                    // 2) 新構造（summary）
                    if (!summary && serpsObj?.summary) {
                      summary = serpsObj.summary;
                    }
                    // 3) {'response': '```json ... ```'} 形式
                    if (
                      !summary &&
                      serpsObj?.response &&
                      typeof serpsObj.response === 'string'
                    ) {
                      try {
                        let txt = serpsObj.response.trim();
                        if (txt.startsWith('```json')) txt = txt.slice(7);
                        if (txt.endsWith('```')) txt = txt.slice(0, -3);
                        const inner = JSON.parse(txt.trim());
                        summary = inner?.summary;
                      } catch (parseErr) {
                        console.warn('[SERPS] parse failed:', parseErr);
                      }
                    }
                    console.log('[SERPS] render summary presence:', {
                      hasSummary: !!summary,
                      summaryLen: summary ? String(summary).length : 0,
                    });
                    if (summary) {
                      return (
                        <Paragraph
                          style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
                          className="serps-summary"
                        >
                          {summary}
                        </Paragraph>
                      );
                    }
                    return (
                      <Skeleton
                        active
                        paragraph={{ rows: 4 }}
                        title={false}
                        className="pulse"
                      />
                    );
                  })()}
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="stats-row">
                {/* 検索ボリューム - ユーザーキーワードの実際値 */}
                <Col xs={12} md={6}>
                  <div style={{ padding: '0 16px' }}>
                    {(() => {
                      const isCompleted =
                        analysisState?.status === 'completed' ||
                        analysisState?.status === 'completed_with_errors';

                      // 実値を解決（>0 の数値が到着したら表示。未到着/0/未定義はスケルトンまたは「データなし」）
                      const resolveVolume = () => {
                        const ka = competitorData?.keywords_analysis;
                        // 優先1: analysis_summary.main_keyword_metrics.search_volume
                        const v1 =
                          ka?.analysis_summary?.main_keyword_metrics
                            ?.search_volume;
                        if (typeof v1 === 'number' && v1 > 0) return v1;
                        // 優先2: main_keyword_metrics.search_volume（新構造）
                        const v2 = ka?.main_keyword_metrics?.search_volume;
                        if (typeof v2 === 'number' && v2 > 0) return v2;
                        // 優先3: safeSummaryWithDefaults.search_volume（フォールバック）
                        const v3 = safeSummaryWithDefaults?.search_volume;
                        if (typeof v3 === 'number' && v3 > 0) return v3;
                        return null;
                      };
                      const volumeValue = resolveVolume();

                      if (volumeValue) {
                        return (
                          <Statistic
                            title="月間検索ボリューム"
                            value={volumeValue}
                            suffix="回"
                            prefix={<LineChartOutlined />}
                            valueStyle={{ fontSize: '20px' }}
                          />
                        );
                      }

                      // 分析完了済みでデータがない場合
                      if (isCompleted) {
                        return (
                          <Statistic
                            title="月間検索ボリューム"
                            value="N/A"
                            prefix={<LineChartOutlined />}
                            valueStyle={{ fontSize: '20px', color: '#999' }}
                          />
                        );
                      }

                      // データ準備中：スケルトン表示
                      return (
                        <div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#666',
                              marginBottom: '8px',
                            }}
                          >
                            月間検索ボリューム
                          </div>
                          <Skeleton
                            active
                            paragraph={{ rows: 1 }}
                            title={false}
                            className="pulse"
                          />
                        </div>
                      );
                    })()}
                  </div>
                </Col>
                {/* 難易度 - 競合Strengthの中央値表示（実行中はsite_summary_generation完了まで、完了画面は数値存在で解除） */}
                <Col xs={12} md={6}>
                  <div style={{ padding: '0 16px' }}>
                    {(() => {
                      const comps =
                        competitorData?.competitor_analysis?.competitors || [];
                      const hasStrengthData = Array.isArray(comps)
                        ? comps.some(c => typeof c?.strength === 'number')
                        : false;
                      // 実行中はcompetitor_scoringフラグで制御。完了画面では数値があれば解除。
                      const isCompleted =
                        analysisState.status === 'completed' ||
                        analysisState.status === 'completed_with_errors';
                      return (
                        (dataReady.competitor_scoring ||
                          (isCompleted && hasStrengthData)) &&
                        competitorData
                      );
                    })() ? (
                      <div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          SEO難易度
                        </div>
                        {(() => {
                          try {
                            const comps =
                              competitorData?.competitor_analysis
                                ?.competitors || [];
                            const strengths = (
                              Array.isArray(comps) ? comps : []
                            )
                              .map(c =>
                                typeof c?.strength === 'number'
                                  ? c.strength
                                  : null,
                              )
                              .filter(
                                v => typeof v === 'number' && !Number.isNaN(v),
                              );
                            if (strengths.length === 0) {
                              return (
                                <Skeleton
                                  active
                                  paragraph={{ rows: 1 }}
                                  title={false}
                                  className="pulse"
                                />
                              );
                            }
                            const sorted = strengths.sort((a, b) => a - b);
                            const mid = Math.floor(sorted.length / 2);
                            const median =
                              sorted.length % 2 === 0
                                ? Math.round(
                                    (sorted[mid - 1] + sorted[mid]) / 2,
                                  )
                                : sorted[mid];
                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  gap: 6,
                                }}
                              >
                                <ThunderboltOutlined
                                  style={{ color: '#eb2f96', fontSize: 18 }}
                                />
                                <span
                                  style={{
                                    fontSize: 20,
                                    fontWeight: 600,
                                    color: '#eb2f96',
                                  }}
                                >
                                  {median}
                                </span>
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: '#eb2f96',
                                    marginLeft: 4,
                                  }}
                                >
                                  / 100
                                </span>
                              </div>
                            );
                          } catch (_) {
                            return (
                              <Skeleton
                                active
                                paragraph={{ rows: 1 }}
                                title={false}
                                className="pulse"
                              />
                            );
                          }
                        })()}
                      </div>
                    ) : (
                      <div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '8px',
                          }}
                        >
                          SEO難易度
                        </div>
                        <Skeleton
                          active
                          paragraph={{ rows: 1 }}
                          title={false}
                          className="pulse"
                        />
                      </div>
                    )}
                  </div>
                </Col>

                {/* 平均文字数（見出し構造と同タイミングで解除） */}
                <Col xs={12} md={6}>
                  <div style={{ padding: '0 16px' }}>
                    {(() => {
                      const comps =
                        competitorData?.competitor_analysis?.competitors || [];
                      const hasHeadings =
                        Array.isArray(comps) &&
                        comps.some(
                          c =>
                            Array.isArray(c.headings) && c.headings.length > 0,
                        );
                      const hasWordCounts =
                        Array.isArray(comps) &&
                        comps.some(
                          c =>
                            typeof (
                              c.word_count || c?.content_analysis?.word_count
                            ) === 'number' &&
                            (c.word_count || c?.content_analysis?.word_count) >
                              0,
                        );
                      return (hasHeadings || hasWordCounts) && competitorData;
                    })() ? (
                      <Statistic
                        title="中央値（文字数）"
                        value={(() => {
                          try {
                            const comps =
                              competitorData?.competitor_analysis
                                ?.competitors || [];
                            if (!Array.isArray(comps) || comps.length === 0)
                              return '-';
                            const nums = comps
                              .map(c => {
                                const fromContent =
                                  c.content_analysis?.word_count ||
                                  c.word_count ||
                                  0;
                                if (fromContent && fromContent > 0)
                                  return fromContent;
                                const headings = Array.isArray(c.headings)
                                  ? c.headings
                                  : [];
                                if (headings.length > 0) {
                                  const text = headings
                                    .map(h => h?.text || '')
                                    .join('');
                                  const estimate = Math.max(
                                    1000,
                                    Math.floor(
                                      text.replace(/\s+/g, '').length * 15,
                                    ),
                                  );
                                  return estimate;
                                }
                                return 0;
                              })
                              .filter(n => typeof n === 'number' && n > 0);
                            if (nums.length === 0) return '-';
                            const sorted = nums.sort((a, b) => a - b);
                            const mid = Math.floor(sorted.length / 2);
                            const median =
                              sorted.length % 2 === 0
                                ? Math.round(
                                    (sorted[mid - 1] + sorted[mid]) / 2,
                                  )
                                : sorted[mid];
                            return median.toLocaleString();
                          } catch (_) {
                            return '-';
                          }
                        })()}
                        suffix="文字"
                        valueStyle={{ fontSize: '20px' }}
                      />
                    ) : (
                      <div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          平均文字数
                        </div>
                        <Skeleton
                          active
                          paragraph={{ rows: 1 }}
                          title={false}
                          className="pulse"
                        />
                      </div>
                    )}
                  </div>
                </Col>
                {/* AI Overview - 実際の結果 */}
                <Col xs={12} md={6}>
                  <div style={{ padding: '0 16px' }}>
                    {dataReady.aio_analysis ||
                    (() => {
                      const effAiOverview =
                        competitorData?.aio_analysis?.ai_overview ||
                        competitorData?.serp_data?.ai_overview ||
                        competitorData?.summary?.ai_overview;
                      const has = !!(
                        effAiOverview &&
                        ((Array.isArray(effAiOverview.elements) &&
                          effAiOverview.elements.length > 0) ||
                          effAiOverview.has_ai_overview === true)
                      );
                      return has;
                    })() ? (
                      <div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          AI Overviews
                        </div>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>
                          {(() => {
                            // 統計カードのAIO表示条件を『準備中はスケルトン／完了で空は「なし」／要素ありは「あり」』に統一
                            const effAiOverview =
                              competitorData?.aio_analysis?.ai_overview ||
                              competitorData?.serp_data?.ai_overview ||
                              competitorData?.summary?.ai_overview;
                            // 準備中はスケルトン。準備完了 or 実データ検出時にのみ表示/なしを切り替え
                            const isReady =
                              !!dataReady?.aio_analysis ||
                              !!(
                                effAiOverview &&
                                ((Array.isArray(effAiOverview.elements) &&
                                  effAiOverview.elements.length > 0) ||
                                  effAiOverview.has_ai_overview === true)
                              );
                            if (!isReady) {
                              return (
                                <Skeleton
                                  active
                                  paragraph={{ rows: 1 }}
                                  title={false}
                                  className="pulse"
                                />
                              );
                            }
                            // Fallback: raw_serp_data からAIOの有無を推定
                            let hasAIO = !!(
                              effAiOverview &&
                              Array.isArray(effAiOverview.elements) &&
                              effAiOverview.elements.length > 0
                            );
                            if (!hasAIO) {
                              try {
                                const raw =
                                  competitorData?.serp_data?.raw_serp_data;
                                const items =
                                  raw?.tasks?.[0]?.result?.[0]?.items || [];
                                const aiItem = items.find(
                                  it => it && it.type === 'ai_overview',
                                );
                                const subs = Array.isArray(aiItem?.items)
                                  ? aiItem.items
                                  : [];
                                hasAIO = subs.some(
                                  si => si?.text || si?.markdown,
                                );
                              } catch (e) {
                                console.warn(
                                  '[Stats-AIO] raw fallback parse error:',
                                  e,
                                );
                              }
                            }
                            return hasAIO ? (
                              <Tag
                                color="green"
                                style={{ fontSize: 14, padding: '4px 12px' }}
                              >
                                <CheckCircleOutlined /> あり
                              </Tag>
                            ) : (
                              <Tag
                                color="default"
                                style={{ fontSize: 14, padding: '4px 12px' }}
                              >
                                なし
                              </Tag>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '8px',
                          }}
                        >
                          AI Overview
                        </div>
                        <Skeleton
                          active
                          paragraph={{ rows: 1 }}
                          title={false}
                          className="pulse"
                        />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
          {/* 検索意図分析 */}
          <Col xs={24}>
            <Card
              className="v2-card"
              title={<>検索意図の深層分析</>}
              hoverable={false}
            >
              {(() => {
                // 🔍 デバッグ: competitorData全体を確認
                console.log(
                  '[検索意図デバッグ] competitorData exists:',
                  !!competitorData,
                );
                console.log(
                  '[検索意図デバッグ] competitorData keys:',
                  competitorData ? Object.keys(competitorData) : 'null',
                );
                console.log(
                  '[検索意図デバッグ] competitorData.aio_analysis exists:',
                  !!competitorData?.aio_analysis,
                );
                console.log(
                  '[検索意図デバッグ] competitorData.comprehensive_report exists:',
                  !!competitorData?.comprehensive_report,
                );
                console.log(
                  '[検索意図デバッグ] competitorData.analysis_summary exists:',
                  !!competitorData?.analysis_summary,
                );

                // 検索意図分析データを取得
                const searchIntentSummary =
                  competitorData?.analysis_summary?.search_intent_summary;
                // 優先: トップレベル → aio_analysis内 → comprehensive_report内
                const rawSearchIntent =
                  competitorData?.search_intent_analysis ||
                  competitorData?.aio_analysis?.search_intent_analysis ||
                  competitorData?.comprehensive_report?.search_intent_analysis;

                console.log(
                  '[検索意図デバッグ] rawSearchIntent:',
                  rawSearchIntent,
                );

                // データをパースして構造化
                let parsedAnalysis = null;
                try {
                  if (!rawSearchIntent) {
                    parsedAnalysis = null;
                  } else if (typeof rawSearchIntent === 'string') {
                    // 旧構造: 文字列JSON with response
                    const parsed = JSON.parse(rawSearchIntent);
                    let intentData = parsed;
                    if (
                      parsed?.response &&
                      typeof parsed.response === 'string'
                    ) {
                      let responseData = parsed.response.trim();
                      responseData = responseData
                        .replace(/^```json\s*/, '')
                        .replace(/\s*```$/, '')
                        .trim();
                      try {
                        intentData = JSON.parse(responseData);
                      } catch (_) {
                        intentData = {};
                      }
                    }
                    if (intentData && typeof intentData === 'object') {
                      parsedAnalysis =
                        intentData.search_intent_analysis || intentData;
                    }
                  } else if (typeof rawSearchIntent === 'object') {
                    // 新構造: オブジェクト/またはラップされた構造
                    let intentData = rawSearchIntent;
                    // {'response': '{...}'} 形式にも対応（コードフェンスが無くてもパース）
                    if (
                      typeof intentData.response === 'string' &&
                      intentData.response.trim().length > 0
                    ) {
                      let responseData = intentData.response.trim();
                      responseData = responseData
                        .replace(/^```json\s*/, '')
                        .replace(/\s*```$/, '')
                        .trim();
                      try {
                        const parsed = JSON.parse(responseData);
                        if (parsed && typeof parsed === 'object') {
                          intentData = parsed;
                        }
                      } catch (parseErr) {
                        console.warn(
                          '検索意図responseのJSONパース失敗:',
                          parseErr,
                        );
                      }
                    }
                    parsedAnalysis =
                      intentData.search_intent_analysis || intentData;
                  }
                } catch (e) {
                  console.warn('検索意図分析データのパースに失敗:', e);
                }

                // searchIntentSummaryまたはparsedAnalysisがあれば表示
                if (searchIntentSummary || parsedAnalysis) {
                  const intentNew =
                    competitorData?.search_intent_analysis ||
                    parsedAnalysis ||
                    null;
                  const summaryText =
                    intentNew?.summary ||
                    parsedAnalysis?.summary ||
                    parsedAnalysis?.analysis_summary ||
                    (typeof searchIntentSummary === 'string'
                      ? searchIntentSummary
                      : '') ||
                    '';
                  const intentTable = Array.isArray(intentNew?.intent_table)
                    ? intentNew.intent_table
                    : Array.isArray(parsedAnalysis?.intent_table)
                      ? parsedAnalysis.intent_table
                      : [];
                  const toggles =
                    intentNew?.toggles || parsedAnalysis?.toggles || {};
                  // 新レイアウトに従い、左：サマリー / 右：テーブル+トグルのみを表示
                  const hasTable =
                    Array.isArray(intentTable) && intentTable.length > 0;
                  const toggleKeys = [
                    'core_needs',
                    'sub_needs',
                    'pain_points',
                    'motivators',
                    'personas',
                  ];
                  const hasToggles = toggleKeys.some(
                    k => Array.isArray(toggles?.[k]) && toggles[k].length > 0,
                  );
                  if (summaryText || hasTable || hasToggles) {
                    return (
                      <div className="fade-in search-intent-analysis">
                        <Row gutter={[16, 16]}>
                          <Col xs={24} md={12}>
                            <div className="search-intent-card">
                              <div>
                                <Text strong className="search-intent-title">
                                  深掘り考察（サマリー）
                                </Text>
                              </div>
                              {summaryText ? (
                                <div
                                  className="search-intent-text"
                                  style={{
                                    marginTop: 8,
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.8,
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatBoldMarkdown(summaryText),
                                  }}
                                />
                              ) : (
                                <Skeleton
                                  active
                                  paragraph={{ rows: 3 }}
                                  title={false}
                                  className="pulse"
                                />
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={12}>
                            {hasTable ? (
                              <Table
                                size="small"
                                pagination={false}
                                dataSource={intentTable.map((it, i) => ({
                                  no: i + 1,
                                  text:
                                    it?.question || it?.q || it?.intent || '',
                                  key: `intent-${i}`,
                                }))}
                                columns={[
                                  {
                                    title: 'No',
                                    dataIndex: 'no',
                                    key: 'no',
                                    width: 48,
                                  },
                                  {
                                    title: '検索意図',
                                    dataIndex: 'text',
                                    key: 'text',
                                  },
                                ]}
                              />
                            ) : (
                              <Skeleton
                                active
                                paragraph={{ rows: 5 }}
                                title={false}
                                className="pulse"
                              />
                            )}
                            <div className="" style={{ marginTop: 12 }}>
                              <Collapse ghost defaultActiveKey={['core_needs']}>
                                {[
                                  { key: 'core_needs', label: 'コアニーズ' },
                                  { key: 'sub_needs', label: 'サブニーズ' },
                                  { key: 'pain_points', label: '検索者の課題' },
                                  { key: 'motivators', label: '動機づけ要因' },
                                  { key: 'personas', label: '想定検索者像' },
                                ].map(cat => (
                                  <Collapse.Panel
                                    header={cat.label}
                                    key={cat.key}
                                    className="poa-collapse intent-toggle-panel"
                                  >
                                    <ul
                                      className="intent-toggle-items"
                                      style={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {(Array.isArray(
                                        parsedAnalysis?.toggles?.[cat.key],
                                      )
                                        ? parsedAnalysis.toggles[cat.key]
                                        : []
                                      ).map((val, i) => (
                                        <li
                                          key={`${cat.key}-${i}`}
                                          style={{
                                            padding: '2px 0',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          <Text
                                            style={{
                                              fontSize: 13,
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word',
                                            }}
                                          >
                                            {val}
                                          </Text>
                                        </li>
                                      ))}
                                    </ul>
                                  </Collapse.Panel>
                                ))}
                              </Collapse>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    );
                  }

                  return (
                    <div className="fade-in search-intent-analysis">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <div className="search-intent-card">
                            <div>
                              <Text strong className="search-intent-title">
                                深掘り考察（サマリー）
                              </Text>
                            </div>
                            {parsedAnalysis?.summary ? (
                              <div
                                className="search-intent-text"
                                style={{
                                  marginTop: 8,
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: 1.8,
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatBoldMarkdown(
                                    parsedAnalysis.summary,
                                  ),
                                }}
                              />
                            ) : (
                              <Skeleton
                                active
                                paragraph={{ rows: 3 }}
                                title={false}
                                className="pulse"
                              />
                            )}
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          {hasTable ? (
                            <Table
                              size="small"
                              pagination={false}
                              dataSource={intentTable.map((it, i) => ({
                                no: i + 1,
                                text: it?.question || it?.q || it?.intent || '',
                                key: `intent-${i}`,
                              }))}
                              columns={[
                                {
                                  title: 'No',
                                  dataIndex: 'no',
                                  key: 'no',
                                  width: 48,
                                },
                                {
                                  title: '検索意図',
                                  dataIndex: 'text',
                                  key: 'text',
                                },
                              ]}
                            />
                          ) : (
                            <Skeleton
                              active
                              paragraph={{ rows: 5 }}
                              title={false}
                              className="pulse"
                            />
                          )}
                          <div className="" style={{ marginTop: 12 }}>
                            <Collapse ghost defaultActiveKey={['core_needs']}>
                              {[
                                { key: 'core_needs', label: 'コアニーズ' },
                                { key: 'sub_needs', label: 'サブニーズ' },
                                { key: 'pain_points', label: '検索者の課題' },
                                { key: 'motivators', label: '動機づけ要因' },
                                { key: 'personas', label: '想定検索者像' },
                              ].map(cat => (
                                <Collapse.Panel
                                  header={cat.label}
                                  key={cat.key}
                                  className="poa-collapse intent-toggle-panel"
                                >
                                  <ul
                                    className="intent-toggle-items"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                  >
                                    {(Array.isArray(
                                      parsedAnalysis?.toggles?.[cat.key],
                                    )
                                      ? parsedAnalysis.toggles[cat.key]
                                      : []
                                    ).map((val, i) => (
                                      <li
                                        key={`${cat.key}-${i}`}
                                        style={{
                                          padding: '2px 0',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        <Text
                                          style={{
                                            fontSize: 13,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          {val}
                                        </Text>
                                      </li>
                                    ))}
                                  </ul>
                                </Collapse.Panel>
                              ))}
                            </Collapse>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                }

                // データが読み込まれていない場合
                return (
                  <Skeleton
                    active
                    paragraph={{ rows: 4 }}
                    title={false}
                    className="pulse"
                  />
                );
              })()}
            </Card>
          </Col>
          {/* トピック構造マップ（検索意図カードの外・カード外枠なし） */}
          <Col xs={24} className="tour-topic-sunburst-section">
            {(() => {
              const hasTopicRows = !!(
                competitorData?.topic_analysis?.site_topic_analysis
                  ?.heatmap_data?.rows &&
                competitorData.topic_analysis.site_topic_analysis.heatmap_data
                  .rows.length > 0
              );
              const hasIntegratedRows = !!(
                competitorData?.topic_analysis?.integrated_heatmap
                  ?.formatted_heatmaps?.topic_heatmap?.rows &&
                competitorData.topic_analysis.integrated_heatmap
                  .formatted_heatmaps.topic_heatmap.rows.length > 0
              );
              const topicReady = hasTopicRows || hasIntegratedRows;
              const topicCount = hasTopicRows
                ? competitorData.topic_analysis.site_topic_analysis.heatmap_data
                    .rows.length
                : hasIntegratedRows
                  ? competitorData.topic_analysis.integrated_heatmap
                      .formatted_heatmaps.topic_heatmap.rows.length
                  : 0;
              const dynamicHeight = topicReady
                ? Math.max(280, Math.round((topicCount / 30) * 700))
                : 280;

              return (
                <TopicSunburstMap
                  competitorData={competitorData}
                  loading={!topicReady}
                  height={dynamicHeight}
                  showStatistics={false}
                  enableAnimation={true}
                  onTopicClick={topicData => {
                    console.log('🔍 [TopicMap] Topic clicked:', topicData);
                  }}
                  isCompleted={
                    analysisState.status === 'completed' ||
                    analysisState.status === 'completed_with_errors'
                  }
                />
              );
            })()}
          </Col>
          {/* 関連キーワード・LSIキーワード - フル幅テーブル表示 */}
          <Col xs={24} md={12}>
            <Card
              className="v2-card"
              title={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <span>関連キーワード（Googleサジェスト）</span>
                  {(() => {
                    const relatedKeywordsObj =
                      competitorData?.keywords_analysis?.related_keywords;
                    const relatedData =
                      relatedKeywordsObj?.related_keywords ||
                      relatedKeywordsObj?.keywords ||
                      relatedKeywordsObj?.suggest_keywords ||
                      (Array.isArray(relatedKeywordsObj)
                        ? relatedKeywordsObj
                        : null);

                    const hasData =
                      Array.isArray(relatedData) && relatedData.length > 0;

                    if (hasData) {
                      return (
                        <Tooltip title="キーワードをコピー">
                          <CopyOutlined
                            onClick={e => {
                              e.stopPropagation();
                              const keywords = relatedData
                                .map(kw => kw.keyword || '')
                                .filter(Boolean);
                              const text = keywords.join('\n');
                              navigator.clipboard.writeText(text);
                              message.success('キーワードをコピーしました');
                            }}
                            style={{
                              color: '#1890ff',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                          />
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                </div>
              }
              hoverable={false}
              bodyStyle={{ padding: '12px' }}
              style={{
                height: 'auto',
                minHeight: '200px',
                maxHeight: '430px',
              }}
            >
              <div
                style={{
                  overflow: 'auto',
                  maxHeight: '350px',
                }}
              >
                {(() => {
                  // 🔧 [REALTIME-FIX] 複数のデータパスを試行してフォールバック
                  const relatedKeywordsObj =
                    competitorData?.keywords_analysis?.related_keywords;
                  const relatedData =
                    relatedKeywordsObj?.related_keywords || // 期待される構造
                    relatedKeywordsObj?.keywords || // 代替構造1
                    relatedKeywordsObj?.suggest_keywords || // 代替構造2
                    (Array.isArray(relatedKeywordsObj)
                      ? relatedKeywordsObj
                      : null); // 配列の場合

                  const hasData =
                    Array.isArray(relatedData) && relatedData.length > 0;

                  if (hasData) {
                    return (
                      <Table
                        dataSource={(() => {
                          // 🔧 [REALTIME-FIX] 修正されたデータパス解決ロジックを使用
                          const relatedKeywordsObj =
                            competitorData?.keywords_analysis?.related_keywords;
                          const relatedKeywords =
                            relatedKeywordsObj?.related_keywords || // 期待される構造
                            relatedKeywordsObj?.keywords || // 代替構造1
                            relatedKeywordsObj?.suggest_keywords || // 代替構造2
                            (Array.isArray(relatedKeywordsObj)
                              ? relatedKeywordsObj
                              : []); // 配列の場合

                          if (!Array.isArray(relatedKeywords)) {
                            console.warn(
                              '🔧 [REALTIME-FIX] relatedKeywords is not an array:',
                              relatedKeywords,
                            );
                            return [];
                          }

                          return relatedKeywords.map((kw, index) => ({
                            key: index,
                            keyword: kw.keyword || '',
                            volume: kw.search_volume || 0,
                            difficulty: kw.keyword_difficulty || 0,
                            cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0,
                          }));
                        })()}
                        columns={[
                          {
                            title: 'キーワード',
                            dataIndex: 'keyword',
                            key: 'keyword',
                            width: 220,
                            ellipsis: true,
                            render: text => (
                              <Space size="small">
                                <Button
                                  type="text"
                                  size="small"
                                  loading={favoriteKeywordsLoading[text]}
                                  icon={
                                    !favoriteKeywordsLoading[text] &&
                                    (favoriteKeywords.includes(text) ? (
                                      <StarFilled />
                                    ) : (
                                      <StarOutlined />
                                    ))
                                  }
                                  onClick={() => toggleFavoriteKeyword(text)}
                                  disabled={isSharedView}
                                  style={{
                                    color: favoriteKeywords.includes(text)
                                      ? '#faad14'
                                      : undefined,
                                    minWidth: '24px',
                                    padding: '0 4px',
                                  }}
                                />
                                <Text
                                  style={{ fontSize: '13px', fontWeight: 500 }}
                                  title={text}
                                >
                                  {text}
                                </Text>
                              </Space>
                            ),
                          },
                          {
                            title: '検索数',
                            dataIndex: 'volume',
                            key: 'volume',
                            width: 76,
                            align: 'right',
                            render: value => (
                              <Text style={{ fontSize: '13px' }}>
                                {value.toLocaleString()}
                              </Text>
                            ),
                          },
                          {
                            title: 'CPC',
                            dataIndex: 'cpc',
                            key: 'cpc',
                            width: 60,
                            align: 'right',
                            render: value => (
                              <Text style={{ fontSize: '13px' }}>¥{value}</Text>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        scroll={{ y: false }}
                        style={{ marginBottom: 0 }}
                      />
                    );
                  } else if (dataReady.related_keywords) {
                    // データ準備完了だがデータがない場合
                    return (
                      <div style={{ padding: '12px' }}>
                        <Text type="secondary">データがありません</Text>
                      </div>
                    );
                  } else {
                    // データ準備中はスケルトン表示
                    return (
                      <div style={{ padding: '20px' }}>
                        <Skeleton
                          active
                          paragraph={{ rows: 6 }}
                          title={false}
                          className="pulse"
                        />
                      </div>
                    );
                  }
                })()}
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              className="v2-card"
              title={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <span>LSIキーワード</span>
                  {(() => {
                    const lsiKeywordsObj =
                      competitorData?.keywords_analysis?.lsi_keywords;
                    const lsiData =
                      lsiKeywordsObj?.lsi_candidates ||
                      lsiKeywordsObj?.keywords ||
                      lsiKeywordsObj?.lsi_keywords ||
                      (Array.isArray(lsiKeywordsObj) ? lsiKeywordsObj : null);

                    const hasData =
                      Array.isArray(lsiData) && lsiData.length > 0;

                    if (hasData) {
                      return (
                        <Tooltip title="キーワードをコピー">
                          <CopyOutlined
                            onClick={e => {
                              e.stopPropagation();
                              const keywords = lsiData
                                .map(kw => kw.keyword || '')
                                .filter(Boolean);
                              const text = keywords.join('\n');
                              navigator.clipboard.writeText(text);
                              message.success('キーワードをコピーしました');
                            }}
                            style={{
                              color: '#1890ff',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                          />
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                </div>
              }
              hoverable={false}
              bodyStyle={{ padding: '12px' }}
              style={{
                height: 'auto',
                minHeight: '200px',
                maxHeight: '430px',
              }}
            >
              <div
                style={{
                  overflow: 'auto',
                  maxHeight: '350px',
                }}
              >
                {(() => {
                  // 🔧 [REALTIME-FIX] 複数のデータパスを試行してフォールバック
                  const lsiKeywordsObj =
                    competitorData?.keywords_analysis?.lsi_keywords;
                  const lsiData =
                    lsiKeywordsObj?.lsi_candidates || // 期待される構造
                    lsiKeywordsObj?.keywords || // 代替構造1
                    lsiKeywordsObj?.lsi_keywords || // 代替構造2
                    (Array.isArray(lsiKeywordsObj) ? lsiKeywordsObj : null); // 配列の場合

                  const hasData = Array.isArray(lsiData) && lsiData.length > 0;

                  if (hasData) {
                    return (
                      <Table
                        dataSource={(() => {
                          // 🔧 [REALTIME-FIX] 修正されたデータパス解決ロジックを使用
                          const lsiKeywordsObj =
                            competitorData?.keywords_analysis?.lsi_keywords;
                          const lsiKeywords =
                            lsiKeywordsObj?.lsi_candidates || // 期待される構造
                            lsiKeywordsObj?.keywords || // 代替構造1
                            lsiKeywordsObj?.lsi_keywords || // 代替構造2
                            (Array.isArray(lsiKeywordsObj)
                              ? lsiKeywordsObj
                              : []); // 配列の場合

                          if (!Array.isArray(lsiKeywords)) {
                            console.warn(
                              '🔧 [REALTIME-FIX] lsiKeywords is not an array:',
                              lsiKeywords,
                            );
                            return [];
                          }

                          return lsiKeywords.map((kw, index) => ({
                            key: index,
                            keyword: kw.keyword || '',
                            volume: kw.search_volume || 0,
                            difficulty: kw.keyword_difficulty || 0,
                            cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0,
                            lsi_level: kw.lsi_level || 1,
                            parent_keyword: kw.parent_keyword || '',
                          }));
                        })()}
                        columns={[
                          {
                            title: 'キーワード',
                            dataIndex: 'keyword',
                            key: 'keyword',
                            width: 220,
                            ellipsis: true,
                            render: (text, record) => (
                              <Space size="small">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={
                                    favoriteKeywords.includes(text) ? (
                                      <StarFilled />
                                    ) : (
                                      <StarOutlined />
                                    )
                                  }
                                  onClick={() => toggleFavoriteKeyword(text)}
                                  style={{
                                    color: favoriteKeywords.includes(text)
                                      ? '#faad14'
                                      : undefined,
                                    minWidth: '24px',
                                    padding: '0 4px',
                                  }}
                                />

                                <Text
                                  style={{ fontSize: '13px', fontWeight: 500 }}
                                  title={text}
                                >
                                  {text}
                                </Text>
                              </Space>
                            ),
                          },
                          {
                            title: '検索数',
                            dataIndex: 'volume',
                            key: 'volume',
                            width: 76,
                            align: 'right',
                            render: value => (
                              <Text style={{ fontSize: '13px' }}>
                                {value.toLocaleString()}
                              </Text>
                            ),
                          },
                          {
                            title: 'CPC',
                            dataIndex: 'cpc',
                            key: 'cpc',
                            width: 60,
                            align: 'right',
                            render: value => (
                              <Text style={{ fontSize: '13px' }}>¥{value}</Text>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        scroll={{ y: false }}
                        style={{ marginBottom: 0 }}
                      />
                    );
                  } else if (dataReady.lsi_keywords) {
                    // データ準備完了だがデータがない場合
                    return (
                      <div style={{ padding: '12px' }}>
                        <Text type="secondary">データがありません</Text>
                      </div>
                    );
                  } else {
                    // データ準備中はスケルトン表示
                    return (
                      <div style={{ padding: '20px' }}>
                        <Skeleton
                          active
                          paragraph={{ rows: 6 }}
                          title={false}
                          className="pulse"
                        />
                      </div>
                    );
                  }
                })()}
              </div>
            </Card>
          </Col>
          {/* よくある質問とPeople Also Searchの2カラム表示 */}
          <>
            {/* よくある質問 */}
            <Col xs={24} md={12}>
              <Card
                className="v2-card"
                title="よくある質問（People Also Ask）"
                hoverable={false}
                bodyStyle={{ padding: '12px' }}
              >
                {dataReady.serp_data &&
                (
                  competitorData?.serp_data?.people_also_ask ||
                  safeSummaryWithDefaults.people_also_ask ||
                  []
                ).length > 0 ? (
                  <Collapse ghost>
                    {(() => {
                      const rawPaa =
                        competitorData?.serp_data?.people_also_ask ||
                        safeSummaryWithDefaults.people_also_ask ||
                        [];
                      const paaItems = rawPaa.filter(item => {
                        const hasPrimary = !!(
                          item &&
                          typeof item.answer === 'string' &&
                          item.answer.trim().length > 0
                        );
                        const hasAnswersArray =
                          Array.isArray(item?.answers) &&
                          item.answers.some(
                            a => (a?.description || '').trim().length > 0,
                          );
                        return hasPrimary || hasAnswersArray;
                      });
                      return paaItems.map((qa, index) => (
                        <Collapse.Panel
                          header={qa.question}
                          key={index}
                          className="poa-collapse"
                          style={{
                            borderBottom:
                              index < paaItems.length - 1
                                ? '1px solid #f0f0f0'
                                : 'none',
                          }}
                        >
                          {(() => {
                            const primaryText =
                              qa.answer && qa.answer.trim().length > 0
                                ? qa.answer
                                : (Array.isArray(qa.answers) &&
                                    qa.answers.find(
                                      a =>
                                        (a?.description || '').trim().length >
                                        0,
                                    )?.description) ||
                                  '';
                            if (!primaryText) return null;
                            return (
                              <Paragraph style={{ marginBottom: 8 }}>
                                {primaryText}
                              </Paragraph>
                            );
                          })()}
                          {(() => {
                            const links = [];
                            if (Array.isArray(qa.answers)) {
                              const seen = new Set();
                              qa.answers.forEach(ans => {
                                const domain = (ans?.domain || '').trim();
                                const url = (ans?.url || '').trim();
                                if (!domain && !url) return;
                                const key = domain || url;
                                if (seen.has(key)) return;
                                seen.add(key);
                                links.push({ domain: domain || url, url: url });
                              });
                            }
                            if (links.length === 0 && qa.source_url) {
                              links.push({
                                domain:
                                  qa.source_domain ||
                                  qa.source_title ||
                                  qa.source_url,
                                url: qa.source_url,
                              });
                            }
                            if (links.length === 0) return null;
                            return (
                              <div style={{ marginTop: 4 }}>
                                {links.map((l, i) => (
                                  <a
                                    key={i}
                                    className="source-pill"
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 6 }}
                                  >
                                    <Text
                                      type="secondary"
                                      style={{
                                        fontSize: 12,
                                        display: 'inline-block',
                                        marginLeft: 8,
                                        whiteSpace: 'normal',
                                        overflow: 'visible',
                                      }}
                                    >
                                      {l.domain}
                                    </Text>
                                  </a>
                                ))}
                              </div>
                            );
                          })()}
                        </Collapse.Panel>
                      ));
                    })()}
                  </Collapse>
                ) : dataReady.serp_data ? (
                  analysisState.status === 'completed' ? (
                    <Text type="secondary">データがありません</Text>
                  ) : (
                    <Skeleton
                      active
                      paragraph={{ rows: 5 }}
                      title={false}
                      className="pulse"
                    />
                  )
                ) : (
                  <Skeleton
                    active
                    paragraph={{ rows: 5 }}
                    title={false}
                    className="pulse"
                  />
                )}
              </Card>
            </Col>

            {/* 他の人はこちらも検索 */}
            {dataReady.serp_data &&
              (
                competitorData?.serp_data?.people_also_search ||
                competitorData?.comprehensive_report?.site_summaries
                  ?.site_summaries?.[0]?.people_also_search ||
                safeSummaryWithDefaults.people_also_search
              )?.length > 0 && (
                <Col xs={24} md={12}>
                  <Card
                    className="v2-card"
                    title="他の人はこちらも検索"
                    hoverable={false}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <Space wrap size="middle">
                      {(
                        competitorData?.serp_data?.people_also_search ||
                        competitorData?.comprehensive_report?.site_summaries
                          ?.site_summaries?.[0]?.people_also_search ||
                        safeSummaryWithDefaults.people_also_search ||
                        []
                      ).map((search, index) => (
                        <Tag
                          key={index}
                          style={{ padding: '4px 12px', cursor: 'pointer' }}
                          onClick={() => toggleFavoriteKeyword(search)}
                        >
                          {favoriteKeywords.includes(search) && (
                            <StarFilled
                              style={{ color: '#faad14', marginRight: 4 }}
                            />
                          )}
                          {search}
                        </Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
          </>
        </Row>
      );
    } catch (error) {
      console.error('Error in renderSummaryTab:', error);
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Alert
            message="データの表示中にエラーが発生しました"
            description="しばらくしてから再度お試しください"
            type="error"
            showIcon
          />
        </div>
      );
    }
  };
  // 競合サイト分析タブ
  const renderCompetitorTab = () => {
    const competitors = competitorData?.competitor_analysis?.competitors || [];

    // データが存在しない場合でもタブは表示し、中身をスケルトンにする
    if (competitors.length === 0) {
      return (
        <div>
          {/* 表示切り替えボタン（タブ相当）は常に表示 */}
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Radio.Group
              value={headingView}
              onChange={e => setHeadingView(e.target.value)}
            >
              {(() => {
                const isCompleted = analysisState?.status === 'completed';
                const hasHeadings =
                  Array.isArray(
                    competitorData?.competitor_analysis?.competitors,
                  ) &&
                  competitorData.competitor_analysis.competitors.some(
                    c => Array.isArray(c.headings) && c.headings.length > 0,
                  );
                const topicHeatmapRows =
                  competitorData?.topic_analysis?.integrated_heatmap
                    ?.formatted_heatmaps?.topic_heatmap?.rows ||
                  competitorData?.integrated_heatmap?.formatted_heatmaps
                    ?.topic_heatmap?.rows ||
                  competitorData?.topic_analysis?.site_topic_analysis
                    ?.heatmap_data?.rows;
                const termsRows =
                  competitorData?.topic_analysis?.integrated_heatmap
                    ?.formatted_heatmaps?.important_terms_heatmap?.rows ||
                  competitorData?.integrated_heatmap?.formatted_heatmaps
                    ?.important_terms_heatmap?.rows ||
                  competitorData?.topic_analysis?.terms_evaluation?.heatmap_data
                    ?.rows;
                const relatedRows =
                  competitorData?.topic_analysis?.integrated_heatmap
                    ?.formatted_heatmaps?.related_keywords_heatmap?.rows ||
                  competitorData?.integrated_heatmap?.formatted_heatmaps
                    ?.related_keywords_heatmap?.rows ||
                  competitorData?.keywords_analysis?.heatmap_data?.rows ||
                  competitorData?.keywords_analysis?.heatmap_data
                    ?.related_keywords?.rows;
                const lsiRows =
                  competitorData?.topic_analysis?.integrated_heatmap
                    ?.formatted_heatmaps?.lsi_keywords_heatmap?.rows ||
                  competitorData?.integrated_heatmap?.formatted_heatmaps
                    ?.lsi_keywords_heatmap?.rows ||
                  competitorData?.keywords_analysis?.lsi_heatmap_data?.rows ||
                  competitorData?.keywords_analysis?.heatmap_data?.lsi_keywords
                    ?.rows;

                const isEnabled = rows =>
                  !isCompleted ? true : Array.isArray(rows) && rows.length > 0;

                const btn = (value, label, enabled) => (
                  <Tooltip title={enabled ? undefined : 'データがありません'}>
                    <Radio.Button value={value} disabled={!enabled}>
                      {label}
                    </Radio.Button>
                  </Tooltip>
                );

                // 追加: 上位サイトタブは常に有効
                return (
                  <>
                    {btn('top-sites', '上位サイト', true)}
                    {btn('headings', '見出し構造', !!hasHeadings)}
                    {btn('topics', 'トピック', isEnabled(topicHeatmapRows))}
                    {btn('terms', '重要語句', isEnabled(termsRows))}
                    {btn('related', '関連キーワード', isEnabled(relatedRows))}
                    {btn('lsi', 'LSIキーワード', isEnabled(lsiRows))}
                  </>
                );
              })()}
            </Radio.Group>
          </div>

          {/* タブ中身はスケルトン表示 */}
          {headingView === 'headings' ? (
            <div style={{ padding: '16px' }}>
              <Skeleton
                active
                paragraph={{ rows: 8 }}
                title={false}
                className="pulse"
              />
            </div>
          ) : (
            <div style={{ padding: '16px' }}>
              <SkeletonTable flat rows={8} />
            </div>
          )}
        </div>
      );
    }
    // ヒートマップデータの準備（新構造・統合ヒートマップ対応）
    const prepareHeatmapData = type => {
      // 新しい構造かどうかを確認（現在のデータ構造に基づく）
      const isNewStructure =
        competitorData?.analysis_summary ||
        (competitorData?.keywords_data &&
          competitorData?.competitor_analyses) ||
        competitorData?.competitor_analysis?.competitors; // 現在の構造を追加

      if (isNewStructure) {
        // 最適化されたヒートマップデータを取得
        const heatmapData = competitorData?.heatmap_data || {};
        const competitors = competitorData?.competitor_analyses || [];

        if (type === 'terms') {
          // 重要語句分析結果を表示（新構造・最適化版）

          const importantTermsAnalysis =
            competitorData?.important_terms_analysis;

          // 最適化されたヒートマップデータを確認
          if (importantTermsAnalysis?.heatmap_data?.rows) {
            const heatmapData = importantTermsAnalysis.heatmap_data;
            const heatmapRows = heatmapData.rows;

            return heatmapRows.map((row, index) => {
              const resultRow = {
                keyword: row.term || row.label || '',
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                uniqueKey: `terms-${index}-${row.term || row.label || ''}`,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  const score = val.normalized_score || val.strength || 0.1;
                  const normalizedScore = Math.max(0.1, Math.min(10.0, score));
                  resultRow[siteKey] = normalizedScore;
                });
              }

              return resultRow;
            });
          }

          // 【修正】新構造での重要語句データ取得（統合データ対応）
          const termsEvaluationData =
            competitorData?.topic_analysis?.terms_evaluation;
          const termsHeatmapFromTopicAnalysis =
            termsEvaluationData?.heatmap_data;

          // integrated_heatmapからも取得を試行
          const integratedHeatmapData =
            competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.important_terms_heatmap;

          // 統合データ優先で取得
          const finalTermsHeatmap =
            integratedHeatmapData || termsHeatmapFromTopicAnalysis;

          if (finalTermsHeatmap?.rows) {
            return finalTermsHeatmap.rows.map((row, index) => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                uniqueKey: `terms-${index}-${row.term}`,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  const score =
                    val.normalized_score || val.term_coverage_score || 0.1;
                  const normalizedScore = Math.max(0.1, Math.min(10.0, score));
                  resultRow[siteKey] = normalizedScore;
                });
              }

              return resultRow;
            });
          }

          // integrated_heatmapからの取得も試行
          const integratedHeatmap =
            competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.important_terms_heatmap;
          if (integratedHeatmap?.rows) {
            return integratedHeatmap.rows.map((row, index) => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                uniqueKey: `terms-${index}-${row.term}`,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  const score =
                    val.normalized_score || val.term_coverage_score || 0.1;
                  const normalizedScore = Math.max(0.1, Math.min(10.0, score));
                  resultRow[siteKey] = normalizedScore;
                });
              }

              return resultRow;
            });
          }

          // フォールバック：通常の重要語句リスト（存在する場合のみ）。存在しなければ空配列
          const importantTerms = importantTermsAnalysis?.terms || [];

          if (importantTerms.length > 0) {
            return importantTerms.map((term, index) => {
              const resultRow = {
                keyword: term.term || term.normalized_term || term,
                term_id: term.term_id,
                importance: term.importance,
                average_score: term.average_score || 0,
                category: term.importance,
                isAnalyzed: true,
                uniqueKey: `terms-${index}-${term.term || term.normalized_term || term}`,
              };

              competitors.forEach((c, idx) => {
                const freq = term.frequency || 0;
                // 頻度を0.1-10.0の範囲に正規化
                const normalizedFreq = Math.min(Math.max(freq / 10, 0.1), 10.0);
                resultRow[`site${idx + 1}`] = normalizedFreq;
              });

              return resultRow;
            });
          }

          return [];
        } else if (type === 'related') {
          console.log('🔎 [prepareHeatmapData][related] paths', {
            fmtd: !!competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.related_keywords_heatmap,
            fmtd_rows:
              competitorData?.topic_analysis?.integrated_heatmap
                ?.formatted_heatmaps?.related_keywords_heatmap?.rows?.length ||
              0,
            top_fmtd:
              !!competitorData?.integrated_heatmap?.formatted_heatmaps
                ?.related_keywords_heatmap,
            top_fmtd_rows:
              competitorData?.integrated_heatmap?.formatted_heatmaps
                ?.related_keywords_heatmap?.rows?.length || 0,
            ka_hmd_rows:
              competitorData?.keywords_analysis?.heatmap_data?.rows?.length ||
              0,
            ka_hmd_rel_rows:
              competitorData?.keywords_analysis?.heatmap_data?.related_keywords
                ?.rows?.length || 0,
          });

          // 関連キーワードヒートマップ（新構造・統合ヒートマップ対応）
          // WSデータ優先、DB取得データをフォールバック
          const relatedHeatmapData =
            competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.related_keywords_heatmap ||
            competitorData?.integrated_heatmap?.keywords_analysis?.heatmap_data;
          if (relatedHeatmapData?.rows) {
            return relatedHeatmapData.rows.map((row, index) => {
              const resultRow = {
                keyword: row.keyword,
                keyword_id: row.keyword_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                uniqueKey: `related-${index}-${row.keyword}`,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  // topic_coverage_scoreを0.1-10.0の範囲に正規化（関連キーワード用に調整）
                  const rawScore =
                    val.topic_coverage_score ||
                    val.relevance ||
                    val.strength ||
                    0.1;
                  // 関連キーワードは通常0-1の範囲なので、10倍して適切な範囲にする
                  const adjustedScore = rawScore < 1 ? rawScore * 10 : rawScore;
                  const normalizedScore = Math.max(
                    0.1,
                    Math.min(10.0, adjustedScore),
                  );
                  resultRow[siteKey] = normalizedScore;
                });
              }

              return resultRow;
            });
          }

          // フォールバック：関連キーワードヒートマップ（新構造・最適化版）
          const keywordsHeatmapData =
            competitorData?.keywords_analysis?.heatmap_data;

          if (keywordsHeatmapData?.rows) {
            const optimizedRows = keywordsHeatmapData.rows.filter(
              row => row.keyword_type === 'related',
            );

            return optimizedRows.map(row => {
              const resultRow = {
                keyword: row.keyword,
                keyword_type: row.keyword_type,
                search_volume: row.search_volume || 0,
                cpc: row.cpc || 0,
                competition: row.competition || 0,
                average_score: row.average_score || 0,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.strength || 0.1;
                });
              }

              return resultRow;
            });
          }

          // フォールバック：従来の方法
          const relatedKeywords =
            competitorData?.keywords_data?.related_keywords || [];

          return relatedKeywords.map(kw => {
            const displayKeyword = kw.keyword || kw;
            const row = { keyword: displayKeyword };

            competitors.forEach((c, idx) => {
              const volume = kw.search_volume || 0;
              // ランダム値ではなく、検索ボリュームに基づく固定値を生成
              const freq =
                volume > 0
                  ? Math.max(1, Math.min(10, Math.floor(volume / 1000) + 1))
                  : 0.1;
              row[`site${idx + 1}`] = freq;
            });

            return row;
          });
        } else if (type === 'lsi') {
          console.log('🔎 [prepareHeatmapData][lsi] paths', {
            fmtd: !!competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.lsi_keywords_heatmap,
            fmtd_rows:
              competitorData?.topic_analysis?.integrated_heatmap
                ?.formatted_heatmaps?.lsi_keywords_heatmap?.rows?.length || 0,
            top_fmtd:
              !!competitorData?.integrated_heatmap?.formatted_heatmaps
                ?.lsi_keywords_heatmap,
            top_fmtd_rows:
              competitorData?.integrated_heatmap?.formatted_heatmaps
                ?.lsi_keywords_heatmap?.rows?.length || 0,
            ka_lsi_heatmap_data_rows:
              competitorData?.keywords_analysis?.lsi_heatmap_data?.rows
                ?.length || 0,
            ka_hmd_rows:
              competitorData?.keywords_analysis?.heatmap_data?.rows?.length ||
              0,
            ka_hmd_lsi_rows:
              competitorData?.keywords_analysis?.heatmap_data?.lsi_keywords
                ?.rows?.length || 0,
          });

          // LSIキーワードヒートマップ（新構造・統合ヒートマップ対応）
          // WSデータ優先、DB取得データをフォールバック
          const lsiHeatmapData =
            competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.lsi_keywords_heatmap ||
            competitorData?.keywords_analysis?.lsi_heatmap_data;
          if (lsiHeatmapData?.rows) {
            return lsiHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.keyword,
                keyword_type: row.keyword_type,
                search_volume: row.search_volume || 0,
                cpc: row.cpc || 0,
                competition: row.competition || 0,
                average_score: row.average_score || 0,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.strength || 0.1;
                });
              }

              return resultRow;
            });
          }

          // フォールバック：LSIキーワードヒートマップ（新構造・最適化版）
          const keywordsHeatmapData =
            competitorData?.keywords_analysis?.heatmap_data;

          if (keywordsHeatmapData?.rows) {
            const optimizedRows = keywordsHeatmapData.rows.filter(
              row => row.keyword_type === 'lsi',
            );

            return optimizedRows.map(row => {
              const resultRow = {
                keyword: row.keyword,
                keyword_type: row.keyword_type,
                search_volume: row.search_volume || 0,
                cpc: row.cpc || 0,
                competition: row.competition || 0,
                average_score: row.average_score || 0,
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.strength || 0.1;
                });
              }

              return resultRow;
            });
          }

          // フォールバック：従来の方法
          const lsiKeywords = competitorData?.keywords_data?.lsi_keywords || [];

          return lsiKeywords.map(kw => {
            const displayKeyword = kw.keyword || kw;
            const row = { keyword: displayKeyword };

            competitors.forEach((c, idx) => {
              const volume = kw.search_volume || 0;
              // ランダム値ではなく、検索ボリュームに基づく固定値を生成
              const freq =
                volume > 0
                  ? Math.max(1, Math.min(10, Math.floor(volume / 1000) + 1))
                  : 0.1;
              row[`site${idx + 1}`] = freq;
            });

            return row;
          });
        } else if (type === 'topics') {
          // トピックヒートマップ（新構造・最適化版）
          // WSデータ優先、DB取得データをフォールバック
          const topicHeatmapData =
            competitorData?.topic_analysis?.integrated_heatmap
              ?.formatted_heatmaps?.topic_heatmap ||
            competitorData?.topic_analysis?.site_topic_analysis?.heatmap_data;

          // ヒートマップデータが存在する場合は強制的にheatmapsをtrueに設定（無限ループ防止）
          if (
            topicHeatmapData?.rows &&
            topicHeatmapData.rows.length > 0 &&
            !dataReady.heatmaps
          ) {
            setDataReady(prev => ({
              ...prev,
              heatmaps: true,
              topic_analysis: true,
            }));
          }

          if (topicHeatmapData?.rows) {
            const mappedRows = topicHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.topic,
                topic_id: row.topic_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: Array.isArray(row.values) ? row.values : [],
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const ci = Number(val?.competitor_index);
                  const rk = Number(val?.rank);
                  let siteIndex = Number.isFinite(ci)
                    ? ci >= 1
                      ? ci
                      : ci + 1
                    : Number.isFinite(rk)
                      ? rk
                      : null;
                  if (siteIndex) {
                    const siteKey = `site${siteIndex}`;
                    const score =
                      val.topic_coverage_score || val.relevance || 0.1;
                    resultRow[siteKey] = Math.max(0.1, Math.min(10.0, score));
                  }
                });
                try {
                  const has9 = Object.prototype.hasOwnProperty.call(
                    resultRow,
                    'site9',
                  );
                  const has10 = Object.prototype.hasOwnProperty.call(
                    resultRow,
                    'site10',
                  );
                  if (!has9 || !has10) {
                    console.log('[HeatmapRowMap][topics]', {
                      topic: resultRow.keyword,
                      has9,
                      has10,
                      valuesLen: row.values.length,
                      sampleKeys: Object.keys(resultRow)
                        .filter(k => k.startsWith('site'))
                        .slice(0, 12),
                    });
                  }
                } catch (err) {
                  console.warn('[HeatmapRowMap][topics][debug-failed]', err);
                }
              }

              return resultRow;
            });

            // 集計: DBに存在するcompetitor indexの一覧
            try {
              const present = new Set();
              topicHeatmapData.rows.forEach(r => {
                if (Array.isArray(r.values)) {
                  r.values.forEach(v => {
                    const ci = Number(v?.competitor_index);
                    const rk = Number(v?.rank);
                    const idxNorm = Number.isFinite(ci)
                      ? ci >= 1
                        ? ci
                        : ci + 1
                      : Number.isFinite(rk)
                        ? rk
                        : null;
                    if (idxNorm) present.add(idxNorm);
                  });
                }
              });
              console.log('[HeatmapRowMap][topics][presentIndices]', {
                present: Array.from(present).sort((a, b) => a - b),
              });
            } catch (err) {
              console.warn(
                '[HeatmapRowMap][topics-present][debug-failed]',
                err,
              );
            }

            return mappedRows;
          }

          // フォールバック：統合ヒートマップからトピックデータを取得
          const integratedHeatmapData = competitorData?.integrated_heatmap;
          if (
            integratedHeatmapData?.topic_analysis?.site_topic_analysis
              ?.heatmap_data?.rows
          ) {
            const integratedTopicData =
              integratedHeatmapData.topic_analysis.site_topic_analysis
                .heatmap_data;

            const mappedRows = integratedTopicData.rows.map(row => {
              const resultRow = {
                keyword: row.topic,
                topic_id: row.topic_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: Array.isArray(row.values) ? row.values : [],
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const ci = Number(val?.competitor_index);
                  const rk = Number(val?.rank);
                  let siteIndex = Number.isFinite(ci)
                    ? ci >= 1
                      ? ci
                      : ci + 1
                    : Number.isFinite(rk)
                      ? rk
                      : null;
                  if (siteIndex) {
                    const siteKey = `site${siteIndex}`;
                    const score =
                      val.topic_coverage_score || val.relevance || 0.1;
                    resultRow[siteKey] = Math.max(0.1, Math.min(10.0, score));
                  }
                });
                try {
                  const has9 = Object.prototype.hasOwnProperty.call(
                    resultRow,
                    'site9',
                  );
                  const has10 = Object.prototype.hasOwnProperty.call(
                    resultRow,
                    'site10',
                  );
                  if (!has9 || !has10) {
                    console.log('[HeatmapRowMap][topics-fallback]', {
                      topic: resultRow.keyword,
                      has9,
                      has10,
                      valuesLen: row.values.length,
                      sampleKeys: Object.keys(resultRow)
                        .filter(k => k.startsWith('site'))
                        .slice(0, 12),
                    });
                  }
                } catch (err) {
                  console.warn(
                    '[HeatmapRowMap][topics-fallback][debug-failed]',
                    err,
                  );
                }
              }

              return resultRow;
            });

            // 集計: DBに存在するcompetitor indexの一覧（fallback）
            try {
              const present = new Set();
              integratedTopicData.rows.forEach(r => {
                if (Array.isArray(r.values)) {
                  r.values.forEach(v => {
                    const ci = Number(v?.competitor_index);
                    const rk = Number(v?.rank);
                    const idxNorm = Number.isFinite(ci)
                      ? ci >= 1
                        ? ci
                        : ci + 1
                      : Number.isFinite(rk)
                        ? rk
                        : null;
                    if (idxNorm) present.add(idxNorm);
                  });
                }
              });
              console.log('[HeatmapRowMap][topics-fallback][presentIndices]', {
                present: Array.from(present).sort((a, b) => a - b),
              });
            } catch (err) {
              console.warn(
                '[HeatmapRowMap][topics-fallback-present][debug-failed]',
                err,
              );
            }

            return mappedRows;
          }

          // フォールバック：トピック分析結果から生成
          const topicAnalysis =
            competitorData?.topic_analysis?.mece_analysis
              ?.topic_recommendations;
          const allTopics = [];

          if (topicAnalysis) {
            const mustHave = topicAnalysis.must_have || [];
            const shouldHave = topicAnalysis.should_have || [];
            const couldHave = topicAnalysis.could_have || [];
            const uniqueAngles = topicAnalysis.unique_angles || [];

            mustHave.forEach(topic =>
              allTopics.push({ topic, priority: 1, category: 'must_have' }),
            );
            shouldHave.forEach(topic =>
              allTopics.push({ topic, priority: 2, category: 'should_have' }),
            );
            couldHave.forEach(topic =>
              allTopics.push({ topic, priority: 3, category: 'could_have' }),
            );
            uniqueAngles.forEach(topic =>
              allTopics.push({ topic, priority: 4, category: 'unique_angles' }),
            );
          }

          return allTopics.map(topicData => {
            const { topic, priority, category } = topicData;
            const row = {
              keyword: topic,
              priority,
              category,
              isAnalyzed: category !== 'headings',
            };

            competitors.forEach((c, idx) => {
              const relevance = Math.random() * 0.5 + 0.5;
              row[`site${idx + 1}`] = relevance;
            });

            return row;
          });
        }

        return [];
      } else {
        // 古い構造での処理

        if (type === 'terms') {
          // 1. 新構造: topic_analysis内からの統合ヒートマップ取得を試行
          const topicAnalysisIntegratedHeatmap =
            competitorData?.topic_analysis?.integrated_heatmap;
          if (
            topicAnalysisIntegratedHeatmap?.formatted_heatmaps
              ?.important_terms_heatmap?.rows
          ) {
            const termsHeatmapData =
              topicAnalysisIntegratedHeatmap.formatted_heatmaps
                .important_terms_heatmap;

            return termsHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: row.values || [], // valuesも保持
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.term_coverage_score || 0.1;
                });
              }

              return resultRow;
            });
          }

          // 2. 新構造: topic_analysis.terms_evaluationからheatmap_data取得を試行
          const termsHeatmapData =
            competitorData?.topic_analysis?.terms_evaluation?.heatmap_data;

          if (termsHeatmapData?.rows) {
            return termsHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: row.values || [], // valuesも保持
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.term_coverage_score || 0.1;
                });
              }

              return resultRow;
            });
          }

          // 3. レガシー構造: integrated_heatmapからの取得を試行
          const integratedHeatmapData = competitorData?.integrated_heatmap;
          if (
            integratedHeatmapData?.formatted_heatmaps?.important_terms_heatmap
              ?.rows
          ) {
            const termsHeatmapData =
              integratedHeatmapData.formatted_heatmaps.important_terms_heatmap;

            return termsHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: row.values || [], // valuesも保持
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.term_coverage_score || 0.1;
                });
              }

              return resultRow;
            });
          }

          // 2. topic_analysisからterms_evaluationデータの取得を試行
          const topicAnalysisData = competitorData?.topic_analysis;
          if (
            topicAnalysisData?.terms_evaluation?.site_terms_evaluation
              ?.heatmap_data?.rows
          ) {
            const termsHeatmapData =
              topicAnalysisData.terms_evaluation.site_terms_evaluation
                .heatmap_data;

            return termsHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: row.values || [],
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.normalized_score || val.term_coverage_score || 0.1;
                });
              }

              return resultRow;
            });
          }

          // 4. 従来のintegratedHeatmapData?.terms_evaluation?.heatmap_data?.rowsからの取得
          if (integratedHeatmapData?.terms_evaluation?.heatmap_data?.rows) {
            const termsHeatmapData =
              integratedHeatmapData.terms_evaluation.heatmap_data;

            return termsHeatmapData.rows.map(row => {
              const resultRow = {
                keyword: row.term,
                term_id: row.term_id,
                importance: row.importance,
                average_score: row.average_score || 0,
                category: row.importance,
                isAnalyzed: true,
                values: row.values || [], // valuesも保持
              };

              // 各競合サイトのスコアを設定
              if (Array.isArray(row.values)) {
                row.values.forEach(val => {
                  const siteKey = `site${val.competitor_index + 1}`;
                  resultRow[siteKey] =
                    val.term_coverage_score || val.normalized_score || 0.1;
                });
              }

              return resultRow;
            });
          }

          // フォールバック：重要語句分析結果を表示（古い構造）
          const importantTerms =
            competitorData?.important_terms_analysis?.terms || [];

          if (importantTerms.length === 0) {
            // 完全フォールバック：基本的なダミーデータを作成
            const fallbackTerms = [
              'ブログ',
              'アフィリエイト',
              'SEO',
              '収益化',
              '記事',
            ];
            return fallbackTerms.map(term => {
              const row = { keyword: term, isAnalyzed: true };
              competitors.forEach((c, idx) => {
                row[`site${idx + 1}`] = 0.1; // 最小値を設定
              });
              return row;
            });
          }

          return importantTerms.map(term => {
            const termData = term.term || term.normalized_term || term;
            const row = { keyword: termData };

            competitors.forEach((c, idx) => {
              const freq = term.frequency || 0;
              const siteFreq = Math.max(
                0,
                freq - Math.floor(Math.random() * freq * 0.3),
              );
              row[`site${idx + 1}`] = siteFreq;
            });

            return row;
          });
        } else if (type === 'related') {
          // 関連キーワードヒートマップ（古い構造）
          const suggestKeywords = competitorData?.suggest_keywords || [];

          return suggestKeywords.map(kw => {
            const displayKeyword = kw.keyword || kw;
            const row = { keyword: displayKeyword };

            competitors.forEach((c, idx) => {
              const volume = kw.search_volume || 0;
              const freq =
                volume > 0
                  ? Math.max(1, Math.floor(Math.random() * 10) + 1)
                  : 0;
              row[`site${idx + 1}`] = freq;
            });

            return row;
          });
        } else if (type === 'lsi') {
          // LSIキーワード（古い構造）
          const lsiData = competitorData?.lsi_keywords || {};
          const lsiCandidates = lsiData.lsi_candidates || [];
          const baseKeyword = sessionData.keyword || '';

          if (lsiCandidates.length > 0) {
            const candidateRows = lsiCandidates.map(kw => {
              const fullKeyword = kw.keyword || '';

              const row = {
                keyword: fullKeyword,
                level: kw.lsi_level || 1,
                isImportant: kw.lsi_level === 1,
                search_volume: kw.search_volume || 0,
                difficulty: kw.keyword_difficulty || 0,
                cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0, // USD→JPY換算
                competition_level: kw.competition_level || '',
              };

              competitors.forEach((c, idx) => {
                const relevanceScore = kw.relevance_score || 0;
                const competition = kw.competition || 0;
                const relevance =
                  relevanceScore > 0
                    ? relevanceScore
                    : competition > 0
                      ? competition
                      : Math.random() * 0.8;

                row[`site${idx + 1}`] = Math.min(1, relevance);
              });
              return row;
            });

            return candidateRows;
          }

          // フォールバック：lsi_candidatesがない場合はlevel1_keywords・level2_keywordsを使用
          const level1Keywords = lsiData.level1_keywords || [];
          const level2Keywords = lsiData.level2_keywords || [];
          const baseWords = baseKeyword
            .split(' ')
            .filter(word => word.length > 0);

          // 1階層キーワードを処理
          const level1Rows = level1Keywords.map(kw => {
            const fullKeyword = kw.keyword || '';
            let displayKeyword = fullKeyword;

            // 単語単位でベースキーワードの各単語を除外
            baseWords.forEach(baseWord => {
              const regex = new RegExp(`\\b${baseWord}\\b`, 'gi');
              displayKeyword = displayKeyword.replace(regex, '').trim();
            });

            // 連続する空白を整理
            displayKeyword = displayKeyword.replace(/\s+/g, ' ').trim();
            if (displayKeyword === '') displayKeyword = fullKeyword;

            const row = {
              keyword: displayKeyword,
              level: 1,
              isImportant: true, // 1階層目は重要マーク
              search_volume: kw.search_volume || 0,
              difficulty: kw.keyword_difficulty || 0,
              cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0, // USD→JPY換算
            };

            competitors.forEach((c, idx) => {
              // LSI関連度スコア（1階層は高めの関連度）
              const relevance = Math.random() * 0.4 + 0.6; // 0.6-1.0の範囲
              row[`site${idx + 1}`] = relevance;
            });
            return row;
          });

          // 2階層キーワードを処理
          const level2Rows = level2Keywords.map(kw => {
            const fullKeyword = kw.keyword || '';
            let displayKeyword = fullKeyword;

            // 単語単位でベースキーワードの各単語を除外
            baseWords.forEach(baseWord => {
              const regex = new RegExp(`\\b${baseWord}\\b`, 'gi');
              displayKeyword = displayKeyword.replace(regex, '').trim();
            });

            // 連続する空白を整理
            displayKeyword = displayKeyword.replace(/\s+/g, ' ').trim();
            if (displayKeyword === '') displayKeyword = fullKeyword;

            const parentKeyword = kw.parent_keyword || '';
            const row = {
              keyword: displayKeyword,
              level: 2,
              isImportant: false, // 2階層目は重要マークなし
              parent: parentKeyword,
              search_volume: kw.search_volume || 0,
              difficulty: kw.keyword_difficulty || 0,
              cpc: kw.cpc ? Math.floor(kw.cpc * 150) : 0, // USD→JPY換算
            };

            competitors.forEach((c, idx) => {
              // LSI関連度スコア（ランダム値を使用：実際のキーワードマッチングデータがないため）
              const relevanceScore = kw.relevance_score || 0;
              const randomVariation = Math.random() * 0.3 + 0.7; // 0.7-1.0の範囲
              const relevance = Math.min(1, relevanceScore * randomVariation);

              row[`site${idx + 1}`] = relevance;
            });
            return row;
          });

          // 1階層・2階層を結合して返す
          return [...level1Rows, ...level2Rows];
        } else if (type === 'topics') {
          // 実際のトピック分析結果（古い構造・MECE分析）を使用
          const topicAnalysis =
            competitorData?.topic_analysis?.mece_analysis
              ?.topic_recommendations;
          const allTopics = [];

          if (topicAnalysis) {
            // MECE分析の結果から統合トピックを取得
            const mustHave = topicAnalysis.must_have || [];
            const shouldHave = topicAnalysis.should_have || [];
            const couldHave = topicAnalysis.could_have || [];
            const uniqueAngles = topicAnalysis.unique_angles || [];

            // 各カテゴリのトピックを重要度とともに追加
            mustHave.forEach(topic =>
              allTopics.push({ topic, priority: 1, category: 'must_have' }),
            );
            shouldHave.forEach(topic =>
              allTopics.push({ topic, priority: 2, category: 'should_have' }),
            );
            couldHave.forEach(topic =>
              allTopics.push({ topic, priority: 3, category: 'could_have' }),
            );
            uniqueAngles.forEach(topic =>
              allTopics.push({ topic, priority: 4, category: 'unique_angles' }),
            );
          }

          // フォールバック：MECE分析結果がない場合は従来の方法
          if (allTopics.length === 0) {
            const topicSet = new Set();
            competitors.forEach(c => {
              if (
                c.headings_analysis?.topics &&
                typeof c.headings_analysis.topics === 'object'
              ) {
                Object.keys(c.headings_analysis.topics).forEach(t =>
                  topicSet.add(t),
                );
              }
            });
            Array.from(topicSet).forEach(topic =>
              allTopics.push({ topic, priority: 5, category: 'headings' }),
            );
          }

          return allTopics.map(topicData => {
            const { topic, priority, category } = topicData;
            const row = {
              keyword: topic,
              priority,
              category,
              isAnalyzed: category !== 'headings', // MECE分析されたトピックかどうか
            };

            competitors.forEach((c, idx) => {
              // 実際の競合分析から各サイトでのトピック出現度を計算
              let topicStrength = 0;

              // 競合サイトの見出しからトピック関連性を推定
              const headings = c.headings_analysis?.headings || [];
              const titleText = c.title || '';
              const allText = [titleText, ...headings.map(h => h.text || '')]
                .join(' ')
                .toLowerCase();

              // トピックキーワードの含有度を計算
              const topicKeywords = topic
                .toLowerCase()
                .split(/[・、\s]+/)
                .filter(k => k.length > 1);
              let matchCount = 0;

              topicKeywords.forEach(keyword => {
                if (allText.includes(keyword)) {
                  matchCount++;
                }
              });

              // マッチ率を計算（0-1の範囲）
              topicStrength =
                topicKeywords.length > 0
                  ? matchCount / topicKeywords.length
                  : 0;

              // 重要度による重み付け（must_have > should_have > could_have > unique_angles > headings）
              const priorityWeight =
                priority <= 2
                  ? 1.0
                  : priority <= 3
                    ? 0.8
                    : priority <= 4
                      ? 0.6
                      : 0.4;
              topicStrength *= priorityWeight;

              row[`site${idx + 1}`] = Math.min(1, topicStrength);
            });

            return row;
          });
        }

        return [];
      }
    };

    // トピック用のヒートマップ色計算（順位ベース）
    const getTopicHeatmapColor = rank => {
      if (rank === 0) return '#f0f0f0'; // 扱っていない
      if (rank === 1) return '#ff4d4f'; // 1位（濃い赤）
      if (rank === 2) return '#ff7875'; // 2位
      if (rank === 3) return '#ff9c94'; // 3位
      if (rank === 4) return '#ffb8b2'; // 4位（薄めのオレンジ系）
      if (rank === 5) return '#ffd8d3'; // 5位
      return '#f6f6f6'; // 6位以下
    };
    // 色関数は共通ユーティリティを使用（Step3と完全一致）
    return (
      <div>
        {/* 表示切り替えボタン */}
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Radio.Group
            value={headingView}
            onChange={e => setHeadingView(e.target.value)}
          >
            {(() => {
              const isCompleted = analysisState?.status === 'completed';
              const hasHeadings =
                Array.isArray(
                  competitorData?.competitor_analysis?.competitors,
                ) &&
                competitorData.competitor_analysis.competitors.some(
                  c => Array.isArray(c.headings) && c.headings.length > 0,
                );
              const topicHeatmapRows =
                competitorData?.topic_analysis?.integrated_heatmap
                  ?.formatted_heatmaps?.topic_heatmap?.rows ||
                competitorData?.topic_analysis?.site_topic_analysis
                  ?.heatmap_data?.rows;
              const termsRows =
                competitorData?.topic_analysis?.integrated_heatmap
                  ?.formatted_heatmaps?.important_terms_heatmap?.rows ||
                competitorData?.topic_analysis?.terms_evaluation?.heatmap_data
                  ?.rows;
              const relatedRows =
                competitorData?.topic_analysis?.integrated_heatmap
                  ?.formatted_heatmaps?.related_keywords_heatmap?.rows ||
                competitorData?.keywords_analysis?.heatmap_data?.rows;
              const lsiRows =
                competitorData?.topic_analysis?.integrated_heatmap
                  ?.formatted_heatmaps?.lsi_keywords_heatmap?.rows ||
                competitorData?.keywords_analysis?.lsi_heatmap_data?.rows;

              const isEnabled = rows =>
                !isCompleted ? true : Array.isArray(rows) && rows.length > 0;

              const btn = (value, label, enabled) => (
                <Tooltip title={enabled ? undefined : 'データがありません'}>
                  <Radio.Button value={value} disabled={!enabled}>
                    {label}
                  </Radio.Button>
                </Tooltip>
              );

              // 追加: 上位サイトタブは常に有効
              return (
                <>
                  {btn('top-sites', '上位サイト', true)}
                  {btn('headings', '見出し構造', !!hasHeadings)}
                  {btn('topics', 'トピック', isEnabled(topicHeatmapRows))}
                  {btn('terms', '重要語句', isEnabled(termsRows))}
                  {btn('related', '関連キーワード', isEnabled(relatedRows))}
                  {btn('lsi', 'LSIキーワード', isEnabled(lsiRows))}
                </>
              );
            })()}
          </Radio.Group>

          {headingView === 'topics' && (
            <Text type="secondary" className="intensity-description">
              <InfoCircleOutlined /> 数値は言及の強度をスコアリングしたもの
            </Text>
          )}
          {headingView === 'terms' && (
            <Text type="secondary" className="intensity-description">
              <InfoCircleOutlined /> 数値は言及の強度をスコアリングしたもの
            </Text>
          )}
          {headingView === 'related' && (
            <Text type="secondary" className="intensity-description">
              <InfoCircleOutlined /> 数値は言及の強度をスコアリングしたもの
            </Text>
          )}
          {headingView === 'lsi' && (
            <Text type="secondary" className="intensity-description">
              <InfoCircleOutlined /> 数値は言及の強度をスコアリングしたもの
            </Text>
          )}
        </div>

        {headingView === 'top-sites' ? (
          <div
            className="fade-in tour-competitor-list-section"
            style={{ paddingTop: 8 }}
          >
            <Row gutter={[16, 16]}>
              {(competitors || []).slice(0, 10).map((c, idx) => {
                const domain = (() => {
                  try {
                    return new URL(c.url || '').hostname.replace('www.', '');
                  } catch (_) {
                    return '';
                  }
                })();
                const summaryText = c.summary || '';
                // トピックカバレッジの簡易平均（存在時）
                const topicHeatmap =
                  competitorData?.topic_analysis?.integrated_heatmap
                    ?.formatted_heatmaps?.topic_heatmap ||
                  competitorData?.topic_analysis?.site_topic_analysis
                    ?.heatmap_data;
                let topicCoveragePct = null;
                try {
                  if (topicHeatmap?.rows && Array.isArray(topicHeatmap.rows)) {
                    // 競合全体からトピック重み（必須/重要/独自/その他）を算出
                    const computeRowClass = record => {
                      // 表示AVG（行）
                      const getDisplayedAvgRow = rec => {
                        const direct = rec?.average_score;
                        if (typeof direct === 'number' && !isNaN(direct))
                          return direct;
                        const alt =
                          rec?.average_normalized_score ??
                          rec?.score ??
                          rec?.normalized_score;
                        if (typeof alt === 'number' && !isNaN(alt)) return alt;
                        let sum = 0;
                        let cnt = 0;
                        if (rec?.values && Array.isArray(rec.values)) {
                          rec.values.forEach(v => {
                            const raw =
                              v.average_score ??
                              v.normalized_score ??
                              v.topic_coverage_score ??
                              0;
                            if (typeof raw === 'number' && !isNaN(raw)) {
                              sum += raw;
                              cnt++;
                            }
                          });
                        } else {
                          for (let i = 1; i <= 9; i++) {
                            const raw = rec[`site${i}`];
                            if (typeof raw === 'number' && !isNaN(raw)) {
                              sum += raw;
                              cnt++;
                            }
                          }
                        }
                        return cnt > 0 ? sum / cnt : 0;
                      };

                      const collectSiteValuesRow = rec => {
                        const arr = [];
                        if (rec?.values && Array.isArray(rec.values)) {
                          rec.values.forEach(v => {
                            const raw =
                              v.normalized_score ??
                              v.average_score ??
                              v.score ??
                              v.topic_coverage_score ??
                              0;
                            if (typeof raw === 'number' && !isNaN(raw))
                              arr.push({
                                value: raw,
                                idx:
                                  typeof v.competitor_index === 'number'
                                    ? v.competitor_index
                                    : null,
                              });
                          });
                        } else {
                          for (let i = 1; i <= 9; i++) {
                            const raw = record[`site${i}`];
                            if (typeof raw === 'number' && !isNaN(raw))
                              arr.push({ value: raw, idx: i - 1 });
                          }
                        }
                        return arr;
                      };

                      const avg = getDisplayedAvgRow(record);
                      const avgRounded = Number.isFinite(avg)
                        ? Math.round(avg * 10) / 10
                        : avg;
                      const siteVals = collectSiteValuesRow(record);
                      const top3Vals = siteVals
                        .filter(v => v.idx !== null && v.idx <= 2)
                        .map(v => v.value);
                      const countTop3Over85 = top3Vals.filter(
                        v => v >= 8.5,
                      ).length;
                      const countSitesOver75 = siteVals.filter(
                        v => v.value >= 7.5,
                      ).length;
                      const countSitesOver7 = siteVals.filter(
                        v => v.value >= 7,
                      ).length;
                      const countSitesOver85 = siteVals.filter(
                        v => v.value >= 8.5,
                      ).length;

                      if (avgRounded >= 8 || countTop3Over85 >= 2)
                        return 'must';
                      if (
                        avgRounded <= 5.0 &&
                        countSitesOver85 > 0 &&
                        countSitesOver85 <= 2
                      )
                        return 'unique';
                      if (avgRounded >= 6 || countSitesOver75 >= 3)
                        return 'important';
                      return 'other';
                    };

                    // カバレッジ寄与（6以上で含有、スコア大でボーナス）
                    const contribution = x => {
                      const v = Math.max(0, Math.min(10, x || 0));
                      if (v >= 8.5) return 1.0;
                      if (v >= 8.0) return 0.9;
                      if (v >= 7.5) return 0.8;
                      if (v >= 7.0) return 0.7;
                      if (v >= 6.5) return 0.6;
                      if (v >= 6.0) return 0.5;
                      return 0.0;
                    };

                    let includedCount = 0; // 必須/重要トピック総数
                    let baseIncluded = 0; // このサイトで6以上の件数
                    let bonusSum = 0; // 高スコアボーナス

                    for (const row of topicHeatmap.rows) {
                      const klass = computeRowClass(row);
                      if (!(klass === 'must' || klass === 'important'))
                        continue;
                      // サイトごとの値取得
                      let val = 0;
                      if (Array.isArray(row.values)) {
                        const vobj = row.values.find(
                          v => v.competitor_index === idx,
                        );
                        val = vobj
                          ? (vobj.normalized_score ??
                            vobj.average_score ??
                            vobj.score ??
                            vobj.topic_coverage_score ??
                            0)
                          : 0;
                      } else {
                        val =
                          row[`site${idx + 1}`] ?? row[`site_${idx + 1}`] ?? 0;
                      }
                      includedCount += 1;
                      if (val >= 6.0) {
                        baseIncluded += 1;
                        if (val >= 8.5) bonusSum += 0.1;
                        else if (val >= 8.0) bonusSum += 0.05;
                        else if (val >= 7.5) bonusSum += 0.02;
                      }
                    }

                    const numerator = baseIncluded + bonusSum;
                    const coverage =
                      includedCount > 0
                        ? Math.min(1, numerator / includedCount)
                        : 0; // 0-1
                    topicCoveragePct = Math.round(coverage * 100);

                    // デバッグ出力（トピックカバレッジ算出）
                    try {
                      console.log('[TopicCoverage]', {
                        competitorIndex: idx,
                        domain: c?.domain || c?.site_domain || c?.url,
                        includedCount,
                        baseIncluded,
                        bonusSum: Number(bonusSum.toFixed(3)),
                        numerator: Number(numerator.toFixed(3)),
                        coverage01: Number(coverage.toFixed(4)),
                        topicCoveragePct,
                      });
                    } catch (err) {
                      console.warn('[TopicCoverage][debug-failed]', err);
                    }
                  }
                } catch (e) {
                  topicCoveragePct = null;
                }

                // 見出しプレビュー（上位3件）
                const headingPreview = (
                  Array.isArray(c.headings) ? c.headings : []
                )
                  .slice(0, 3)
                  .map(h => h?.text || '')
                  .filter(Boolean)
                  .join(' / ');

                return (
                  <Col xs={24} key={`top-${idx}`}>
                    <Card
                      className={`v2-card ${idx === 0 ? 'tour-first-competitor-card' : ''}`}
                      hoverable={false}
                      bodyStyle={{ padding: 16 }}
                    >
                      <Row gutter={16} align="top">
                        {/* 左カラム（5:5） */}
                        <Col xs={24} md={12}>
                          {/* 1段目：favicon + タイトル */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <Badge
                              count={idx + 1}
                              style={{ backgroundColor: '#bfbfbf' }}
                            />
                            {domain ? (
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                                alt="favicon"
                                width={16}
                                height={16}
                                style={{ borderRadius: 2 }}
                                onError={e => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : null}
                            <a
                              href={c.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: 14,
                                color: '#1890ff',
                                fontWeight: 500,
                                whiteSpace: 'normal',
                                overflow: 'visible',
                              }}
                            >
                              {c.title || domain || c.url}
                            </a>
                          </div>

                          {/* 2段目整形：1行目=Strength/種別/ドメイン, 2行目=カバレッジ/文字数/見出し数/構成プレビュー */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              marginBottom: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Row gutter={[8, 4]} style={{ width: '100%' }}>
                              <Col span={24}>
                                {dataReady.competitor_scoring ||
                                analysisState.status === 'completed' ||
                                analysisState.status ===
                                  'completed_with_errors' ? (
                                  (() => {
                                    const raw =
                                      c.strength ??
                                      c.Strength ??
                                      (c.metrics
                                        ? c.metrics.strength
                                        : undefined);
                                    const num = Number(raw);
                                    const strengthValue = Number.isFinite(num)
                                      ? Math.round(num)
                                      : null;
                                    return strengthValue !== null ? (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4,
                                          marginRight: 8,
                                        }}
                                      >
                                        <ThunderboltOutlined
                                          style={{
                                            color: '#eb2f96',
                                            fontSize: 16,
                                          }}
                                        />
                                        <Text
                                          style={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: '#eb2f96',
                                          }}
                                        >
                                          {strengthValue}
                                        </Text>
                                      </span>
                                    ) : null;
                                  })()
                                ) : (
                                  <Skeleton
                                    active
                                    title={false}
                                    paragraph={{ rows: 1, width: 60 }}
                                    style={{ width: 60, height: 22 }}
                                  />
                                )}
                                {c.site_type ? (
                                  <Tag
                                    style={{
                                      backgroundColor:
                                        (colorByType[c.site_type] || {}).bg ||
                                        '#f5f5f5',
                                      color:
                                        (colorByType[c.site_type] || {}).text ||
                                        '#595959',
                                      border: 'none',
                                    }}
                                  >
                                    {siteTypeMap[c.site_type] || c.site_type}
                                  </Tag>
                                ) : null}
                                {domain ? (
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: 12,
                                      display: 'inline-block',
                                      marginLeft: 8,
                                      whiteSpace: 'normal',
                                      overflow: 'visible',
                                    }}
                                  >
                                    {domain}
                                  </Text>
                                ) : null}
                              </Col>
                              <Divider
                                style={{
                                  margin: '8px',
                                  borderColor: '#fafafa',
                                }}
                              />
                              <Col span={24}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Button
                                    size="small"
                                    style={{
                                      height: 22,
                                      padding: '0 8px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      fontSize: 12,
                                    }}
                                    onClick={() => {
                                      try {
                                        const headingsArr = Array.isArray(
                                          c.headings_analysis?.headings,
                                        )
                                          ? c.headings_analysis.headings
                                          : Array.isArray(c.headings)
                                            ? c.headings
                                            : [];
                                        const normalized = headingsArr.map(
                                          h => {
                                            const rawLevel =
                                              h.level || h.tag || 2;
                                            let lvl = rawLevel;
                                            if (typeof rawLevel === 'string') {
                                              const up = rawLevel.toUpperCase();
                                              if (up.startsWith('H'))
                                                lvl =
                                                  parseInt(up.slice(1)) || 2;
                                              else
                                                lvl = parseInt(rawLevel) || 2;
                                            }
                                            const text = (
                                              h.text ||
                                              h.title ||
                                              ''
                                            ).trim();
                                            return { lvl, text };
                                          },
                                        );
                                        const structuredHeadings = [];
                                        let currentH2 = null;
                                        normalized.forEach(item => {
                                          if (!item.text) return;
                                          if (item.lvl === 2) {
                                            currentH2 = {
                                              level: 2,
                                              text: item.text,
                                              h3s: [],
                                              h4s: [],
                                            };
                                            structuredHeadings.push(currentH2);
                                          } else if (item.lvl === 3) {
                                            if (!currentH2) {
                                              currentH2 = {
                                                level: 2,
                                                text: '(前置)',
                                                h3s: [],
                                                h4s: [],
                                              };
                                              structuredHeadings.push(
                                                currentH2,
                                              );
                                            }
                                            currentH2.h3s.push(item.text);
                                          }
                                        });

                                        setScorePopupData({
                                          source: 'site',
                                          siteIndex: c.position || c.rank || -1,
                                          pageUrl: c.url,
                                          pageTitle:
                                            c.title || c.domain || c.url,
                                          favicon:
                                            c.favicon ||
                                            (c.basic_info
                                              ? c.basic_info.favicon
                                              : ''),
                                          siteName:
                                            c.domain ||
                                            (c.basic_info
                                              ? c.basic_info.domain
                                              : ''),
                                          score: 0,
                                          topic: '',
                                          structuredHeadings,
                                        });
                                        setScorePopupVisible(true);
                                      } catch (_) {
                                        setScorePopupData({
                                          source: 'site',
                                          siteIndex: c.position || c.rank || -1,
                                          pageUrl: c.url,
                                          pageTitle:
                                            c.title || c.domain || c.url,
                                          favicon:
                                            c.favicon ||
                                            (c.basic_info
                                              ? c.basic_info.favicon
                                              : ''),
                                          siteName:
                                            c.domain ||
                                            (c.basic_info
                                              ? c.basic_info.domain
                                              : ''),
                                          score: 0,
                                          topic: '',
                                          structuredHeadings: [],
                                        });
                                        setScorePopupVisible(true);
                                      }
                                    }}
                                  >
                                    構成プレビュー
                                  </Button>
                                  {typeof topicCoveragePct === 'number' ? (
                                    <Tag
                                      style={{
                                        backgroundColor: '#fffbe6',
                                        color: '#ad8b00',
                                        border: 'none',
                                        height: 22,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      トピックカバレッジ {topicCoveragePct}%
                                    </Tag>
                                  ) : null}
                                  <Tag
                                    style={{
                                      height: 22,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {(c.word_count || 0).toLocaleString()}文字
                                  </Tag>
                                  <Tag
                                    color="green"
                                    style={{
                                      height: 22,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {c.headings_analysis?.total_headings ||
                                      (Array.isArray(c.headings)
                                        ? c.headings.length
                                        : 0)}
                                    見出し
                                  </Tag>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>

                        {/* 右カラム（5:5） */}
                        <Col xs={24} md={12}>
                          {summaryText ? (
                            <Paragraph
                              style={{ margin: 0, whiteSpace: 'pre-wrap' }}
                            >
                              {summaryText}
                            </Paragraph>
                          ) : (
                            <Skeleton
                              active
                              paragraph={{ rows: 3 }}
                              title={false}
                              className="pulse"
                            />
                          )}
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ) : headingView === 'headings' ? (
          // 見出し横並び表示
          <div
            id="competitor-heading-cards"
            className="heading-comparison tour-competitor-headings-section"
            style={{
              overflowX: 'auto',
              paddingBottom: 16,
              marginBottom: -16,
            }}
          >
            <style>{`
              #competitor-heading-cards .ant-card-head {
                padding: 8px !important;
              }
              #competitor-heading-cards {
                cursor: grab;
              }
              #competitor-heading-cards:active {
                cursor: grabbing;
              }
              #competitor-heading-cards .ant-card,
              #competitor-heading-cards .ant-card-body {
                pointer-events: none;
              }
              #competitor-heading-cards .ant-card-head {
                pointer-events: auto;
                cursor: pointer;
              }
              #competitor-heading-cards .ant-card-body {
                pointer-events: auto;
                cursor: default;
              }
            `}</style>
            <Row
              gutter={16}
              wrap={false}
              style={{
                minWidth: 'max-content',
                cursor: 'grab',
                userSelect: 'none',
              }}
              onMouseDown={e => {
                const row = e.currentTarget;
                const scrollContainer = row.parentElement;
                const startX = e.pageX - row.offsetLeft;
                const scrollLeft = scrollContainer.scrollLeft;

                const handleMouseMove = moveEvent => {
                  if (!scrollContainer) return;
                  const x = moveEvent.pageX - row.offsetLeft;
                  const walk = (x - startX) * 2;
                  scrollContainer.scrollLeft = scrollLeft - walk;
                };

                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                  if (row) {
                    row.style.cursor = 'grab';
                  }
                };

                row.style.cursor = 'grabbing';
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);

                // デフォルトのドラッグ動作を防ぐ
                e.preventDefault();
              }}
            >
              {competitors.map((competitor, index) => (
                <Col key={index} style={{ width: '400px', flexShrink: 0 }}>
                  <Card
                    className="v2-card"
                    title={
                      dataReady.competitor_analysis ? (
                        <Space
                          direction="vertical"
                          size="small"
                          style={{
                            width: '100%',
                            padding: '0 4px',
                            margin: '0',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              paddingBottom: '8px',
                            }}
                          >
                            <Badge
                              count={index + 1}
                              style={{ backgroundColor: '#bfbfbf' }}
                            />
                            <div style={{ flex: 1 }}>
                              <Tooltip
                                title={
                                  competitor?.headings_analysis?.meta_data
                                    ?.title || competitor.title
                                }
                              >
                                <div
                                  style={{
                                    fontWeight: 'normal',
                                    fontSize: '14px',
                                    lineHeight: '1.3',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                    overflow: 'hidden',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '2.6em',
                                  }}
                                >
                                  <span
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {competitor.title}
                                  </span>
                                </div>
                              </Tooltip>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                URL:{' '}
                                <a
                                  href={competitor.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#1890ff',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {competitor.url}
                                </a>
                              </div>
                            </div>
                          </div>
                          <Space size="small">
                            {(() => {
                              const raw =
                                competitor.strength ??
                                competitor.Strength ??
                                (competitor.metrics
                                  ? competitor.metrics.strength
                                  : undefined);
                              const num = Number(raw);
                              const strengthValue = Number.isFinite(num)
                                ? Math.round(num)
                                : null;
                              const isCompleted =
                                analysisState.status === 'completed' ||
                                analysisState.status ===
                                  'completed_with_errors';
                              if (
                                dataReady.competitor_scoring ||
                                (isCompleted && strengthValue !== null)
                              ) {
                                return strengthValue !== null ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                    }}
                                  >
                                    <ThunderboltOutlined
                                      style={{ color: '#eb2f96', fontSize: 16 }}
                                    />
                                    <Text
                                      style={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: '#eb2f96',
                                      }}
                                    >
                                      {strengthValue}
                                    </Text>
                                  </span>
                                ) : null;
                              }
                              return (
                                <Skeleton
                                  active
                                  title={false}
                                  paragraph={{ rows: 1, width: 60 }}
                                  style={{ width: 60, height: 22 }}
                                />
                              );
                            })()}
                            <Tag>
                              {(competitor.word_count || 0).toLocaleString()}
                              文字
                            </Tag>
                            <Tag color="green">
                              {competitor.headings_analysis?.total_headings ||
                                0}
                              見出し
                            </Tag>
                            {competitor.site_type && (
                              <Tag
                                style={{
                                  backgroundColor:
                                    (colorByType[competitor.site_type] || {})
                                      .bg || '#f5f5f5',
                                  color:
                                    (colorByType[competitor.site_type] || {})
                                      .text || '#595959',
                                  border: 'none',
                                }}
                              >
                                {siteTypeMap[competitor.site_type] ||
                                  competitor.site_type}
                              </Tag>
                            )}
                          </Space>
                        </Space>
                      ) : (
                        <div style={{ padding: '0 4px' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              paddingBottom: '8px',
                            }}
                          >
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: '#52c41a',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}
                            >
                              {index + 1}
                            </div>
                            <div style={{ flex: 1 }}>
                              <Skeleton
                                active
                                paragraph={{ rows: 2, width: ['100%', '80%'] }}
                                title={false}
                                className="pulse"
                                style={{ marginBottom: '4px' }}
                              />
                            </div>
                          </div>
                          <Skeleton
                            active
                            paragraph={{ rows: 1, width: ['60%'] }}
                            title={false}
                            className="pulse"
                          />
                        </div>
                      )
                    }
                    style={{ height: 'calc(100vh - 90px)' }}
                    bodyStyle={{
                      height: 'calc(100% - 120px)',
                      overflow: 'hidden',
                      padding: '8px 12px',
                    }}
                    hoverable={false}
                  >
                    <div
                      style={{
                        height: '100%',
                        overflowY: 'auto',
                        paddingRight: '8px',
                      }}
                    >
                      {dataReady.competitor_analysis ? (
                        competitor.headings_analysis?.headings &&
                        competitor.headings_analysis.headings.length > 0 ? (
                          <div className="fade-in">
                            {competitor.headings_analysis.headings
                              .filter(heading => heading.level > 1)
                              .map((heading, index) => {
                                const levelStyles = {
                                  2: {
                                    color: '#fa8c16',
                                    backgroundColor: '#fff7e6',
                                  },
                                  3: {
                                    color: '#faad14',
                                    backgroundColor: '#fffbe6',
                                  },
                                  4: {
                                    color: '#52c41a',
                                    backgroundColor: '#f6ffed',
                                  },
                                  5: {
                                    color: '#1890ff',
                                    backgroundColor: '#e6f7ff',
                                  },
                                  6: {
                                    color: '#722ed1',
                                    backgroundColor: '#f9f0ff',
                                  },
                                };

                                const level = heading.level || 2;
                                const text = heading.text || '';
                                const displayText =
                                  text.length > 60
                                    ? text.substring(0, 60) + '...'
                                    : text;
                                const style =
                                  levelStyles[level] || levelStyles[6];

                                // 階層に応じたインデント（H2=0px, H3=20px, H4=40px...）
                                const indentLevel = Math.max(0, level - 2);
                                const paddingLeft = indentLevel * 20;

                                return (
                                  <div
                                    key={index}
                                    style={{
                                      marginBottom: '8px',
                                      paddingLeft: `${paddingLeft}px`,
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '8px',
                                    }}
                                  >
                                    <Tag
                                      style={{
                                        ...style,
                                        border: `1px solid ${style.color}`,
                                        fontSize: '11px',
                                        minWidth: '28px',
                                        textAlign: 'center',
                                        flexShrink: 0,
                                        fontWeight: '500',
                                      }}
                                    >
                                      H{level}
                                    </Tag>
                                    <span
                                      style={{
                                        fontSize: '13px',
                                        lineHeight: '1.4',
                                        wordBreak: 'break-word',
                                        fontWeight: '500',
                                      }}
                                      title={text}
                                    >
                                      {displayText}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <div
                            style={{
                              textAlign: 'center',
                              padding: '40px',
                              color: '#999',
                            }}
                          >
                            <Text type="secondary">
                              見出し情報を取得できませんでした
                            </Text>
                          </div>
                        )
                      ) : (
                        <div style={{ padding: '10px 0' }}>
                          <Skeleton
                            active
                            paragraph={{ rows: 10 }}
                            title={false}
                            className="pulse"
                          />
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ) : (
          // ヒートマップ表示
          (() => {
            // ヒートマップは種類ごとに段階解除（topics/terms/related/lsi）。
            const heatmapData = prepareHeatmapData(headingView);
            const hasData =
              Array.isArray(heatmapData) && heatmapData.length > 0;
            const isCompleted =
              analysisState?.status === 'completed' ||
              analysisState?.status === 'completed_with_errors';
            // 🔧 ヒートマップ専用フラグのみを使用（分析サマリー用と完全分離）
            const isTypeReady = (() => {
              if (headingView === 'topics') return !!dataReady.heatmap_topics;
              if (headingView === 'terms') return !!dataReady.heatmap_terms;
              if (headingView === 'related') return !!dataReady.heatmap_related;
              if (headingView === 'lsi') return !!dataReady.heatmap_lsi;
              return !!dataReady.heatmaps;
            })();

            // データが実際に存在する場合は表示
            if (hasData) {
              // 実データ表示（以下のテーブル描画処理へ）
            } else if (isTypeReady || isCompleted) {
              // データ準備完了またはSTEP完了済みで、データがない場合
              return (
                <div style={{ padding: '12px' }}>
                  <Text type="secondary">データがありません</Text>
                </div>
              );
            } else {
              // データ準備中：スケルトン表示
              return (
                <div style={{ padding: '20px' }}>
                  <Skeleton
                    active
                    paragraph={{ rows: 6 }}
                    title={false}
                    className="pulse"
                  />
                </div>
              );
            }

            // 以下、実データの表示処理
            const maxValue =
              headingView === 'related' ? 20 : headingView === 'terms' ? 10 : 1;

            const columns = [
              {
                title:
                  headingView === 'topics'
                    ? 'トピック'
                    : headingView === 'terms'
                      ? '重要語句'
                      : 'キーワード',
                dataIndex: 'keyword',
                key: 'keyword',
                fixed: 'left',
                width: 260,

                render: (text, record) => {
                  if (headingView === 'lsi' && record.isImportant) {
                    return (
                      <Space>
                        <span>{text}</span>
                        <Tag
                          size="small"
                          color="red"
                          style={{ fontSize: '10px', padding: '0 4px' }}
                        >
                          重要
                        </Tag>
                      </Space>
                    );
                  }

                  // 表示AVGと同等の算出
                  const getDisplayedAvg = rec => {
                    const direct = rec?.average_score;
                    if (typeof direct === 'number' && !isNaN(direct))
                      return direct;
                    const alt =
                      rec?.average_normalized_score ??
                      rec?.score ??
                      rec?.normalized_score;
                    if (typeof alt === 'number' && !isNaN(alt)) return alt;
                    let sum = 0;
                    let cnt = 0;
                    if (rec?.values && Array.isArray(rec.values)) {
                      rec.values.forEach(v => {
                        const raw =
                          v.average_score ??
                          v.normalized_score ??
                          v.topic_coverage_score ??
                          0;
                        if (typeof raw === 'number' && !isNaN(raw)) {
                          sum += raw;
                          cnt++;
                        }
                      });
                    } else {
                      for (let i = 1; i <= 9; i++) {
                        const raw = rec[`site${i}`];
                        if (typeof raw === 'number' && !isNaN(raw)) {
                          sum += raw;
                          cnt++;
                        }
                      }
                    }
                    return cnt > 0 ? sum / cnt : 0;
                  };

                  const collectSiteValues = rec => {
                    const arr = [];
                    if (rec?.values && Array.isArray(rec.values)) {
                      rec.values.forEach(v => {
                        const raw =
                          v.normalized_score ??
                          v.average_score ??
                          v.score ??
                          v.topic_coverage_score ??
                          0;
                        if (typeof raw === 'number' && !isNaN(raw))
                          arr.push({
                            value: raw,
                            idx:
                              typeof v.competitor_index === 'number'
                                ? v.competitor_index
                                : null,
                          });
                      });
                    } else {
                      for (let i = 1; i <= 9; i++) {
                        const raw = record[`site${i}`];
                        if (typeof raw === 'number' && !isNaN(raw))
                          arr.push({ value: raw, idx: i - 1 });
                      }
                    }
                    return arr;
                  };

                  const avg = getDisplayedAvg(record);
                  const avgRounded = Number.isFinite(avg)
                    ? Math.round(avg * 10) / 10
                    : avg;
                  const siteVals = collectSiteValues(record);
                  const top3Vals = siteVals
                    .filter(v => v.idx !== null && v.idx <= 2)
                    .map(v => v.value);
                  const countTop3Over85 = top3Vals.filter(v => v >= 8.5).length;
                  const countSitesOver75 = siteVals.filter(
                    v => v.value >= 7.5,
                  ).length;
                  const countSitesOver7 = siteVals.filter(
                    v => v.value >= 7,
                  ).length;
                  const countSitesOver85 = siteVals.filter(
                    v => v.value >= 8.5,
                  ).length;

                  let statusLabel = null;
                  let statusColor = undefined;
                  if (avgRounded >= 8 || countTop3Over85 >= 2) {
                    statusLabel = '必須';
                    statusColor = 'magenta';
                  } else if (
                    avgRounded <= 5.0 &&
                    countSitesOver85 > 0 &&
                    countSitesOver85 <= 2
                  ) {
                    statusLabel = '独自';
                    statusColor = 'cyan';
                  } else if (avgRounded >= 6 || countSitesOver75 >= 3) {
                    statusLabel = '重要';
                    statusColor = 'gold';
                  }

                  try {
                    console.log('[HeatmapLabel]', {
                      topic: record?.topic || record?.name || record?.keyword,
                      headingView,
                      avg: Number.isFinite(avg) ? Number(avg.toFixed(2)) : avg,
                      avgRounded,
                      top3: top3Vals,
                      countTop3Over85,
                      countSitesOver75,
                      countSitesOver7,
                      countSitesOver85,
                      statusLabel,
                    });
                  } catch (err) {
                    console.warn('[HeatmapLabel][debug-failed]', err);
                  }

                  return (
                    <div
                      className="heatmap-topic"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
                      {headingView === 'topics' && statusLabel ? (
                        <Tag
                          color={statusColor}
                          style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                        >
                          {statusLabel}
                        </Tag>
                      ) : null}
                    </div>
                  );
                },
                onCell: (record, rowIndex) => {
                  const isHoveredRow = rowIndex === hoveredRowIndex;
                  return {
                    onMouseEnter: () => setHoveredRowIndex(rowIndex),
                    onMouseLeave: () => setHoveredRowIndex(null),
                    className: isHoveredRow ? 'heatmap-highlight' : '',
                  };
                },
              },
              // AVG列を追加
              {
                title: 'AVG',
                dataIndex: 'average_score',
                key: 'average_score',
                width: 60,
                sortDirections: ['descend'],
                sorter: (a, b) => {
                  const getAvg = record => {
                    const direct = record.average_score;
                    if (typeof direct === 'number' && !isNaN(direct))
                      return direct;
                    const alt =
                      record.average_normalized_score ??
                      record.score ??
                      record.normalized_score;
                    if (typeof alt === 'number' && !isNaN(alt)) return alt;
                    let sum = 0;
                    let cnt = 0;
                    if (record.values && Array.isArray(record.values)) {
                      record.values.forEach(v => {
                        const raw =
                          v.average_score ??
                          v.normalized_score ??
                          v.topic_coverage_score ??
                          0;
                        if (typeof raw === 'number' && !isNaN(raw)) {
                          sum += raw;
                          cnt++;
                        }
                      });
                    } else {
                      for (let i = 1; i <= 10; i++) {
                        const raw = record[`site${i}`];
                        if (typeof raw === 'number' && !isNaN(raw)) {
                          sum += raw;
                          cnt++;
                        }
                      }
                    }
                    return cnt > 0 ? sum / cnt : 0;
                  };
                  return getAvg(a) - getAvg(b);
                },
                render: (value, record) => {
                  const direct = record.average_score;
                  let finalAvg;
                  if (typeof direct === 'number' && !isNaN(direct)) {
                    finalAvg = direct;
                  } else {
                    const alt =
                      record.average_normalized_score ??
                      record.score ??
                      record.normalized_score;
                    if (typeof alt === 'number' && !isNaN(alt)) {
                      finalAvg = alt;
                    } else {
                      let sum = 0;
                      let cnt = 0;
                      if (record.values && Array.isArray(record.values)) {
                        record.values.forEach(v => {
                          const raw =
                            v.average_score ??
                            v.normalized_score ??
                            v.topic_coverage_score ??
                            0;
                          sum += raw || 0;
                          cnt++;
                        });
                      } else {
                        for (let i = 1; i <= 9; i++) {
                          const raw = record[`site${i}`];
                          if (typeof raw === 'number' && !isNaN(raw)) {
                            sum += raw;
                            cnt++;
                          }
                        }
                      }
                      finalAvg = cnt > 0 ? sum / cnt : 0;
                    }
                  }
                  return (
                    <span>{finalAvg > 0 ? finalAvg.toFixed(1) : '-'}</span>
                  );
                },
                onCell: record => {
                  const getFinalAvg = () => {
                    const direct = record.average_score;
                    if (typeof direct === 'number' && !isNaN(direct))
                      return direct;
                    const alt =
                      record.average_normalized_score ??
                      record.score ??
                      record.normalized_score;
                    if (typeof alt === 'number' && !isNaN(alt)) return alt;
                    let sum = 0;
                    let cnt = 0;
                    if (record.values && Array.isArray(record.values)) {
                      record.values.forEach(v => {
                        const raw =
                          v.average_score ??
                          v.normalized_score ??
                          v.topic_coverage_score ??
                          0;
                        sum += raw || 0;
                        cnt++;
                      });
                    } else {
                      for (let i = 1; i <= 9; i++) {
                        const raw = record[`site${i}`];
                        if (typeof raw === 'number' && !isNaN(raw)) {
                          sum += raw;
                          cnt++;
                        }
                      }
                    }
                    return cnt > 0 ? sum / cnt : 0;
                  };
                  const finalAvg = getFinalAvg();
                  let backgroundColor;
                  if (headingView === 'topics')
                    backgroundColor = getHeatmapColor(finalAvg, 10.0);
                  else if (headingView === 'terms')
                    backgroundColor = finalAvg
                      ? getTermsHeatmapColor(finalAvg, 10.0)
                      : '#f0f0f0';
                  else if (headingView === 'related')
                    backgroundColor = finalAvg
                      ? getRelatedHeatmapColor(finalAvg, 10.0)
                      : '#f0f0f0';
                  else
                    backgroundColor = finalAvg
                      ? getLSIHeatmapColor(finalAvg, 10.0)
                      : '#f0f0f0';
                  return {
                    className: 'heatmap-cell',
                    style: {
                      '--heatmap-bg': backgroundColor,
                      textAlign: 'center',
                    },
                    onClick: () =>
                      showScorePopup(record.keyword, -1, finalAvg, record),
                  };
                },
              },
              ...competitors.map((c, idx) => ({
                title: `${idx + 1}`,
                dataIndex: `site${idx + 1}`,
                key: `site${idx + 1}`,
                width: 46,
                sortDirections: ['descend'],
                sorter: (a, b) => {
                  const extract = (record, colIdx) => {
                    let val = record[`site${colIdx + 1}`] ?? 0;
                    if (record.values && Array.isArray(record.values)) {
                      let vobj =
                        record.values.find(v => {
                          const ci = Number(v?.competitor_index);
                          const rk = Number(v?.rank);
                          return (
                            ci === colIdx ||
                            ci === colIdx + 1 ||
                            rk === colIdx + 1
                          );
                        }) || record.values[colIdx];
                      if (vobj && typeof vobj === 'object') {
                        val =
                          vobj.normalized_score ??
                          vobj.average_score ??
                          vobj.score ??
                          vobj.topic_coverage_score ??
                          vobj.strength ??
                          0;
                      }
                    }
                    return typeof val === 'number' && !isNaN(val) ? val : 0;
                  };
                  const av = extract(a, idx);
                  const bv = extract(b, idx);
                  return av - bv;
                },
                render: (value, record) => {
                  // 新しいデータ構造に対応
                  let actualValue = value;
                  if (record.values && Array.isArray(record.values)) {
                    let vobj =
                      record.values.find(v => {
                        const ci = Number(v?.competitor_index);
                        const rk = Number(v?.rank);
                        return ci === idx || ci === idx + 1 || rk === idx + 1;
                      }) || record.values[idx];
                    if (vobj && typeof vobj === 'object') {
                      actualValue =
                        vobj.normalized_score ??
                        vobj.average_score ??
                        vobj.score ??
                        vobj.topic_coverage_score ??
                        vobj.strength ??
                        0;
                    }
                  }
                  let displayValue;
                  if (headingView === 'topics') {
                    displayValue =
                      actualValue > 0 ? actualValue.toFixed(1) : '-';
                  } else if (headingView === 'terms') {
                    displayValue = actualValue ? actualValue.toFixed(1) : '-';
                  } else if (headingView === 'related') {
                    displayValue = actualValue ? actualValue.toFixed(1) : '-';
                  } else {
                    // lsi
                    displayValue = actualValue ? actualValue.toFixed(1) : '-';
                  }
                  // セル本体(td)にクラスを付与するため、ここでは値のみ返す
                  return <span>{displayValue}</span>;
                },
                onCell: (record, rowIndex) => {
                  // 背景色はtdに適用
                  let backgroundColor;
                  const value = record[`site${idx + 1}`] ?? 0;
                  let actualValue = value;
                  if (record.values && Array.isArray(record.values)) {
                    let vobj =
                      record.values.find(v => {
                        const ci = Number(v?.competitor_index);
                        const rk = Number(v?.rank);
                        return ci === idx || ci === idx + 1 || rk === idx + 1;
                      }) || record.values[idx];
                    if (vobj && typeof vobj === 'object') {
                      actualValue =
                        vobj.normalized_score ??
                        vobj.average_score ??
                        vobj.score ??
                        vobj.topic_coverage_score ??
                        vobj.strength ??
                        0;
                    }
                  }
                  if (headingView === 'topics') {
                    backgroundColor = getHeatmapColor(actualValue, 10.0);
                  } else if (headingView === 'terms') {
                    backgroundColor = actualValue
                      ? getTermsHeatmapColor(actualValue, 10.0)
                      : '#f0f0f0';
                  } else if (headingView === 'related') {
                    backgroundColor = actualValue
                      ? getRelatedHeatmapColor(actualValue, 10.0)
                      : '#f0f0f0';
                  } else {
                    backgroundColor = actualValue
                      ? getLSIHeatmapColor(actualValue, 10.0)
                      : '#f0f0f0';
                  }
                  const isHoveredRow = rowIndex === hoveredRowIndex;
                  const isHoveredCol = idx === hoveredColIndex;
                  return {
                    className: `heatmap-cell ${
                      isHoveredRow || isHoveredCol ? 'heatmap-highlight' : ''
                    }`,
                    style: {
                      '--heatmap-bg': backgroundColor,
                      textAlign: 'center',
                    },
                    onMouseEnter: () => setHoveredColIndex(idx),
                    onMouseLeave: () => setHoveredColIndex(null),
                    onClick: () =>
                      showScorePopup(record.keyword, idx, actualValue, record),
                  };
                },
              })),
            ];

            return !heatmapData || heatmapData.length === 0 ? (
              <div style={{ padding: '16px' }}>
                <SkeletonTable flat columns={columns.length} rows={8} />
              </div>
            ) : (
              <div className="fade-in">
                <Table
                  className={`heatmap-table heatmap-table-${headingView} ${headingView === 'topics' ? 'tour-topics-heatmap' : ''}`}
                  columns={columns}
                  dataSource={heatmapData}
                  rowKey={record =>
                    record.uniqueKey || `${headingView}-${record.keyword}`
                  }
                  scroll={{ x: 800 }}
                  pagination={false}
                  size="small"
                  rowClassName={(record, index) =>
                    index % 2 === 0 ? 'heatmap-row-even' : 'heatmap-row-odd'
                  }
                />
              </div>
            );
          })()
        )}
      </div>
    );
  };
  // AI Overviewテキストの解析と表示用helper関数
  const parseAIOverviewText = (text, references = []) => {
    if (!text) return [];

    // 文を分割（日本語の句読点を考慮）
    const sentences = text.split(/[。！？]/g).filter(s => s.trim().length > 0);

    return sentences
      .map((sentence, index) => {
        const trimmedSentence = sentence.trim();
        if (!trimmedSentence) return null;

        // 各文に対応する参照元を推定（簡易実装）
        const referenceForSentence =
          references[index % references.length] || references[0];

        return {
          text: trimmedSentence + '。',
          reference: referenceForSentence,
        };
      })
      .filter(Boolean);
  };
  // テキスト内のリストやテーブルを検出・変換
  const formatContentWithStructure = text => {
    if (!text) return text;

    // 文字列を行ごとに分割
    const lines = text.split('\n');
    let formattedLines = [];
    let inUnorderedList = false;
    let inOrderedList = false;
    let inTable = false;
    let tableRows = [];
    let currentListDepth = 0; // ネストした箇条書きレベル（最大1に制限）

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      const leadingSpaces = line.match(/^\s+/)?.[0]?.length || 0;
      let listDepth = Math.floor(leadingSpaces / 2);
      if (listDepth > 1) listDepth = 1; // 過剰な入れ子を防ぐ

      // 空行の処理
      if (!trimmedLine) {
        if (inUnorderedList) {
          formattedLines.push('</ul>');
          inUnorderedList = false;
        }
        if (inOrderedList) {
          formattedLines.push('</ol>');
          inOrderedList = false;
        }
        if (inTable) {
          // テーブル終了処理
          if (tableRows.length > 0) {
            formattedLines.push(
              '<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">',
            );
            tableRows.forEach((row, rowIndex) => {
              const cells = row
                .split('|')
                .map(cell => cell.trim())
                .filter(cell => cell);
              if (cells.length > 0) {
                const cellTag = rowIndex === 0 ? 'th' : 'td';
                const cellStyle =
                  rowIndex === 0
                    ? 'border: 1px solid #ddd; padding: 8px; background-color: #f5f5f5; font-weight: bold;'
                    : 'border: 1px solid #ddd; padding: 8px;';
                const cellsHtml = cells
                  .map(
                    cell =>
                      `<${cellTag} style="${cellStyle}">${cell}</${cellTag}>`,
                  )
                  .join('');
                formattedLines.push(`<tr>${cellsHtml}</tr>`);
              }
            });
            formattedLines.push('</table>');
          }
          inTable = false;
          tableRows = [];
        }
        formattedLines.push('<br/>');
        return;
      }

      // テーブル形式の検出（|で区切られた行）
      if (trimmedLine.includes('|') && trimmedLine.split('|').length >= 3) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        tableRows.push(trimmedLine);
        return;
      }

      // 番号付きリストの検出（1. 2. 3. など）
      if (/^\d+\.\s/.test(trimmedLine)) {
        if (inUnorderedList) {
          formattedLines.push('</ul>');
          inUnorderedList = false;
        }
        if (!inOrderedList) {
          formattedLines.push(
            '<ol style="margin: 8px 0; padding-left: 20px;">',
          );
          inOrderedList = true;
        }
        const listItem = trimmedLine.replace(/^\d+\.\s*/, '');
        formattedLines.push(`<li style="margin: 4px 0;">${listItem}</li>`);
        return;
      }

      // 箇条書きの検出と変換（・•-で始まる行）+ ネスト対応（2スペースごとにレベルアップ）
      if (
        trimmedLine.startsWith('・') ||
        trimmedLine.startsWith('•') ||
        trimmedLine.startsWith('-')
      ) {
        // ネスト制御
        while (currentListDepth > listDepth) {
          formattedLines.push('</ul>');
          currentListDepth--;
        }
        if (currentListDepth < listDepth) {
          for (let i = currentListDepth; i < listDepth; i++) {
            formattedLines.push('<ul style="margin: 4px 0 4px 16px;">');
          }
          currentListDepth = listDepth;
        }

        if (inOrderedList) {
          formattedLines.push('</ol>');
          inOrderedList = false;
        }
        if (!inUnorderedList) inUnorderedList = true;
        const listItem = trimmedLine.replace(/^[・•-]\s*/, '');
        formattedLines.push(`<li style="margin: 4px 0;">${listItem}</li>`);
        return;
      }

      // 通常のテキスト
      if (inUnorderedList) {
        while (currentListDepth > 0) {
          formattedLines.push('</ul>');
          currentListDepth--;
        }
        formattedLines.push('</ul>');
        inUnorderedList = false;
      }
      if (inOrderedList) {
        formattedLines.push('</ol>');
        inOrderedList = false;
      }
      if (inTable) {
        // テーブル終了処理
        if (tableRows.length > 0) {
          formattedLines.push(
            '<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">',
          );
          tableRows.forEach((row, rowIndex) => {
            const cells = row
              .split('|')
              .map(cell => cell.trim())
              .filter(cell => cell);
            if (cells.length > 0) {
              const cellTag = rowIndex === 0 ? 'th' : 'td';
              const cellStyle =
                rowIndex === 0
                  ? 'border: 1px solid #ddd; padding: 8px; background-color: #f5f5f5; font-weight: bold;'
                  : 'border: 1px solid #ddd; padding: 8px;';
              const cellsHtml = cells
                .map(
                  cell =>
                    `<${cellTag} style="${cellStyle}">${cell}</${cellTag}>`,
                )
                .join('');
              formattedLines.push(`<tr>${cellsHtml}</tr>`);
            }
          });
          formattedLines.push('</table>');
        }
        inTable = false;
        tableRows = [];
      }

      // 見出し形式の検出（##で始まる行）
      if (trimmedLine.startsWith('##')) {
        const headingText = trimmedLine.replace(/^#+\s*/, '');
        formattedLines.push(
          `<h3 style="margin: 16px 0 8px 0; font-weight: bold; color: #202124;">${headingText}</h3>`,
        );
        return;
      }

      formattedLines.push(trimmedLine);
    });

    // 最後の処理
    if (inUnorderedList) {
      while (currentListDepth > 0) {
        formattedLines.push('</ul>');
        currentListDepth--;
      }
      formattedLines.push('</ul>');
    }
    if (inOrderedList) {
      formattedLines.push('</ol>');
    }
    if (inTable && tableRows.length > 0) {
      formattedLines.push(
        '<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">',
      );
      tableRows.forEach((row, rowIndex) => {
        const cells = row
          .split('|')
          .map(cell => cell.trim())
          .filter(cell => cell);
        if (cells.length > 0) {
          const cellTag = rowIndex === 0 ? 'th' : 'td';
          const cellStyle =
            rowIndex === 0
              ? 'border: 1px solid #ddd; padding: 8px; background-color: #f5f5f5; font-weight: bold;'
              : 'border: 1px solid #ddd; padding: 8px;';
          const cellsHtml = cells
            .map(
              cell => `<${cellTag} style="${cellStyle}">${cell}</${cellTag}>`,
            )
            .join('');
          formattedLines.push(`<tr>${cellsHtml}</tr>`);
        }
      });
      formattedLines.push('</table>');
    }

    return formattedLines.join('\n');
  };

  // 簡易Markdown -> HTML 変換（リンク/箇条書き/テーブルに対応）
  const formatMarkdownWithStructure = md => {
    if (!md) return '';
    let src = md;
    try {
      // 1) Footnote-style links [[n]](url) をプレースホルダに変換
      const refMap = [];
      src = src.replace(/\[\[(\d+)\]\]\(([^)]+)\)/g, (m, num, url) => {
        let domain = '';
        try {
          domain = new URL(url).hostname.replace('www.', '');
        } catch (_) {
          domain = `ref-${num}`;
        }
        const token = `<<<REF${refMap.length}>>>`;
        refMap.push({ url, domain });
        return token;
      });
      // リンク [text](url)
      src = src.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        const safeText = (text || '')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const safeUrl = (url || '').replace(/"/g, '&quot;');
        return `<a class="inline-link" href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
      });
      // 太字 **text**
      src = src.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // 2) 構造フォーマット
      let html = formatContentWithStructure(src);
      // 3) プレースホルダをグレーラベルのアンカーに置換（行生成後に反映して汚染を防ぐ）
      refMap.forEach((ref, i) => {
        const safeUrl = (ref.url || '').replace(/"/g, '&quot;');
        const pill = `<a class="source-pill" href="${safeUrl}" target="_blank" rel="noopener noreferrer">${ref.domain}</a>`;
        html = html.replaceAll(`<<<REF${i}>>>`, pill);
      });
      return html;
    } catch (e) {
      return formatContentWithStructure(md);
    }
  };
  const renderAIOTab = () => {
    const { actual_aio, slots } = competitorData?.aio_analysis || {};
    // AIOは `aio_analysis.ai_overview`から取得
    const aiOverview = competitorData?.aio_analysis?.ai_overview;

    // DataForSEOの raw_serp_data から補完（ai_overview.elements が空の場合）
    const sanitizeAiText = raw => {
      if (!raw || typeof raw !== 'string') return '';
      // ゼロ幅/不可視文字を除去
      let text = raw.replace(/[\u200B-\u200D\uFEFF]/g, '');
      // ドメインやURLが連結されている場合にスペースを挿入
      text = text.replace(/([a-z0-9_.-]+\.[a-z]{2,})(?=[A-Za-z0-9])/gi, '$1 ');
      // 変な断片 **< の除去
      text = text.replace(/\*+\s*<+/g, '');
      // DataForSEOの断片的なフラグを除去
      text = text.replace(/!nav/gi, ' ');
      // 🔧 DataForSEOのアクセシビリティ読み上げテキストを除去
      // パターン: "55,000 55 comma 000" → "55,000"
      // パターン: "62,000 62 comma 000" → "62,000"
      // パターン: "1.2 1.2" → "1.2"
      text = text.replace(/(\d{1,3}(?:,\d{3})+)\s+\d+\s+comma\s+\d+/gi, '$1');
      text = text.replace(/(\d+\.\d+)\s+\d+\.\d+/g, '$1');
      // 数字の直後に同じ数字が繰り返されるパターンを除去 (例: "5.5 5.5 5.5" → "5.5")
      text = text.replace(/(\d+(?:\.\d+)?)\s+(?:\1\s*)+/g, '$1 ');
      // "5 5,0 0 0" のような断片化パターンを除去
      text = text.replace(/\d+\s+\d+,\d+\s+\d+\s+\d+/g, '');
      return text.trim();
    };

    const sanitizeAiMarkdown = raw => {
      if (!raw || typeof raw !== 'string') return '';
      let md = raw.replace(/[\u200B-\u200D\uFEFF]/g, '');
      // ドメイン連結の緩和（URL後にスペース）
      md = md.replace(/(https?:\/\/[^\s)]+)(?=[A-Za-z0-9])/g, '$1 ');
      // 変な断片 **< の除去
      md = md.replace(/\*+\s*<+/g, '');
      md = md.replace(/!nav/gi, ' ');
      return md.trim();
    };

    const buildAiOverviewFromRaw = () => {
      try {
        const raw = competitorData?.serp_data?.raw_serp_data;
        const tasks = raw?.tasks;
        const result0 =
          Array.isArray(tasks) &&
          tasks[0] &&
          tasks[0].result &&
          tasks[0].result[0];
        const items = result0?.items;
        if (!Array.isArray(items)) return null;
        const aiItem = items.find(it => it && it.type === 'ai_overview');
        if (!aiItem) return null;
        const subItems = Array.isArray(aiItem.items) ? aiItem.items : [];
        const elements = subItems
          .map(si => {
            const text = sanitizeAiText(si?.text || '');
            const title = sanitizeAiText(si?.title || '');
            const refs = Array.isArray(si?.references) ? si.references : [];
            const markdown = sanitizeAiMarkdown(si?.markdown || '');
            if (!text && !title && !markdown) return null;
            return { text, title, references: refs, markdown };
          })
          .filter(Boolean);
        const references = Array.isArray(aiItem.references)
          ? aiItem.references
          : [];
        if (elements.length === 0) return null;
        return { elements, references, has_ai_overview: true };
      } catch (_) {
        return null;
      }
    };

    const effectiveAiOverview = (() => {
      const has =
        aiOverview &&
        Array.isArray(aiOverview.elements) &&
        aiOverview.elements.length > 0;
      if (has) return aiOverview;
      const built = buildAiOverviewFromRaw();
      return built || aiOverview;
    })();

    const aiHasElements = !!(
      effectiveAiOverview &&
      ((Array.isArray(effectiveAiOverview.elements) &&
        effectiveAiOverview.elements.length > 0) ||
        (effectiveAiOverview.text &&
          effectiveAiOverview.text.trim().length > 0))
    );
    const isCompleted =
      analysisState.status === 'completed' || analysisState.status === 'error';

    // 言及サイトをAIO参照と有機順位から集計
    const buildMentionedSites = () => {
      const normalizeDomain = raw => {
        if (!raw || typeof raw !== 'string') return '';
        let s = raw.trim().toLowerCase();
        try {
          if (s.startsWith('http://') || s.startsWith('https://')) {
            s = new URL(s).hostname;
          }
        } catch (e) {
          console.debug('[normalizeDomain] URL解析に失敗:', raw, e);
        }
        if (s.startsWith('www.')) s = s.slice(4);
        return s;
      };
      const countMap = new Map();
      // AIO参照元（全スロットの参照もカウント）
      const refs = effectiveAiOverview?.references || [];
      refs.forEach(ref => {
        let domainRaw = (ref?.domain || '').trim();
        if (!domainRaw && ref?.url) {
          try {
            domainRaw = new URL(ref.url).hostname;
          } catch (e) {
            console.debug('[mentionedSites] ref.urlの解析に失敗:', ref?.url, e);
          }
        }
        const domain = normalizeDomain(domainRaw);
        if (!domain) return;
        const prev = countMap.get(domain) || {
          count: 0,
          organicRank: '-',
          url: undefined,
        };
        const url = prev.url || ref?.url || undefined;
        countMap.set(domain, { ...prev, count: prev.count + 1, url });
      });
      // 各要素の参照
      const elements = effectiveAiOverview?.elements || [];
      elements.forEach(el => {
        const elRefs = Array.isArray(el?.references) ? el.references : [];
        elRefs.forEach(r => {
          let dRaw = (r?.domain || '').trim();
          if (!dRaw && r?.url) {
            try {
              dRaw = new URL(r.url).hostname;
            } catch (e) {
              console.debug(
                '[mentionedSites] element reference url解析に失敗:',
                r?.url,
                e,
              );
            }
          }
          const d = normalizeDomain(dRaw);
          if (!d) return;
          const prev = countMap.get(d) || {
            count: 0,
            organicRank: '-',
            url: undefined,
          };
          const refUrl = prev.url || r?.url || undefined;
          countMap.set(d, { ...prev, count: prev.count + 1, url: refUrl });
        });
      });
      // 競合（オーガニック順位）
      try {
        const organic = competitorData?.serp_data?.organic_results || [];
        organic.forEach((item, idx) => {
          const itemUrl = item?.url || '';
          if (!itemUrl) return;
          let domain = '';
          try {
            domain = normalizeDomain(new URL(itemUrl).hostname);
          } catch (e) {
            console.debug(
              '[mentionedSites] organic url解析に失敗:',
              itemUrl,
              e,
            );
            domain = normalizeDomain(itemUrl);
          }
          if (!domain) return;
          const prev = countMap.get(domain) || {
            count: 0,
            organicRank: '-',
            url: undefined,
          };
          const rank =
            typeof item?.rank_group === 'number'
              ? item.rank_group
              : typeof item?.rank_absolute === 'number'
                ? item.rank_absolute
                : typeof item?.position === 'number'
                  ? item.position
                  : '-';
          const organicRank =
            prev.organicRank === '-'
              ? rank
              : Math.min(prev.organicRank || rank, rank || prev.organicRank);
          const existingUrl = prev.url || item?.url || undefined;
          countMap.set(domain, { ...prev, organicRank, url: existingUrl });
        });
      } catch (e) {
        console.debug('[mentionedSites] 有機順位の解析中にエラー:', e);
      }
      return Array.from(countMap.entries())
        .map(([domain, v]) => ({
          domain,
          count: v.count || 0,
          organicRank: v.organicRank ?? '-',
          url: v.url,
        }))
        .sort((a, b) => (b.count || 0) - (a.count || 0));
    };
    const mentionedSites = buildMentionedSites();
    return (
      <Row gutter={[24, 16]} className="aio-row">
        {/* メイン AI Overview コンテンツ（左70%、モバイル100%） */}
        <Col xs={24} md={17}>
          <Card
            className="v2-card"
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <div
                  className="rank-favicon-container"
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ color: '#1a73e8', fontSize: '16px' }}>🔍</span>
                  <span>AI Overviews | 概要</span>
                </div>
                <span
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    fontWeight: 'normal',
                  }}
                >
                  取得日時:{' '}
                  {(() => {
                    const timestamp =
                      aiOverview?.timestamp ||
                      competitorData?.serp_data?.timestamp;
                    if (timestamp) {
                      try {
                        return new Date(timestamp).toLocaleString('ja-JP');
                      } catch (e) {
                        return '不明';
                      }
                    }
                    return '不明';
                  })()}
                </span>
              </div>
            }
            hoverable={false}
            bodyStyle={{ padding: '16px 20px' }}
          >
            {aiHasElements ? (
              <div
                className="fade-in aio-body"
                style={{
                  lineHeight: '1.8',
                  fontSize: '14px',
                  color: '#202124',
                }}
              >
                {effectiveAiOverview.elements &&
                effectiveAiOverview.elements.length > 0 ? (
                  effectiveAiOverview.elements.map((element, elemIdx) => {
                    const allText = sanitizeAiText(element.text || '');
                    const elementReferences =
                      element.references ||
                      effectiveAiOverview.references ||
                      [];
                    const cleanMarkdown = sanitizeAiMarkdown(
                      element.markdown || '',
                    );
                    const hasMarkdown = !!cleanMarkdown;
                    const parsedSentences = parseAIOverviewText(
                      allText,
                      elementReferences,
                    );

                    return (
                      <div key={elemIdx}>
                        {/* セクションタイトル（もしあれば） */}
                        {element.title && (
                          <div
                            style={{
                              fontWeight: 'bold',
                              marginBottom: 12,
                              fontSize: '15px',
                              color: '#202124',
                            }}
                          >
                            {element.title}
                          </div>
                        )}

                        {/* フォーマット済みコンテンツの表示 */}
                        <div
                          style={{ marginBottom: 16 }}
                          dangerouslySetInnerHTML={{
                            __html: hasMarkdown
                              ? formatMarkdownWithStructure(cleanMarkdown)
                              : formatContentWithStructure(allText)
                                  .split('\n')
                                  .map((line, lineIdx) => {
                                    if (
                                      line.includes('<ul>') ||
                                      line.includes('</ul>') ||
                                      line.includes('<li>') ||
                                      line.includes('<table>') ||
                                      line.includes('</table>')
                                    ) {
                                      return line;
                                    }

                                    // 通常の文の場合、センテンスと参照元をセットで処理
                                    const sentences = line
                                      .split(/[。！？]/g)
                                      .filter(s => s.trim().length > 0);
                                    return sentences
                                      .map((sentence, sentIdx) => {
                                        const trimmedSentence = sentence.trim();
                                        if (!trimmedSentence) return '';

                                        const referenceForSentence =
                                          elementReferences[
                                            sentIdx % elementReferences.length
                                          ] || elementReferences[0];
                                        const referenceLink =
                                          referenceForSentence
                                            ? `<a href="${referenceForSentence.url || '#'}" target="_blank" rel="noopener noreferrer" style="
                                    display: inline-block;
                                    margin-left: 4px;
                                    padding: 2px 6px;
                                    font-size: 11px;
                                    background-color: #f1f3f4;
                                    border-radius: 12px;
                                    color: #5f6368;
                                    text-decoration: none;
                                    vertical-align: super;
                                    line-height: 1;
                                  ">${referenceForSentence.domain || 'source'}</a>`
                                            : '';

                                        return `<div style="margin-bottom: 8px;">${trimmedSentence}。${referenceLink}</div>`;
                                      })
                                      .join('');
                                  })
                                  .join(''),
                          }}
                        />

                        {/* 要素間の区切り線 */}
                        {elemIdx < effectiveAiOverview.elements.length - 1 && (
                          <div
                            style={{
                              height: '1px',
                              background: '#dadce0',
                              margin: '16px 0',
                            }}
                          />
                        )}
                      </div>
                    );
                  })
                ) : effectiveAiOverview.text ? (
                  <div style={{ marginBottom: '16px' }}>
                    <div
                      style={{
                        lineHeight: '1.8',
                        fontSize: '14px',
                        color: '#202124',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {effectiveAiOverview.text}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : isCompleted ? (
              <div style={{ padding: '8px 12px', color: '#5f6368' }}>
                AI Overviews はありません
              </div>
            ) : (
              <div style={{ padding: '16px' }}>
                <Skeleton
                  active
                  paragraph={{ rows: 10 }}
                  title={false}
                  className="pulse"
                />
              </div>
            )}
            {/* 言及サイトは別カードに移動 */}
          </Card>
          {/* 言及サイト（概要とは別カード） */}
          <Card
            className="v2-card"
            hoverable={false}
            style={{ marginTop: 24 }}
            title="言及サイト"
          >
            {mentionedSites.length > 0 ? (
              <Table
                size="small"
                pagination={false}
                dataSource={mentionedSites.map((s, idx) => ({
                  key: s.domain || idx,
                  domain: s.domain,
                  count: s.count,
                  organicRank: s.organicRank ?? '-',
                  url: s.url,
                }))}
                columns={[
                  {
                    title: 'ドメイン',
                    dataIndex: 'domain',
                    key: 'domain',
                    render: (text, record) =>
                      record.url ? (
                        <a
                          href={record.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1a0dab' }}
                        >
                          {text}
                        </a>
                      ) : (
                        <span>{text}</span>
                      ),
                  },
                  {
                    title: '出現回数',
                    dataIndex: 'count',
                    key: 'count',
                    width: 120,
                    align: 'center',
                    sorter: (a, b) => (a.count || 0) - (b.count || 0),
                    defaultSortOrder: 'descend',
                  },
                  {
                    title: 'オーガニック順位',
                    dataIndex: 'organicRank',
                    key: 'organicRank',
                    width: 160,
                    align: 'center',
                    sorter: (a, b) => {
                      const av =
                        typeof a.organicRank === 'number'
                          ? a.organicRank
                          : Infinity;
                      const bv =
                        typeof b.organicRank === 'number'
                          ? b.organicRank
                          : Infinity;
                      return av - bv;
                    },
                  },
                ]}
              />
            ) : (
              <div style={{ color: '#666', fontSize: 12 }}>
                データがありません
              </div>
            )}
          </Card>
        </Col>

        {/* 右側リンクカード（30%、モバイル100%） */}
        <Col xs={24} md={7}>
          <div style={{ position: 'sticky', top: 20 }}>
            {effectiveAiOverview &&
            effectiveAiOverview.references &&
            effectiveAiOverview.references.length > 0 ? (
              <div className="fade-in">
                {/* 件数表示 */}
                <div
                  style={{
                    marginBottom: 12,
                    fontSize: '12px',
                    color: '#666',
                    fontWeight: 'bold',
                  }}
                >
                  参照元 ({effectiveAiOverview.references.length}件)
                </div>

                {effectiveAiOverview.references.map((source, idx) => (
                  <Card
                    key={idx}
                    hoverable={false}
                    style={{
                      marginBottom: 12,
                      borderRadius: 8,
                      transition: 'background-color 0.2s ease',
                      cursor: 'pointer',
                      boxShadow: 'none',
                    }}
                    bodyStyle={{ padding: 12 }}
                    onClick={() => {
                      if (source.url) {
                        window.open(source.url, '_blank');
                      }
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    {/* ファビコン＋ドメイン */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${source.domain || new URL(source.url || '').hostname}&sz=16`}
                        alt="favicon"
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 2,
                          backgroundColor: '#f1f3f4',
                        }}
                        onError={e => {
                          // ファビコン取得失敗時のフォールバック
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'inline-block';
                        }}
                      />
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: '#f1f3f4',
                          borderRadius: 2,
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                        }}
                      >
                        🌐
                      </div>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: '11px',
                          color: '#5f6368',
                        }}
                      >
                        {source.domain}
                      </Text>
                    </div>

                    {/* タイトル */}
                    <div
                      style={{
                        marginBottom: 6,
                        fontSize: '13px',
                        fontWeight: 'normal',
                        color: '#1a0dab',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {source.title || source.source || `参照元 ${idx + 1}`}
                    </div>

                    {/* 説明文 */}
                    {source.text && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#5f6368',
                          lineHeight: '1.3',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {source.text}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            ) : isCompleted ? (
              <div style={{ color: '#5f6368', fontSize: '12px' }}>
                参照元はありません
              </div>
            ) : (
              <div>
                {Array.from({ length: 5 }, (_, idx) => (
                  <Card
                    key={idx}
                    hoverable={false}
                    style={{
                      marginBottom: 12,
                      borderRadius: 8,
                      boxShadow: 'none',
                    }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <Skeleton
                      active
                      paragraph={{ rows: 2 }}
                      title={false}
                      className="pulse"
                    />
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Col>

        {/* 狙うAIOスロットはカードごと削除（非表示） */}
      </Row>
    );
  };

  // AIOコンテンツタブのレンダリング
  const renderAIOContentTab = () => {
    console.log('🔍 [renderAIOContentTab] Debug info:', {
      competitorData: competitorData ? 'exists' : 'null',
      hasAioContentStrategy: !!competitorData?.aio_content_strategy,
      loading: loading,
      dataReady_aio_content_strategy: dataReady.aio_content_strategy,
      finalLoading: loading || !dataReady.aio_content_strategy,
    });

    const tl =
      competitorData?.topic_landscape ||
      competitorData?.topic_analysis?.landscape;
    let landscapeSummary = tl?.summary;
    if (!landscapeSummary && tl && typeof tl.response === 'string') {
      try {
        let txt = tl.response.trim();
        if (txt.startsWith('```json')) txt = txt.slice(7);
        if (txt.endsWith('```')) txt = txt.slice(0, -3);
        const inner = JSON.parse(txt.trim());
        landscapeSummary = inner?.summary;
      } catch (parseErr) {
        console.warn('Topic landscape summary parse failed:', parseErr);
      }
    }

    return (
      <div>
        {/* AIOコンテンツ戦略（内部でトピック・ランドスケープカードを表示） */}
        <AIOContentStrategy
          analysisData={competitorData}
          loading={loading || !dataReady.aio_content_strategy}
        />
      </div>
    );
  };
  // 見出し構造をツリー形式に変換
  const convertHeadingsToTree = headings => {
    // headingsが存在しない場合は空の配列を返す
    if (!headings || !Array.isArray(headings)) {
      return [];
    }

    const tree = [];
    const stack = [];

    headings.forEach(heading => {
      const node = {
        title: heading.text || heading.title || 'タイトルなし',
        key: heading.id || `heading-${Math.random()}`,
        level: heading.level || 1,
      };

      while (
        stack.length > 0 &&
        stack[stack.length - 1].level >= heading.level
      ) {
        stack.pop();
      }

      if (stack.length === 0) {
        tree.push(node);
      } else {
        const parent = stack[stack.length - 1];
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      }

      stack.push(node);
    });

    return tree;
  };

  const handleImportantTermToggle = term => {
    const newSelectedTerms = selectedImportantTerms.includes(term)
      ? selectedImportantTerms.filter(t => t !== term)
      : [...selectedImportantTerms, term];

    setSelectedImportantTerms(newSelectedTerms);

    // セッションデータを更新
    updateSessionData({ selectedImportantTerms: newSelectedTerms });
  };

  // 重要語句の強度選択は廃止

  // スケルトンコンポーネントの定義
  const SkeletonCard = ({ title, rows = 3, avatar = false }) => (
    <Card title={title} style={{ marginBottom: 16 }}>
      <Skeleton active avatar={avatar} paragraph={{ rows }} title={!avatar} />
    </Card>
  );
  const SkeletonTable = ({ title, columns = 4, rows = 5, flat = false }) => {
    if (flat) {
      return (
        <div style={{ marginBottom: 16 }}>
          <Skeleton
            active
            paragraph={{ rows }}
            title={false}
            className="pulse"
          />
        </div>
      );
    }
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Skeleton active>
          <div style={{ marginBottom: 16 }}>
            {Array.from({ length: rows }, (_, i) => (
              <div
                key={i}
                style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}
              >
                {Array.from({ length: columns }, (_, j) => (
                  <div key={j} style={{ flex: 1 }}>
                    <Skeleton.Input active style={{ width: '100%' }} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Skeleton>
      </Card>
    );
  };

  const SkeletonStats = ({ title }) => (
    <Card title={title} style={{ marginBottom: 16 }}>
      <div
        style={{ display: 'flex', gap: '24px', justifyContent: 'space-around' }}
      >
        <div style={{ textAlign: 'center' }}>
          <Skeleton.Avatar active size={64} />
          <Skeleton.Input active style={{ width: '80px', marginTop: '8px' }} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <Skeleton.Avatar active size={64} />
          <Skeleton.Input active style={{ width: '80px', marginTop: '8px' }} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <Skeleton.Avatar active size={64} />
          <Skeleton.Input active style={{ width: '80px', marginTop: '8px' }} />
        </div>
      </div>
    </Card>
  );
  // loading状態でも通常のタブ構造を表示し、各カードはdataReady状態に応じてスケルトンか実データを表示する
  // データがない場合でも、loading状態やshowProgressUIがtrueの場合はスケルトン状態のタブを表示
  if (!competitorData && !loading && !showProgressUI) {
    // 初期化中の場合はローディング表示
    if (initializing) {
      return (
        <div className="step-container competitor-analysis">
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">データを読み込み中...</Text>
            </div>
          </div>
        </div>
      );
    }

    // V2分離アーキテクチャ: analysis_idがあれば分析中として扱う
    if (analysisState.analysisId) {
      return (
        <div className="step-container competitor-analysis">
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">分析を準備中...</Text>
            </div>
          </div>
        </div>
      );
    }

    // V2分離アーキテクチャではanalysis_idがない場合のみアラート表示
    // return (
    //   <div className="step-container competitor-analysis">
    //     <Alert
    //       message="分析IDが見つかりません"
    //       description="分析を開始するには新規分析画面から開始してください。"
    //       type="info"
    //       showIcon
    //       action={
    //         <Button
    //           size="small"
    //           type="primary"
    //           onClick={() => (window.location.href = '/aio/analysis/new')}
    //         >
    //           新規分析へ
    //         </Button>
    //       }
    //     />
    //   </div>
    // );
  }
  return (
    <div className="step-container competitor-analysis">
      {/* チュートリアルTour（リサーチ完了後のみ表示） */}
      {!tourLoading && !isSharedView && computedState.isCompleted && (
        <EnhancedTour
          open={tourOpen}
          steps={(() => {
            const config = require('../../../config/tourConfigs').TOUR_CONFIGS
              .research_result;
            if (!config) return [];
            return config.steps.map(step => ({
              ...step,
              target: step.target || null,
            }));
          })()}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={tourCurrentStep}
          onChange={current => {
            updateTourCurrentStep(current);
            // ステップが変わったときにタブとサブタブを切り替え
            const config = require('../../../config/tourConfigs').TOUR_CONFIGS
              .research_result;
            if (config && config.steps[current]) {
              const step = config.steps[current];
              if (step.tabKey) {
                console.log(`[Tour] Switching to tab: ${step.tabKey}`);
                setActiveTabKey(step.tabKey);
              }
              if (step.subTabKey) {
                console.log(`[Tour] Switching to subtab: ${step.subTabKey}`);
                setTourHeadingView(step.subTabKey);
              }
            }
          }}
          tourTitle="リサーチ結果"
        />
      )}

      {/* リアルタイム進捗表示UI（共有ビューでは非表示） */}
      {!isSharedView && enableProgressUI && showProgressUI && (
        <>
          {console.log('🔍 [DEBUG-PROGRESS] RealtimeProgressUI render:', {
            showProgressUI,
            currentStepMessage,
            progress: analysisState.progress,
            status: analysisState.status,
          })}
          <RealtimeProgressUI
            currentStepMessage={currentStepMessage}
            isVisible={showProgressUI}
            progress={analysisState.progress}
            showProgress={true}
          />
        </>
      )}

      {/* ゲスト用サンプル説明バナー */}
      {isGuest && !isSharedView && (
        <div style={{
          background: '#e6f4ff',
          border: '1px solid #91caff',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 12,
          fontSize: 13,
          color: '#0958d9',
        }}>
          狙いたいキーワードを入力してリサーチが完了した画面のサンプルです。実際の画面でリサーチ結果をご覧いただけます。
        </div>
      )}

      {/* パンくず＆ヘッダー（共有ビューでは非表示） */}
      {!isSharedView && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '16px 0',
          }}
        >
          <Breadcrumb>
            <Breadcrumb.Item>
              <a href="/aio/dashboard">AIOコンテンツモード</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <a href="/aio/analysis/list">リサーチ結果</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>リサーチ詳細</Breadcrumb.Item>
          </Breadcrumb>
          {computedState.isCompleted && !isGuest && (
            <TourHelpButton
              onClick={restartTour}
              tooltip="ガイドツアーを再表示"
            />
          )}
        </div>
      )}
      {!isSharedView && (
        <div style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Col xs={24} lg={16}>
              <Title
                level={1}
                className="gradient-text"
                style={{ fontSize: 24 }}
              >
                「{sessionData.keyword}」のリサーチ結果
              </Title>
              <Typography.Text type="secondary">
                競合状況や重要トピックを深く分析し、SEO/AEOで勝てるコンテンツ戦略を設計します
              </Typography.Text>
            </Col>
            {showShareSettings && (
              <Col xs={24} lg={8} style={{ textAlign: 'right' }}>
                <ShareSettings
                  analysisId={sessionData?.step1AnalysisResult?.analysis_id}
                  analysisStatus={analysisState.status}
                  initialShareStatus={
                    typeof competitorData?.share_status === 'string'
                      ? competitorData.share_status
                      : sessionData?.step1AnalysisResult?.share_status ||
                        'private'
                  }
                  initialShareUrl={competitorData?.share_url}
                  onShareUpdate={shareInfo => {
                    console.log('Share settings updated:', shareInfo);
                    // 必要に応じて分析データを更新
                    if (competitorData) {
                      setCompetitorData(prev => ({
                        ...prev,
                        share_status: shareInfo.status,
                        share_url: shareInfo.url,
                      }));
                    }
                  }}
                />
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* スティッキー監視用の要素 */}
      <div
        ref={stickyObserverRef}
        style={{ height: '1px', marginTop: '-1px' }}
      />

      {/* TIPS（分析結果詳細用）- 共有ビューでは非表示 */}
      {!isSharedView && (
        <div style={{ marginBottom: 16 }}>
          <TipsRotator tips={researchTips} intervalMs={7000} fadeMs={300} />
        </div>
      )}

      <Tabs
        activeKey={activeTabKey}
        onChange={key => setActiveTabKey(key)}
        className="analysis-tabs"
        tabBarGutter={0}
        renderTabBar={(props, DefaultTabBar) => {
          return (
            <div
              style={{
                display: 'flex',
                width: '100%',
                position: 'sticky',
                top: 0,
                zIndex: 100,
                paddingTop: 0,
                marginTop: 0,
                backgroundColor: 'transparent',
              }}
            >
              <DefaultTabBar
                {...props}
                style={{
                  flex: 1,
                  width: '100%',
                  marginBottom: '24px',
                  backgroundColor: '#fff',
                  transition: 'background-color 0.2s ease',
                }}
              />
              <style>{`
              .analysis-tabs .ant-tabs-nav-list { 
                display: flex; 
                width: 100%; 
              }
              .analysis-tabs .ant-tabs-tab { 
                flex: 1 1 25%; 
                max-width: 25%; 
                justify-content: center; 
                text-align: center;
                margin: 0 !important;
              }
              .analysis-tabs .ant-tabs-tab .ant-tabs-tab-btn {
                width: 100%;
                text-align: center;
                justify-content: center;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .analysis-tabs .ant-tabs-ink-bar {
                width: 25% !important;
                transition: left 0.3s ease, width 0.3s ease !important;
              }
            `}</style>
            </div>
          );
        }}
      >
        <TabPane
          tab={
            <div style={{ width: '100%', textAlign: 'center' }}>
              分析サマリー
            </div>
          }
          key="1"
        >
          {renderSummaryTab()}
        </TabPane>

        {/* 競合サイト分析: 常にタブは有効（中身はスケルトン/空表示制御） */}
        <TabPane
          tab={
            <div style={{ width: '100%', textAlign: 'center' }}>
              競合サイト分析
            </div>
          }
          key="2"
        >
          {renderCompetitorTab()}
        </TabPane>

        {/* AI Overview: メインタブのみ非活性化（未データ時） */}
        <TabPane
          disabled={
            (analysisState?.status === 'completed' || dataReady.complete) &&
            !competitorData?.aio_analysis?.ai_overview?.exists &&
            !competitorData?.serp_data?.ai_overview?.exists &&
            !competitorData?.serp_data?.ai_overview?.has_ai_overview
          }
          tab={
            <div
              style={{
                width: '100%',
                textAlign: 'center',
                opacity:
                  (analysisState?.status === 'completed' ||
                    dataReady.complete) &&
                  !competitorData?.aio_analysis?.ai_overview?.exists &&
                  !competitorData?.serp_data?.ai_overview?.exists &&
                  !competitorData?.serp_data?.ai_overview?.has_ai_overview
                    ? 0.5
                    : 1,
              }}
              title={
                (analysisState?.status === 'completed' || dataReady.complete) &&
                !competitorData?.aio_analysis?.ai_overview?.exists &&
                !competitorData?.serp_data?.ai_overview?.exists &&
                !competitorData?.serp_data?.ai_overview?.has_ai_overview
                  ? 'データがありません'
                  : undefined
              }
            >
              AI Overviews
            </div>
          }
          key="3"
        >
          {renderAIOTab()}
        </TabPane>

        {/* コンテンツ戦略: 常にタブは有効（中身はスケルトン/空表示制御） */}
        <TabPane
          tab={
            <div style={{ width: '100%', textAlign: 'center' }}>
              コンテンツ戦略
            </div>
          }
          key="4"
        >
          {renderAIOContentTab()}
        </TabPane>
      </Tabs>

      {/* スコアポップアップモーダル */}
      <Modal
        title={
          scorePopupData ? (
            scorePopupData.source === 'site' ? (
              <a
                href={scorePopupData.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff' }}
              >
                {scorePopupData.pageTitle}
              </a>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: getHeatmapColor(
                      scorePopupData.score,
                      10.0,
                    ),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{ fontSize: 12, color: '#000', fontWeight: 500 }}
                  >
                    {scorePopupData.score.toFixed(1)}
                  </span>
                </div>
                <span style={{ fontWeight: 600 }}>{scorePopupData.topic}</span>
              </div>
            )
          ) : null
        }
        open={scorePopupVisible}
        onCancel={() => setScorePopupVisible(false)}
        footer={null}
        width={600}
        centered
      >
        {scorePopupData && (
          <>
            {/* heatmap由来のときはモーダルタイトルにスコア+トピックを表示するため、本文側のタイトル行は表示しない */}

            {scorePopupData.siteIndex !== -1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <Text
                  style={{ fontSize: 14, color: '#666', fontWeight: '500' }}
                >
                  【{scorePopupData.siteIndex}】
                </Text>
                <img
                  src={scorePopupData.favicon}
                  alt="favicon"
                  style={{ width: 16, height: 16 }}
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
                <Text
                  style={{ fontSize: 14, color: '#666', fontWeight: '400' }}
                >
                  {scorePopupData.siteName}
                </Text>
              </div>
            )}

            {scorePopupData.source !== 'site' && (
              <div style={{ marginBottom: 16 }}>
                {scorePopupData.siteIndex !== -1 ? (
                  <a
                    href={scorePopupData.pageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#1890ff',
                      fontSize: 15,
                      fontWeight: 'bold',
                      textDecoration: 'none',
                      display: 'block',
                      lineHeight: '1.4',
                    }}
                  >
                    {scorePopupData.pageTitle}
                  </a>
                ) : (
                  <Text type="secondary">平均値</Text>
                )}
              </div>
            )}

            {scorePopupData.siteIndex !== -1 &&
              scorePopupData.structuredHeadings.length > 0 && (
                <div>
                  <div
                    style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      border: '1px solid #f0f0f0',
                      borderRadius: 4,
                    }}
                  >
                    <Collapse
                      size="small"
                      ghost
                      expandIconPosition="end"
                      className="competitor-analysis-collapse"
                      style={{ backgroundColor: '#fafafa' }}
                    >
                      {scorePopupData.structuredHeadings.map(
                        (headingItem, index) => {
                          const isH3Structure = headingItem.level === 3;
                          const hasSubHeadings = isH3Structure
                            ? headingItem.h4s && headingItem.h4s.length > 0
                            : headingItem.h3s && headingItem.h3s.length > 0;

                          if (!hasSubHeadings) {
                            return (
                              <div
                                key={index}
                                className="ant-collapse-item"
                                style={{
                                  borderBottom: '1px solid #f0f0f0',
                                  backgroundColor: '#fafafa',
                                }}
                              >
                                <div
                                  className="ant-collapse-header"
                                  style={{
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    minHeight: '48px',
                                    cursor: 'default',
                                  }}
                                >
                                  <Tag
                                    color={isH3Structure ? 'blue' : 'orange'}
                                    size="small"
                                  >
                                    {isH3Structure ? 'H3' : 'H2'}
                                  </Tag>
                                  <Text
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      flex: 1,
                                    }}
                                  >
                                    {headingItem.text.length > 48
                                      ? headingItem.text.substring(0, 48) +
                                        '...'
                                      : headingItem.text}
                                  </Text>
                                  <div style={{ width: '16px' }}></div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <Collapse.Panel
                              header={
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    minHeight: '24px',
                                  }}
                                >
                                  <Tag
                                    color={isH3Structure ? 'blue' : 'orange'}
                                    size="small"
                                  >
                                    {isH3Structure ? 'H3' : 'H2'}
                                  </Tag>
                                  <Text
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      flex: 1,
                                    }}
                                  >
                                    {headingItem.text.length > 48
                                      ? headingItem.text.substring(0, 48) +
                                        '...'
                                      : headingItem.text}
                                  </Text>
                                </div>
                              }
                              key={index}
                            >
                              {isH3Structure ? (
                                headingItem.h4s.length > 0 ? (
                                  <div style={{ paddingLeft: 16 }}>
                                    {headingItem.h4s.map((h4Text, h4Index) => (
                                      <div
                                        key={h4Index}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          marginBottom: 6,
                                        }}
                                      >
                                        <Tag color="purple" size="small">
                                          H4
                                        </Tag>
                                        <Text style={{ fontSize: 12 }}>
                                          {h4Text.length > 58
                                            ? h4Text.substring(0, 58) + '...'
                                            : h4Text}
                                        </Text>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12, paddingLeft: 16 }}
                                  >
                                    H4見出しはありません
                                  </Text>
                                )
                              ) : headingItem.h3s.length > 0 ? (
                                <div style={{ paddingLeft: 16 }}>
                                  {headingItem.h3s.map((h3Text, h3Index) => (
                                    <div
                                      key={h3Index}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        marginBottom: 6,
                                      }}
                                    >
                                      <Tag color="green" size="small">
                                        H3
                                      </Tag>
                                      <Text style={{ fontSize: 12 }}>
                                        {h3Text.length > 58
                                          ? h3Text.substring(0, 58) + '...'
                                          : h3Text}
                                      </Text>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 12, paddingLeft: 16 }}
                                >
                                  H3見出しはありません
                                </Text>
                              )}
                            </Collapse.Panel>
                          );
                        },
                      )}
                    </Collapse>
                  </div>
                </div>
              )}

            {scorePopupData.siteIndex === -1 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  このトピックの全サイト平均スコアです
                </Text>
              </div>
            )}
          </>
        )}
      </Modal>

      {isGuest && !isSharedView && <GuestBottomBar />}
    </div>
  );
};

export default CompetitorAnalysis;
